/* =============================================================================
   Rynoka — Warm Cream redesign — UI behaviours
   Loaded after toyland-scripts.js in theme.liquid.

   Handles:
   - Hover quick-add buttons on product cards (uses /cart/add.js)
   - Sticky PDP ATC bar visibility
   - PDP gallery (thumbnails, prev/next arrows, touch swipe, desktop hover zoom)
   -    PDP gallery share (icon on image opens modal — copy link + social URLs follow variant)
   - Product card multi-image carousel (PLP grids: swipe, dots, desktop arrows)
   - PDP tabs
   - Pincode checker (mocked, real lookup is server-side optional)
   - Cart drawer count refresh after AJAX adds
   ============================================================================= */
(function () {
  'use strict';

  // ─── Helpers ───────────────────────────────────────────────────────────────
  /* Maximum units of one variant allowed in a single order.
     Single source of truth for every JS check and every message that quotes the
     number. The Liquid side has to agree, and cannot read this — the `max` attribute
     on each quantity input is written by `assign qty_max` in:
       sections/toyland-product.liquid, sections/main-product.liquid,
       sections/featured-product.liquid, sections/main-cart-items.liquid,
       snippets/quantity-input.liquid
     Changing the cap means changing here AND those five. `grep -rn "qty_max = "`
     finds them all. (A shopper who gets past the input cap is still stopped by the
     interceptor below, so the Liquid value is a courtesy, not the enforcement.)

     Sized against the bundle deals: at 3, a 2-unit combo could only ever be bought
     once — a second one needed 4 units and was refused, which is what shoppers hit. */
  var TL_MAX_PER_ORDER = 4;

  /* Read an /cart/add.js response the same way everywhere.
     Throws an Error carrying `tlLimit` when the failure was our own per-order cap
     (which has already toasted the reason), so callers can avoid double-messaging
     while still messaging every OTHER failure. */
  function tlReadAddResponse(r) {
    return r
      .json()
      .catch(function () {
        // Non-JSON body (proxy error page, connection cut mid-response).
        throw new Error('Unable to add to cart — please try again.');
      })
      .then(function (data) {
        if (!r.ok || (data && data.status)) {
          var err = new Error(
            (data && (data.description || data.message)) || 'Unable to add to cart'
          );
          if (data && data.tl_limit) err.tlLimit = true;
          throw err;
        }
        return data;
      });
  }

  /* Shopper-facing text for any add/update failure. Mirrors tlCartErrorMessage in
     toyland-scripts.js; kept local so this file never depends on load order. */
  function tlCartErrorText(err) {
    var raw = err && err.message ? String(err.message).trim() : '';
    if (!raw) return 'Something went wrong — please try again.';
    if (/failed to fetch|networkerror|load failed|network request failed|aborted/i.test(raw)) {
      return 'Network problem — check your connection and try again.';
    }
    return raw;
  }

  /* One rule for every add path in this file: if the item did not go into the cart,
     the shopper is told why. `tlLimit` failures were already toasted by the cap. */
  function tlReportAddFailure(err) {
    if (err && err.tlLimit) return;
    tlShowToast(tlCartErrorText(err), 'error');
  }

  function ajaxAdd(variantId, qty) {
    // Max-3-per-item is enforced centrally by the /cart/add interceptor below.
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty || 1 })
    }).then(tlReadAddResponse);
  }

  /* `cache: 'no-store'` — this read is what every cart surface repaints from. A
     revalidated-but-stale /cart.js repaints the drawer without the line that was
     just added, which the shopper reads as "add to cart did nothing". */
  function ajaxCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      if (!r.ok) throw new Error('Could not load cart');
      return r.json();
    });
  }

  // ─── Lightweight toast (cart quantity limit + add errors) ──────────────────
  function tlShowToast(message, variant) {
    if (!message) return;
    var host = document.querySelector('[data-tl-toast-host]');
    if (!host) {
      host = document.createElement('div');
      host.setAttribute('data-tl-toast-host', '');
      host.className = 'tl-toast-host';
      document.body.appendChild(host);
    }
    var toast = document.createElement('div');
    toast.className = 'tl-toast' + (variant ? ' tl-toast--' + variant : '');
    /* An error is announced assertively — a polite live region can be held back until
       the screen reader finishes whatever it was saying, and a failed add is exactly
       the moment the shopper needs to hear about. */
    var isError = variant === 'error';
    toast.setAttribute('role', isError ? 'alert' : 'status');
    toast.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    /* One icon per variant. The error variant used to fall through to the SUCCESS
       tick, so a red "…is already sold out" panel was headed by a checkmark — it read
       as a confirmation of the very thing that had just failed. */
    var SVG_OPEN =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">';
    var icon;
    if (variant === 'limit') {
      // Info circle
      icon = SVG_OPEN + '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
    } else if (isError) {
      // Alert triangle
      icon = SVG_OPEN + '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    } else {
      // Success tick in a cart
      icon = SVG_OPEN + '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>';
    }
    toast.innerHTML = '<span class="tl-toast__icon" aria-hidden="true">' + icon + '</span><span class="tl-toast__msg"></span>';
    toast.querySelector('.tl-toast__msg').textContent = message;
    host.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, 3200);
  }
  // Expose so other scripts (toyland-scripts.js) can reuse the same toast.
  window.tlShowToast = tlShowToast;

  /* ─── Body scroll-lock watchdog ────────────────────────────────────────────
     Symptom this fixes: after a minute or so on the site, the page freezes and the
     shopper can no longer add anything to the cart.

     Cause, measured on the dev server: Klaviyo's signup popup opens ~9s after page
     load and locks the page by putting `klaviyo-prevent-body-scrolling` on <body>
     (overflow:hidden). It removes that class again when the popup is dismissed — but
     its close button renders 21×21px hard against the right edge of the dialog, so on
     a 390–420px phone it is both under the 44px touch minimum and half off-screen, and
     shoppers frequently never manage to hit it. The lock then stays for the rest of
     the session: wheel/touch scrolling was still dead at 15s, 30s, 45s and 60s.

     Why that kills add-to-cart specifically: the PDP's main Add to Cart sits below the
     fold, and the sticky bar is only revealed once the main button scrolls out of
     view. With scrolling dead, neither button is reachable — so the shopper has no way
     to add anything, which is exactly "I could add at first, then I couldn't".

     The rule here is narrow on purpose: release the lock ONLY when nothing that
     legitimately owns it is on screen — not our cart drawer, not our mobile nav, not
     Dawn's own lock, and not a genuinely visible third-party dialog. A popup the
     shopper is actually looking at keeps its lock and behaves exactly as before; this
     only cleans up a lock that has been left behind.

     Polling runs only while a lock class is present, and stops as soon as it clears. */
  (function installScrollLockWatchdog() {
    if (window.__tlScrollLockWatchdog) return;
    window.__tlScrollLockWatchdog = true;

    // Third-party scroll locks we know how to clean up after.
    var LOCK_CLASSES = ['klaviyo-prevent-body-scrolling'];
    var timer = null;

    function bodyIsLocked() {
      var b = document.body;
      if (!b) return false;
      for (var i = 0; i < LOCK_CLASSES.length; i++) {
        if (b.classList.contains(LOCK_CLASSES[i])) return true;
      }
      return false;
    }

    // Something of ours (or Dawn's) is deliberately holding the page still.
    function ownModalOpen() {
      var b = document.body;
      if (b.classList.contains('overflow-hidden')) return true; // Dawn cart drawer
      if (b.classList.contains('tl-mobile-nav-open')) return true;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) return true;
      return false;
    }

    /* How long a third-party popup may hold the page still after it opens. Long
       enough that the popup still feels like a modal, short enough that the page can
       never be stuck: once it elapses the lock goes but the popup stays on screen and
       fully usable, exactly as it would over a normally-scrolling page. */
    var LOCK_GRACE_MS = 5000;
    var lockedAt = 0;

    /* A real, on-screen third-party dialog. Size-gated because Klaviyo leaves small
       hidden stubs in the DOM permanently — only a box big enough to actually be a
       dialog counts as "the shopper is looking at something". */
    function thirdPartyDialogVisible() {
      var nodes = document.querySelectorAll('div[class*="kl-private"], form[class*="kl-private"]');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        var r = el.getBoundingClientRect();
        if (r.width < 80 || r.height < 80) continue;
        if (r.bottom < 0 || r.top > (window.innerHeight || 0)) continue;
        var cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none') continue;
        if (parseFloat(cs.opacity) <= 0.05) continue;
        return true;
      }
      return false;
    }

    /* force = the shopper just tried to scroll. Wanting to scroll IS the signal that
       they are done with the popup, so the lock goes immediately — no waiting out the
       grace period, no hunting for a 21px close button. */
    function releaseIfStranded(force) {
      if (!bodyIsLocked()) {
        lockedAt = 0;
        stopPolling();
        return;
      }
      // Never interfere with our own modals — those locks are correct and are
      // released by the code that set them.
      if (ownModalOpen()) return;

      if (!force && thirdPartyDialogVisible()) {
        if (!lockedAt) lockedAt = Date.now();
        if (Date.now() - lockedAt < LOCK_GRACE_MS) return;
      }

      var b = document.body;
      for (var i = 0; i < LOCK_CLASSES.length; i++) b.classList.remove(LOCK_CLASSES[i]);
      // Some builds also set it inline; clear only what a scroll lock would have set.
      if (b.style.overflow === 'hidden') b.style.overflow = '';
      if (b.style.position === 'fixed') {
        b.style.position = '';
        b.style.top = '';
        b.style.width = '';
      }
      stopPolling();
    }

    function startPolling() {
      if (timer) return;
      timer = window.setInterval(releaseIfStranded, 1000);
    }
    function stopPolling() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    function onBodyMutation() {
      if (bodyIsLocked()) {
        if (!lockedAt) lockedAt = Date.now();
        // Re-check on a beat: the dialog is usually painted just after the class lands.
        startPolling();
        releaseIfStranded();
      } else {
        lockedAt = 0;
        stopPolling();
      }
    }

    function onScrollIntent() {
      if (bodyIsLocked()) releaseIfStranded(true);
    }

    function boot() {
      if (!document.body) return;
      new MutationObserver(onBodyMutation).observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      /* A scroll gesture against a locked page is the shopper telling us the popup is
         in their way. Passive + capture so this never delays or blocks the gesture. */
      ['wheel', 'touchmove'].forEach(function (evt) {
        document.addEventListener(evt, onScrollIntent, { passive: true, capture: true });
      });

      // Dismissing a popup often fires neither a body mutation nor an event we can see.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') setTimeout(releaseIfStranded, 200);
      });
      document.addEventListener('click', function () {
        if (bodyIsLocked()) setTimeout(releaseIfStranded, 250);
      }, true);
      onBodyMutation();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  })();

  // ─── Centralized per-item, per-order quantity guard ────────────────────────
  // Single choke point: wrap window.fetch so EVERY /cart/add and /cart/update
  // request across the whole site is clamped to 3 per variant — no matter which
  // handler (this file, toyland-scripts.js, or Dawn) fires it.
  (function installCartLimitGuard() {
    if (window.__tlCartLimitGuard) return;
    window.__tlCartLimitGuard = true;

    var TL_MAX = TL_MAX_PER_ORDER;
    var nativeFetch = window.fetch.bind(window);
    var LIMIT_MSG = 'You’ve reached the maximum of ' + TL_MAX + ' per order for this item.';

    function urlOf(input) {
      if (typeof input === 'string') return input;
      if (input && input.url) return input.url;
      return '';
    }

    /* ── Cart writes run one at a time ────────────────────────────────────────
       The cap reads the cart, decides how much may still be added, then writes.
       Read-decide-write is only correct if nothing else writes in between, and
       plenty does: a double-click, the drawer upsell, a bundle app issuing its own
       follow-up write. Concurrently, two adds both read "1 in cart", both allow 2
       more, and the cart ends at 5 — over the cap the whole mechanism exists to
       enforce, with no message shown because neither add saw itself as blocked.
       Serialising is also what makes the toast honest: by the time an add is
       evaluated, every earlier add has already landed.
       The queue can never wedge: a write that hangs stops holding the queue after
       QUEUE_MAX_WAIT_MS, so a dead request costs one slow add, not every later one. */
    var QUEUE_MAX_WAIT_MS = 15000;
    var writeQueue = Promise.resolve();

    /* ── Re-entrancy, and why it is measured HERE and not by "is a write running" ──
       Kaching Bundles and Bundler both run with intercept_cart_request: our add
       reaches their interceptor, which rewrites it and re-issues its own
       /cart/add.js through window.fetch — straight back into this queue. Queueing
       that behind the write that is waiting on it deadlocks both until the 15s
       escape hatch fires, which the shopper sees as Add to Cart hanging.

       So a re-entrant write has to skip the queue. The trap is what counts as
       re-entrant. A global "a write is in flight" counter does NOT: two independent
       adds overlap all the time — a second surface clicked while the first add is
       still waiting on /cart.js — and every one of them then skipped the queue too.
       Both read "3 in cart", both allowed one more, and the cart landed on 5 with a
       cap of 4 and no message, because neither add ever saw itself as blocked. That
       is the whole mechanism this queue exists to provide, switched off by exactly
       the overlap it was built for.

       The honest signal is the call STACK, not the clock: a re-entrant write is one
       issued from inside our own hand-off to the next fetch layer. passThrough marks
       that window, so an interceptor re-issuing from within it runs straight through
       while a merely concurrent add still queues. Every hand-off below goes through
       passThrough for that reason — `nativeFetch` direct is only for reads, which
       can never come back through here. */
    var passThroughDepth = 0;

    function passThrough(input, init) {
      passThroughDepth++;
      try {
        return nativeFetch(input, init);
      } finally {
        passThroughDepth--;
      }
    }

    function enqueueWrite(run) {
      if (passThroughDepth > 0) return Promise.resolve().then(run);

      var result = writeQueue.then(run, run);
      writeQueue = Promise.race([
        result.catch(function () {}),
        new Promise(function (resolve) { setTimeout(resolve, QUEUE_MAX_WAIT_MS); })
      ]);
      return result;
    }

    /* Map of variantId(string) -> total quantity currently in cart.
       Returns null — NOT {} — when the cart cannot be read. The two are completely
       different facts and collapsing them is dangerous in both directions: an
       unreadable cart read as "empty" tells the cap every slot is free, so it waves
       through adds it exists to block; and it is indistinguishable from a real empty
       cart everywhere downstream. A storefront whose /cart.js is failing (an expired
       `theme dev` token answers 401 to everything, and a flaky network does the same
       intermittently) then behaves as though the shopper's cart had been wiped. */
    function currentCartMap() {
      return nativeFetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) {
          if (!r.ok) throw new Error('cart read failed: ' + r.status);
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) throw new Error('cart read was not a cart');
          var map = {};
          cart.items.forEach(function (it) {
            map[String(it.id)] = (map[String(it.id)] || 0) + it.quantity;
          });
          return map;
        })
        .catch(function () { return null; });
    }

    /* A Shopify-style error Response so existing callers' error handling works.
       `tl_limit` marks it as OUR cap rather than a Shopify rejection, so handlers
       know the reason has already been toasted and don't say it twice. */
    function blockedResponse(description) {
      var payload = {
        status: 422,
        message: 'Cart quantity limit',
        tl_limit: true,
        description: description || 'You can add a maximum of ' + TL_MAX + ' of this item per order.'
      };
      return new Response(JSON.stringify(payload), {
        status: 422,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    /* Is this add a bundle/combo deal rather than a plain line?
       Kaching Bundles and Bundler both mark their lines with a `properties[…]` key
       containing "kaching"/"bundle" — the same marker snippets/toyland-cart-bundle-flag
       .liquid keys off, and the reasoning behind it is written up there.

       It matters here because the two cases need OPPOSITE treatment on overflow. A
       plain add of 2 with one slot left is helpfully trimmed to 1. A combo of 2 with
       one slot left must NOT be trimmed: the deal is priced for its full quantity, so
       a half-added combo puts a single unit in the cart at a price that no longer
       applies and leaves the shopper looking at a combo they did not get. */
    var TL_BUNDLE_KEY_RE = /kaching|bundle/i;

    function formDataIsBundleAdd(fd) {
      var hit = false;
      try {
        fd.forEach(function (value, key) {
          if (/^properties\[/i.test(key) && TL_BUNDLE_KEY_RE.test(key)) hit = true;
        });
      } catch (e) { /* no iterable FormData → treat as plain */ }
      return hit;
    }

    function jsonItemIsBundleAdd(item) {
      var props = item && item.properties;
      if (!props || typeof props !== 'object') return false;
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && TL_BUNDLE_KEY_RE.test(k)) return true;
      }
      return false;
    }

    function bundleBlockedMsg(want) {
      return (
        'This combo adds ' + want + ' of this item, and there is a limit of ' +
        TL_MAX + ' per order. Remove some from your cart first.'
      );
    }

    /* Kaching posts a combo in ONE of two shapes, and which one depends on the deal.
       A single-variant deal ("Combo Pack – Buy 2") posts flat `id` + `quantity`.
       A mixed deal ("Mix & Match — Any 2 Panels") posts multi-line FormData instead:
         items[0][id], items[0][quantity], items[0][properties][__kaching_bundles],
         items[1][id], …
       and carries NO plain `id` at all. Verified against the live storefront.

       That second shape used to fall out of the FormData branch on `body.get('id')`
       being null and pass through untouched — no cap, and no all-or-nothing check, on
       exactly the adds that need both. It only looked correct because Kaching's own
       interceptor re-issues the add as JSON, which the branch below does understand;
       the guard was relying on a third party's implementation detail to enforce the
       store's own limit. Parsed here so both shapes meet the same rules. */
    function formDataLines(fd) {
      var byIndex = {};
      try {
        fd.forEach(function (value, key) {
          var m = /^items\[(\d+)\]\[(id|quantity)\]$/i.exec(key);
          if (m) {
            var slot = (byIndex[m[1]] = byIndex[m[1]] || { properties: {} });
            slot[m[2].toLowerCase()] = value;
            return;
          }
          var p = /^items\[(\d+)\]\[properties\]\[(.+)\]$/i.exec(key);
          if (p) {
            var s = (byIndex[p[1]] = byIndex[p[1]] || { properties: {} });
            s.properties[p[2]] = value;
          }
        });
      } catch (e) { /* no iterable FormData → not a multi-line add */ }

      return Object.keys(byIndex)
        .sort(function (a, b) { return Number(a) - Number(b); })
        .filter(function (i) { return byIndex[i].id != null; })
        .map(function (i) { return { index: i, item: byIndex[i] }; });
    }

    /* ── Sold-out variants are refused here, for every add on the site ──────────
       Shopify's /cart/add.js does NOT refuse them on this store. Checked 2026-10-05
       on rynoka.com: the Rosette waist panel's White variant (tracked, policy DENY,
       0 in stock, `available: false`) went into the cart with a 200. Every disabled
       "Sold Out" button was therefore the only thing standing between a shopper and
       a line checkout would later refuse — and the sticky bar never disabled, so it
       added the sold-out variant straight in.
       So the verdict is checked at the one choke point every add passes through —
       PDP form, sticky bar, Buy Now, card quick-add, drawer upsell, an app's
       re-issued payload. Verdicts come from the page's own variant JSON first, then
       /variants/<id>.js (one read per variant per page, cached). Only an explicit
       `available: false` blocks: an unreadable verdict lets the add through, because
       refusing a sale over a failed lookup is the worse failure. The refusal is
       Shopify's own 422 shape, so every caller already toasts its description. */
    var variantVerdicts = {};
    var variantJsonSeeded = false;

    function seedVerdictsFromPage() {
      if (variantJsonSeeded) return;
      variantJsonSeeded = true;
      try {
        document.querySelectorAll('script[data-toyland-variants-json]').forEach(function (s) {
          var list = JSON.parse(s.textContent || '[]');
          (Array.isArray(list) ? list : []).forEach(function (v) {
            if (v && v.id != null && typeof v.available === 'boolean') {
              variantVerdicts[String(v.id)] = Promise.resolve({ available: v.available, name: v.name || v.title || '' });
            }
          });
        });
      } catch (e) { /* malformed JSON → fall back to per-variant reads */ }
    }

    function variantVerdict(id) {
      seedVerdictsFromPage();
      var key = String(id);
      if (!variantVerdicts[key]) {
        variantVerdicts[key] = nativeFetch('/variants/' + encodeURIComponent(key) + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (v) {
            return v && typeof v.available === 'boolean' ? { available: v.available, name: v.name || v.title || '' } : null;
          })
          .catch(function () { return null; });
        // An unreadable verdict is not cached, so the next add asks again.
        variantVerdicts[key].then(function (v) { if (!v) delete variantVerdicts[key]; });
      }
      return variantVerdicts[key];
    }

    /* Every variant id one add would put in the cart, whatever shape it was posted in. */
    function addVariantIds(init) {
      var body = init && init.body;
      var ids = [];
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (body.get('id') != null) ids.push(body.get('id'));
        else formDataLines(body).forEach(function (l) { ids.push(l.item.id); });
      } else if (typeof body === 'string') {
        var p;
        try { p = JSON.parse(body); } catch (e) { p = null; }
        if (p && Array.isArray(p.items)) p.items.forEach(function (it) { if (it && it.id != null) ids.push(it.id); });
        else if (p && p.id != null) ids.push(p.id);
      }
      return ids.filter(function (id, i) { return String(id) && ids.indexOf(id) === i; });
    }

    /* Resolves to a refusal Response when any variant in the add is sold out, else null. */
    function soldOutRefusal(init) {
      var ids = addVariantIds(init);
      if (!ids.length) return Promise.resolve(null);
      return Promise.all(ids.map(variantVerdict)).then(function (verdicts) {
        var out = null;
        verdicts.forEach(function (v) { if (!out && v && v.available === false) out = v; });
        if (!out) return null;
        var description = (out.name ? out.name + ' is' : 'This item is') + ' sold out.';
        return new Response(
          JSON.stringify({ status: 422, message: 'Cart Error', tl_sold_out: true, description: description }),
          { status: 422, headers: { 'Content-Type': 'application/json' } }
        );
      });
    }

    function handleAdd(input, init, map) {
      var body = init && init.body;

      // FormData single-item add (PDP form, sticky ATC, Dawn product-form)
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var idF = body.get('id');

        if (idF == null) {
          var lines = formDataLines(body);
          if (!lines.length) return passThrough(input, init); // genuinely unknown → passthrough

          /* Same rules as the JSON multi-line branch: one shared allowance per
             variant across the payload, and a combo is refused whole rather than
             trimmed, because half a combo is a mispriced cart. */
          var isBundleF = lines.some(function (l) { return jsonItemIsBundleAdd(l.item); });
          var pendingF = {};
          var trimmedF = false;
          var overflowF = 0;

          lines.forEach(function (l) {
            var key = String(l.item.id);
            var ex = (map[key] || 0) + (pendingF[key] || 0);
            var al = TL_MAX - ex;
            var w = parseInt(l.item.quantity, 10);
            if (isNaN(w)) w = 1;
            if (w > al) {
              if (w > overflowF) overflowF = w;
              w = Math.max(0, al);
              trimmedF = true;
            }
            pendingF[key] = (pendingF[key] || 0) + w;
            l.allowed = w;
          });

          if (trimmedF && isBundleF) {
            tlShowToast(bundleBlockedMsg(overflowF), 'limit');
            return blockedResponse(bundleBlockedMsg(overflowF));
          }
          if (!lines.some(function (l) { return l.allowed > 0; })) {
            tlShowToast(LIMIT_MSG, 'limit');
            return blockedResponse();
          }
          if (trimmedF) {
            lines.forEach(function (l) {
              body.set('items[' + l.index + '][quantity]', String(l.allowed));
            });
            tlShowToast('Quantity limited to ' + TL_MAX + ' per item per order.', 'limit');
          }
          return passThrough(input, init);
        }
        var existF = map[String(idF)] || 0;
        var allowF = TL_MAX - existF;
        var wantF = parseInt(body.get('quantity'), 10);
        if (isNaN(wantF)) wantF = 1;
        if (allowF <= 0) { tlShowToast(LIMIT_MSG, 'limit'); return blockedResponse(); }
        if (wantF > allowF) {
          if (formDataIsBundleAdd(body)) {
            tlShowToast(bundleBlockedMsg(wantF), 'limit');
            return blockedResponse(bundleBlockedMsg(wantF));
          }
          body.set('quantity', String(allowF));
          tlShowToast('Only ' + allowF + ' more can be added (max ' + TL_MAX + ' per order).', 'limit');
        }
        return passThrough(input, init);
      }

      // JSON body add (ajaxAdd, tlAddToCart, bundle apps' rewritten multi-line add)
      if (typeof body === 'string') {
        var payload;
        try { payload = JSON.parse(body); } catch (e) { return passThrough(input, init); }

        if (payload && Array.isArray(payload.items)) {
          /* A multi-line payload is all-or-nothing when ANY line is a bundle line:
             the lines of one combo are priced as a set, so dropping or shrinking one
             of them silently sells a different, mispriced thing. Non-bundle bulk adds
             keep the old per-line trim. */
          var isBundlePayload = payload.items.some(jsonItemIsBundleAdd);
          var pending = {};
          var trimmed = false;
          var overflowWant = 0;

          payload.items.forEach(function (it) {
            var key = String(it.id);
            // Two lines of the same variant in one payload share the same allowance.
            var ex = (map[key] || 0) + (pending[key] || 0);
            var al = TL_MAX - ex;
            var w = parseInt(it.quantity, 10); if (isNaN(w)) w = 1;
            if (w > al) {
              if (w > overflowWant) overflowWant = w;
              w = Math.max(0, al);
              it.quantity = w;
              trimmed = true;
            }
            /* Accumulate the PARSED number, never it.quantity — a bundle app that sends
               quantities as strings ("1") turned this into string concatenation, so a
               third line of the same variant saw `pending` as "011" and got an allowance
               of 4 − 11. Every later line was then silently zeroed out of a payload that
               fitted perfectly well. */
            pending[key] = (pending[key] || 0) + w;
          });

          if (isBundlePayload && trimmed) {
            tlShowToast(bundleBlockedMsg(overflowWant), 'limit');
            return blockedResponse(bundleBlockedMsg(overflowWant));
          }

          payload.items = payload.items.filter(function (it) { return it.quantity > 0; });
          if (!payload.items.length) { tlShowToast(LIMIT_MSG, 'limit'); return blockedResponse(); }
          if (trimmed) tlShowToast('Quantity limited to ' + TL_MAX + ' per item per order.', 'limit');
          init.body = JSON.stringify(payload);
          return passThrough(input, init);
        }

        if (payload && payload.id != null) {
          var ex2 = map[String(payload.id)] || 0;
          var al2 = TL_MAX - ex2;
          var w2 = parseInt(payload.quantity, 10); if (isNaN(w2)) w2 = 1;
          if (al2 <= 0) { tlShowToast(LIMIT_MSG, 'limit'); return blockedResponse(); }
          if (w2 > al2) {
            if (jsonItemIsBundleAdd(payload)) {
              tlShowToast(bundleBlockedMsg(w2), 'limit');
              return blockedResponse(bundleBlockedMsg(w2));
            }
            payload.quantity = al2;
            tlShowToast('Only ' + al2 + ' more can be added (max ' + TL_MAX + ' per order).', 'limit');
            init.body = JSON.stringify(payload);
          }
          return passThrough(input, init);
        }
      }

      return passThrough(input, init); // unknown shape → passthrough
    }

    // /cart/change.js — sets an absolute quantity for one line; clamp to TL_MAX.
    function handleChange(input, init) {
      var body = init && init.body;
      // change.js can use JSON or URL-encoded/FormData; handle JSON (Dawn uses JSON).
      if (typeof body === 'string') {
        var payload;
        try { payload = JSON.parse(body); } catch (e) { return passThrough(input, init); }
        if (payload && payload.quantity != null) {
          var n = parseInt(payload.quantity, 10) || 0;
          if (n > TL_MAX) {
            payload.quantity = TL_MAX;
            tlShowToast(LIMIT_MSG, 'limit');
            init.body = JSON.stringify(payload);
          }
        }
        return passThrough(input, init);
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData && body.get('quantity') != null) {
        var q = parseInt(body.get('quantity'), 10) || 0;
        if (q > TL_MAX) { body.set('quantity', String(TL_MAX)); tlShowToast(LIMIT_MSG, 'limit'); }
      }
      return passThrough(input, init);
    }

    // /cart/update.js — clamp every requested quantity to TL_MAX.
    function handleUpdate(input, init) {
      var body = init && init.body;
      if (typeof body !== 'string') return passThrough(input, init);
      var payload;
      try { payload = JSON.parse(body); } catch (e) { return passThrough(input, init); }
      if (!payload || !payload.updates) return passThrough(input, init);
      var capped = false;
      if (Array.isArray(payload.updates)) {
        payload.updates = payload.updates.map(function (q) {
          var n = parseInt(q, 10) || 0;
          if (n > TL_MAX) { capped = true; return TL_MAX; }
          return n;
        });
      } else {
        Object.keys(payload.updates).forEach(function (k) {
          var n = parseInt(payload.updates[k], 10) || 0;
          if (n > TL_MAX) { payload.updates[k] = TL_MAX; capped = true; }
        });
      }
      if (capped) tlShowToast(LIMIT_MSG, 'limit');
      init.body = JSON.stringify(payload);
      return passThrough(input, init);
    }

    /* Re-sync the visible cart after ANY successful write, whoever made it.
       Our own handlers already refresh after their own adds, but Kaching Bundles runs
       with intercept_cart_request: true — it rewrites a bundle add into a multi-line
       payload and can issue cart writes of its own — and Dawn writes to /cart/change.js
       directly. Without this, a bundle add could land in the cart while the header
       count and the drawer still showed the pre-add state.
       Debounced because a single bundle add can produce several writes in a row, and
       guarded by a flag so a redundant refresh never stacks up.
       /cart.js is a read, so nothing here can re-enter this wrapper. */
    var syncTimer = null;
    function scheduleCartSync() {
      if (syncTimer) clearTimeout(syncTimer);
      syncTimer = setTimeout(function () {
        syncTimer = null;
        /* Ticket taken when the read is ISSUED, not when it resolves: a later-issued
           read is by definition the more current picture of the cart, so if an earlier
           one comes back after it, its payload is dropped rather than painted over the
           newer one. Out-of-order /cart.js responses were one way the drawer could end
           up showing a cart without the line that had just been added. */
        var ticket = tlNextCartTicket();
        nativeFetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (typeof window.tlSyncCartUI === 'function') window.tlSyncCartUI(cart, ticket);
          })
          .catch(function () {});
      }, 120);
    }

    /* When Shopify refuses a cart write, log what was actually SENT next to what came
       back. Shopify's rejection names the product but not the variant id, so a message
       like "…– Black is already sold out" on a variant with stock is impossible to
       diagnose from the toast alone — the id in the payload is what tells you whether
       the theme, or an app rewriting the payload, picked the wrong variant.
       Everything here is wrapped: a diagnostic must never be able to break an add. */
    function logRefusedWrite(input, init, res) {
      try {
        var sent;
        var body = init && init.body;
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          sent = {};
          body.forEach(function (value, key) { sent[key] = value; });
        } else if (typeof body === 'string') {
          try { sent = JSON.parse(body); } catch (e) { sent = body; }
        }
        res.clone().json().then(function (data) {
          // Our own refusals (cap, sold out) were never sent to Shopify; don't log them as its.
          if (data && (data.tl_limit || data.tl_sold_out)) return;
          console.warn('[Rynoka] cart write refused by Shopify', {
            url: urlOf(input),
            sent: sent,
            response: data
          });
        }).catch(function () {});
      } catch (e) { /* never let diagnostics break the cart */ }
    }

    function afterWrite(promise, input, init) {
      return promise.then(function (res) {
        if (res && res.ok) scheduleCartSync();
        else if (res && typeof res.clone === 'function') logRefusedWrite(input, init, res);
        return res;
      });
    }

    window.fetch = function (input, init) {
      var url = urlOf(input);

      // Only intercept when we can read the init body (all our callers pass one).
      if (/\/cart\/add(\.js)?(\?|$)/.test(url) && init && init.body && !(input instanceof Request)) {
        return afterWrite(
          enqueueWrite(function () {
            // Both reads in parallel: the verdict lookup costs an add no extra round trip.
            return Promise.all([soldOutRefusal(init), currentCartMap()]).then(function (got) {
              if (got[0]) return got[0];
              var map = got[1];
              /* The cart could not be read, so the cap has nothing to decide against.
                 The add still goes through — refusing it would strand a shopper whose
                 /cart.js is merely slow or blipping — but it goes through UNCAPPED and
                 says so in the console, rather than silently pretending the cart was
                 empty and handing out every slot. Shopify still enforces inventory. */
              if (map === null) {
                console.warn('[Rynoka] /cart.js unreadable — per-order cap not applied to this add');
                return passThrough(input, init);
              }
              return handleAdd(input, init, map);
            });
          }),
          input,
          init
        );
      }
      if (/\/cart\/update(\.js)?(\?|$)/.test(url) && init && init.body && !(input instanceof Request)) {
        return afterWrite(enqueueWrite(function () { return handleUpdate(input, init); }), input, init);
      }
      if (/\/cart\/change(\.js)?(\?|$)/.test(url) && init && init.body && !(input instanceof Request)) {
        return afterWrite(enqueueWrite(function () { return handleChange(input, init); }), input, init);
      }
      // Bodyless / Request-object cart writes still can't be inspected, but they DO
      // change the cart — queue and sync after them too rather than letting the UI drift.
      if (/\/cart\/(add|update|change)(\.js)?(\?|$)/.test(url)) {
        return afterWrite(enqueueWrite(function () { return passThrough(input, init); }), input, init);
      }
      return nativeFetch(input, init);
    };
  })();

  /** Header / legacy cart bubbles — safe with full cart JSON or /cart/add.js response */
  function updateHeaderCartBubbles(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var c = cart.item_count;
    var bubble = document.querySelector('[data-toyland-cart-count]');
    if (bubble) {
      bubble.textContent = String(c);
      if (c > 0) bubble.removeAttribute('hidden');
      else bubble.setAttribute('hidden', '');
    }
    document.querySelectorAll('[data-tl-cart-count]').forEach(function (el) {
      el.textContent = c;
      el.style.display = c > 0 ? 'flex' : 'none';
    });
  }

  /* ── Cart repaint ordering ─────────────────────────────────────────────────
     Several things read /cart.js after a write: the handler that made the add, the
     interceptor's post-write sync, Dawn's own cart-update event. Their responses can
     come back in a different order than they were asked for, and the loser used to
     win — an older payload painted last left the drawer showing a cart WITHOUT the
     line that had just been added, which is exactly the "drawer updated but my item
     isn't in it" report.

     So every read takes a ticket when it is ISSUED (a later-issued read is by
     definition the more current picture), and a payload is only painted if no
     newer-issued read has painted already. */
  var tlCartSeq = 0;
  var tlCartPainted = 0;
  var tlDispatchingOwnRefresh = false;

  function tlNextCartTicket() {
    return ++tlCartSeq;
  }

  /* Is this payload a whole cart, i.e. safe to repaint every cart surface from?
     /cart.js and /cart/update.js return one; /cart/add.js does NOT — a single add
     replies with the added LINE, a multi-line add with just `{items:[…]}`, and an
     app broadcasting "the cart changed" may send nothing but a count. Painting from
     any of those blanks the drawer, because a cart with no `items` array is
     indistinguishable from an empty one at the point where the empty state is drawn.
     That is the "I added a fourth one and my cart went empty" report: the cart was
     never empty, the drawer was repainted from something that was not a cart. */
  function tlIsWholeCart(cart) {
    return !!cart && typeof cart.item_count === 'number' && Array.isArray(cart.items);
  }

  /** Repaint every cart surface from one payload.
      Ticket omitted = "paint it, but stay correctable" — the payload is applied and
      `tlCartPainted` is left where it was, so a properly ticketed read can still
      overwrite it. Claiming the newest ticket instead let a stale payload handed in
      by an app event win permanently: every later read looked older and was dropped. */
  function tlApplyCart(cart, ticket) {
    if (!tlIsWholeCart(cart)) return;
    if (ticket == null) {
      updateHeaderCartBubbles(cart);
      tlRenderCart(cart);
      tlDispatchingOwnRefresh = true;
      try {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
      } finally {
        tlDispatchingOwnRefresh = false;
      }
      return;
    }
    if (ticket < tlCartPainted) return; // a newer read already painted
    tlCartPainted = ticket;
    updateHeaderCartBubbles(cart);
    tlRenderCart(cart);
    // Kept for the header section and any third party listening; flagged so our own
    // cart:refresh listener doesn't paint the same payload a second time.
    tlDispatchingOwnRefresh = true;
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    } finally {
      tlDispatchingOwnRefresh = false;
    }
  }

  function refreshCartUI() {
    var ticket = tlNextCartTicket();
    return ajaxCart().then(function (cart) {
      tlApplyCart(cart, ticket);
      return cart;
    });
  }

  /* Re-sync everything the shopper can see from one cart payload. Called by the
     cart-write interceptor above after ANY successful add/change/update, so a write
     made by Dawn or by Kaching's cart interceptor updates the header count and the
     drawer just like our own do.
     It routes through tlApplyCart so an interceptor sync obeys the same repaint
     ordering as every other read — and so it can never overwrite a newer one.
     tlRenderCart only reads the cart, so there is no way back into the interceptor. */
  window.tlSyncCartUI = function (cart, ticket) {
    tlApplyCart(cart, ticket);
  };

  function openDawnCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  // ─── Quick-add buttons on product cards ────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-toyland-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    // Genuinely out of stock — say so rather than swallowing the click.
    if (btn.dataset.variantAvailable === 'false') {
      tlShowToast('This one is sold out right now.', 'error');
      return;
    }

    if (btn.dataset.busy === 'true') return;
    btn.dataset.busy = 'true';

    var variantId = btn.dataset.variantId;
    var label = btn.querySelector('[data-quick-add-label]');
    var origLabel = label ? label.textContent : '';

    if (label) label.textContent = 'Adding…';

    ajaxAdd(variantId, 1)
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || response.message || 'Cart error');
        }
        return refreshCartUI();
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: cart }));
        btn.dataset.added = 'true';
        if (label) label.textContent = '✓ Added';
        openDawnCartDrawer();
        setTimeout(function () {
          btn.dataset.added = 'false';
          btn.removeAttribute('data-added');
          if (label) label.textContent = origLabel;
          btn.dataset.busy = 'false';
        }, 1600);
      })
      .catch(function (err) {
        var msg = err && err.message ? String(err.message) : '';
        /* Toast the full reason as well as shortening it onto the button: a card
           button is ~120px wide, so the truncated label alone was often unreadable —
           and when the card has no label span there was nothing to read at all. */
        tlReportAddFailure(err);
        if ((err && err.tlLimit) || /maximum of/i.test(msg)) {
          if (label) label.textContent = origLabel;
          btn.dataset.busy = 'false';
          return;
        }
        /* The button says "Try again" and nothing more specific. It used to guess
           "Sold out" by string-matching the error, which put a sold-out claim on cards
           for perfectly stocked products whenever an add failed for some unrelated
           reason — a per-order cap, a network blip, an app rejecting the write. The
           real reason is in the toast above, in full; the button only has room to say
           whether it is worth pressing again. */
        if (label) {
          label.textContent = 'Try again';
          setTimeout(function () { label.textContent = origLabel; }, 2000);
        }
        btn.dataset.busy = 'false';
      });
  });

  // ─── Product card image carousel (homepage Row 1 pattern: translateX + clones + transitionend)
  function toylandPrefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  // ─── Sticky header group height ────────────────────────────────────────────
  /* The header group (announcement bar + header) is `position: sticky; top: 0`, so
     anything else that sticks has to clear its *live* height — the announcement bar
     is optional per template and its copy can wrap on narrow screens. Publishing the
     measurement as `--tl-headergroup-h` lets CSS (sticky PDP gallery, mobile
     mini-gallery) stay declarative instead of hard-coding a pixel guess. */
  function tlHeaderGroupHeight() {
    var group =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('.tl-header');
    if (!group) return 64;
    var h = Math.round(group.getBoundingClientRect().height);
    return h > 0 ? h : 64;
  }

  (function tlTrackHeaderGroupHeight() {
    var last = -1;
    function publish() {
      var h = tlHeaderGroupHeight();
      if (h === last) return;
      last = h;
      document.documentElement.style.setProperty('--tl-headergroup-h', h + 'px');
    }
    publish();
    window.addEventListener('resize', publish, { passive: true });
    window.addEventListener('load', publish);
    var group = document.querySelector('.shopify-section-group-header-group');
    if (group && typeof ResizeObserver === 'function') {
      new ResizeObserver(publish).observe(group);
    }
  })();

  /** Product card carousel shell is hidden & JS skipped below this width (matches `toyland-redesign.css`). */
  var TL_CARD_CAROUSEL_DESKTOP_MIN = '(min-width: 1024px)';

  function toylandCardCarouselViewportIsDesktop() {
    try {
      return window.matchMedia(TL_CARD_CAROUSEL_DESKTOP_MIN).matches;
    } catch (e2) {
      return true;
    }
  }

  function toylandBindCardCarousel(viewportEl) {
    if (!viewportEl || viewportEl.dataset.tlCardCarouselBound === '1') return;
    if (!toylandCardCarouselViewportIsDesktop()) {
      if (viewportEl.dataset.tlCardCarouselBpListener !== '1') {
        viewportEl.dataset.tlCardCarouselBpListener = '1';
        var mqDesk = window.matchMedia(TL_CARD_CAROUSEL_DESKTOP_MIN);
        var onDeskBpChange = function () {
          if (mqDesk.matches && viewportEl.dataset.tlCardCarouselBound !== '1') {
            toylandBindCardCarousel(viewportEl);
          }
        };
        if (typeof mqDesk.addEventListener === 'function') mqDesk.addEventListener('change', onDeskBpChange);
        else if (mqDesk.addListener) mqDesk.addListener(onDeskBpChange);
      }
      return;
    }
    var track = viewportEl.querySelector('[data-tl-card-carousel-track]');
    var slideNodes = viewportEl.querySelectorAll('.tl-card-product__carousel-slide:not(.tl-card-product__carousel-slide--clone)');
    var n = slideNodes.length;
    if (!track || n < 2) return;
    viewportEl.dataset.tlCardCarouselBound = '1';

    var dots = viewportEl.querySelectorAll('[data-tl-card-carousel-dot]');
    var reduceMotion = toylandPrefersReducedMotion();
    /* logical index 0…n−1 when infinite clones; simplified 0…n−1 when reduced-motion */
    var pos = reduceMotion ? 0 : 1;
    var infiniteMode = !reduceMotion;
    var autoplayTimer = null;
    var CARD_CAROUSEL_AUTOPLAY_MS = 1500;

    function toylandCardCarouselHoverAutoplayOk() {
      if (reduceMotion || n < 2) return false;
      try {
        return window.matchMedia('(hover: hover)').matches;
      } catch (err) {
        return false;
      }
    }

    function slideW() {
      var raw =
        viewportEl.getBoundingClientRect().width ||
        viewportEl.offsetWidth ||
        (track && track.clientWidth) ||
        1;
      return Math.max(1, Math.round(raw));
    }

    /** Match each slide’s CSS width to translateX step (px) — avoids “next slide peek” on mobile/tablet. */
    function syncSlideWidths() {
      var w = slideW();
      var slides = viewportEl.querySelectorAll('.tl-card-product__carousel-slide');
      slides.forEach(function (slide) {
        slide.style.flexShrink = '0';
        slide.style.flexGrow = '0';
        slide.style.flexBasis = w + 'px';
        slide.style.width = w + 'px';
        slide.style.minWidth = w + 'px';
        slide.style.maxWidth = w + 'px';
        slide.style.boxSizing = 'border-box';
      });
    }

    function stopCardCarouselAutoplay() {
      if (autoplayTimer !== null) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startCardCarouselAutoplay() {
      if (!toylandCardCarouselHoverAutoplayOk()) return;
      stopCardCarouselAutoplay();
      function tickAdvance() {
        infiniteMode ? go(1) : goSimple(1);
      }
      tickAdvance();
      autoplayTimer = window.setInterval(tickAdvance, CARD_CAROUSEL_AUTOPLAY_MS);
    }

    function setTransition(enabled) {
      if (reduceMotion) {
        track.style.transition = 'none';
        return;
      }
      track.style.transition = enabled ? '' : 'none';
    }

    function apply() {
      var w = slideW();
      track.style.transform = 'translateX(' + -pos * w + 'px)';
    }

    function snapWithoutAnimation() {
      void track.offsetHeight;
      setTransition(false);
      apply();
      void track.offsetHeight;
      setTransition(true);
    }

    function canonicalIndex() {
      if (!infiniteMode) return Math.max(0, Math.min(n - 1, pos));
      if (pos <= 0) return n - 1;
      if (pos >= n + 1) return 0;
      return pos - 1;
    }

    function syncDots() {
      var idx = canonicalIndex();
      dots.forEach(function (dot, i) {
        var on = i === idx;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    function goSimple(delta) {
      pos = ((pos + delta) % n + n) % n;
      apply();
      syncDots();
    }

    function onTransitionEnd(e) {
      if (e.propertyName !== 'transform') return;
      if (!infiniteMode || n < 2) return;
      if (pos === n + 1) {
        pos = 1;
        snapWithoutAnimation();
      } else if (pos === 0) {
        pos = n;
        snapWithoutAnimation();
      }
      syncDots();
    }

    function go(delta) {
      if (!infiniteMode) {
        goSimple(delta);
        return;
      }
      var nextPos = pos + delta;
      if (nextPos < 0 || nextPos > n + 1) return;
      pos = nextPos;
      apply();
      syncDots();
    }

    function goToCanonical(targetIdx) {
      var k = targetIdx >> 0;
      if (k < 0 || k >= n || isNaN(k)) return;
      if (canonicalIndex() === k) return;
      pos = infiniteMode ? k + 1 : k;
      apply();
      syncDots();
    }

    track.style.transform = '';

    if (reduceMotion) {
      pos = 0;
      syncSlideWidths();
      apply();
      syncDots();
    } else {
      var firstEl = slideNodes[0];
      var lastEl = slideNodes[n - 1];
      var cloneFirst = firstEl.cloneNode(true);
      var cloneLast = lastEl.cloneNode(true);
      cloneFirst.classList.add('tl-card-product__carousel-slide--clone');
      cloneLast.classList.add('tl-card-product__carousel-slide--clone');
      cloneFirst.setAttribute('aria-hidden', 'true');
      cloneLast.setAttribute('aria-hidden', 'true');
      Array.prototype.slice.call(cloneFirst.querySelectorAll('[id]')).forEach(function (el) {
        el.removeAttribute('id');
      });
      Array.prototype.slice.call(cloneLast.querySelectorAll('[id]')).forEach(function (el) {
        el.removeAttribute('id');
      });
      Array.prototype.slice.call(cloneFirst.attributes).forEach(function (a) {
        if (/^data-shopify/.test(a.name)) cloneFirst.removeAttribute(a.name);
      });
      Array.prototype.slice.call(cloneLast.attributes).forEach(function (a) {
        if (/^data-shopify/.test(a.name)) cloneLast.removeAttribute(a.name);
      });

      track.insertBefore(cloneLast, track.firstChild);
      track.appendChild(cloneFirst);

      track.addEventListener('transitionend', onTransitionEnd);
      pos = 1;
      syncSlideWidths();
      /* Avoid “first slide swoops in” on cold load: prime position with transition off (hero uses same idea). */
      setTransition(false);
      apply();
      syncDots();
      requestAnimationFrame(function () {
        void track.offsetHeight;
        setTransition(true);
      });
    }

    viewportEl.addEventListener('mouseenter', startCardCarouselAutoplay);
    viewportEl.addEventListener('mouseleave', stopCardCarouselAutoplay);

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var i = parseInt(dot.getAttribute('data-index'), 10);
        if (!isNaN(i)) goToCanonical(i);
      });
    });

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        infiniteMode ? go(-1) : goSimple(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        infiniteMode ? go(1) : goSimple(1);
      }
    });

    /* Swipe / touch + suppress PDP link navigation after swipe */
    var carouselPtrX = null;
    var suppressCardLinkUntil = 0;
    viewportEl.addEventListener(
      'pointerdown',
      function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        carouselPtrX = e.clientX;
      },
      { passive: true }
    );
    viewportEl.addEventListener(
      'pointerup',
      function (e) {
        if (carouselPtrX === null) return;
        var sx = carouselPtrX;
        carouselPtrX = null;
        var dx = e.clientX - sx;
        if (Math.abs(dx) < 36) return;
        if (typeof performance !== 'undefined') {
          suppressCardLinkUntil = performance.now() + 400;
        } else {
          suppressCardLinkUntil = Date.now() + 400;
        }
        if (dx < 0) {
          infiniteMode ? go(1) : goSimple(1);
        } else {
          infiniteMode ? go(-1) : goSimple(-1);
        }
      },
      { passive: true }
    );
    viewportEl.addEventListener(
      'pointercancel',
      function () {
        carouselPtrX = null;
      },
      { passive: true }
    );
    viewportEl.addEventListener(
      'click',
      function (e) {
        var now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if (now >= suppressCardLinkUntil) return;
        var a = e.target.closest && e.target.closest('a.tl-card-product__media-link');
        if (a && viewportEl.contains(a)) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        syncSlideWidths();
        apply();
        syncDots();
      });
      ro.observe(viewportEl);
    } else {
      window.addEventListener('resize', function () {
        syncSlideWidths();
        apply();
        syncDots();
      });
    }
  }

  function toylandInitCardCarousels(root) {
    var scope = root && root.querySelector ? root : document;
    scope.querySelectorAll('[data-tl-card-carousel]').forEach(toylandBindCardCarousel);
  }

  /** Re-bind after AJAX (Shop infinite scroll, facets grid swap). Skips viewports already bound via dataset. */
  window.toylandInitCardCarousels = toylandInitCardCarousels;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      toylandInitCardCarousels();
    });
  } else {
    toylandInitCardCarousels();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev && ev.target && ev.target.querySelector) {
      toylandInitCardCarousels(ev.target);
    }
  });

  /* Read or swap the Add-to-Cart button's LABEL, never its whole contents.
     The ATC carries a cart icon plus two spans — the label and the running total — and
     a bundle app (Kaching Bundles) writes the bundle price into that total span. Setting
     `btn.textContent` for "Adding…" therefore threw away the icon and both spans: the
     icon never came back, and the restored label read "Add to CartRs. 2,208.30" (the two
     spans' text run together, since the "·" between them is a CSS ::before).
     Falls back to the button itself on any ATC that has no label span. */
  function tlAtcLabel(btn, text) {
    if (!btn) return '';
    var target = btn.querySelector('[data-pdp-atc-label]') || btn;
    var prev = target.textContent;
    if (text != null) target.textContent = text;
    return prev;
  }

  /* The variant picker's availability verdict for this PDP form — data-tl-unavailable
     on the main ATC (see syncPdpPrice for why it is not `disabled`). It also covers a
     combination with NO variant: the picker leaves the hidden id on the previous
     variant then, so the id alone would add something the shopper did not pick. */
  function tlPdpUnavailable(form) {
    var btn = form && form.querySelector('[data-toyland-pdp-submit], [data-toyland-main-atc]');
    return !!(btn && btn.getAttribute('data-tl-unavailable') === 'true');
  }

  /* The sticky bar (desktop + mobile) mirrors that verdict: "Sold Out" and disabled
     when the selected variant cannot be bought. It used to stay "Add to Cart" whatever
     was selected — and submitted the form, sold-out variant and all.
     Only undoes its OWN sold-out state on the way back, so it never re-enables a
     button an add in flight is holding disabled. */
  function tlSyncStickyAvailability(form) {
    form = form || document.querySelector('[data-toyland-pdp-form]');
    if (!form) return;
    var out = tlPdpUnavailable(form);
    document.querySelectorAll('[data-toyland-sticky-atc-add], [data-toyland-sticky-buy-now]').forEach(function (b) {
      var isAdd = b.hasAttribute('data-toyland-sticky-atc-add');
      if (out) {
        b.disabled = true;
        b.setAttribute('data-tl-unavailable', 'true');
        if (isAdd) b.textContent = 'Sold Out';
      } else if (b.getAttribute('data-tl-unavailable') === 'true') {
        b.disabled = false;
        b.removeAttribute('data-tl-unavailable');
        if (isAdd) b.textContent = 'Add to Cart';
      }
    });
  }

  /* Keep the sticky bar's price in step with the Add-to-Cart button.
     It used to recompute unit price × qty, which is only right when nothing else is
     pricing the order. With a bundle selected, Kaching rewrites the ATC total AFTER
     syncPdpPrice has run, so the bar was left advertising the single-item price next to
     a bundle-priced button. Mirroring the button removes the race — whoever writes last,
     the two agree — and it is the honest number either way, because the sticky Add to
     Cart submits the very same form as the main one.
     Blank means "nothing priceable on the button right now" (mid-"Adding…", or sold
     out), and the bar simply keeps its last good value rather than blanking out. */
  var TL_MONEY_RE = /(?:₹|Rs\.?|INR)\s*\d[\d,]*(?:\.\d{1,2})?/gi;

  function tlSyncStickyPrice(fallbackText) {
    var stickyPriceEl = document.querySelector('[data-sticky-price]');
    if (!stickyPriceEl) return;
    var next = '';
    var priceEl = document.querySelector('[data-pdp-atc-price]');
    if (priceEl && !priceEl.hidden) next = (priceEl.textContent || '').trim();
    if (!next) {
      // Some bundle apps replace the button's children outright rather than writing into
      // our span — in that case take the last money-looking token it left behind.
      var btn = document.querySelector('[data-toyland-main-atc]');
      var hits = btn ? (btn.textContent || '').match(TL_MONEY_RE) : null;
      if (hits && hits.length) next = hits[hits.length - 1].trim();
    }
    if (!next && fallbackText) next = fallbackText;
    if (next && stickyPriceEl.textContent.trim() !== next) stickyPriceEl.textContent = next;
  }

  /* Watch the button rather than our span: an app that swaps the children out would
     leave a MutationObserver bound to a detached span watching nothing. */
  function tlWatchAtcPrice() {
    var btn = document.querySelector('[data-toyland-main-atc]');
    if (!btn || typeof MutationObserver === 'undefined') return;
    if (btn.getAttribute('data-tl-price-mirror') === 'true') return;
    btn.setAttribute('data-tl-price-mirror', 'true');
    new MutationObserver(function () {
      tlSyncStickyPrice();
    }).observe(btn, { childList: true, characterData: true, subtree: true });
    tlSyncStickyPrice();
  }

  /* Hide our quantity stepper while a bundle deal is live on the product.
     Kaching owns the quantity in that case — picking "Duo" sets it to 2 itself — so
     leaving the stepper usable let the shopper push it to 3 with a 2-item bundle
     selected, and the widget, the price box and the button all then disagreed about what
     was being bought. The input stays in the DOM (display:none on its wrapper) because
     the form still has to submit a quantity.

     Detection is by the widget actually being VISIBLE, not by anything server-rendered:
     the app embed ships on every product page, but the block is injected client-side and
     only where a deal applies (its settings say blockVisibility: "selected-collections"),
     and it starts life under an inline `display: none` until the app decides to show it.
     Hence a short poll rather than a one-shot check — it re-evaluates for 12s so both the
     appear and the never-appears cases settle correctly, then stops. */
  function tlWatchBundleBlock() {
    // Was `.tl-pdp__qty` alone, back when hiding the stepper was this watcher's only
    // job. The sticky bar's bundle chip now rides the same poll, and a product with no
    // stepper still has a bar to fill — so run whenever either is on the page.
    if (!document.querySelector('.tl-pdp__qty, [data-toyland-sticky-atc]')) return;
    var ticks = 0;
    function check() {
      var block = document.querySelector('.kaching-bundles__block');
      var live = !!(block && block.offsetParent !== null);
      document.body.classList.toggle('tl-pdp-bundle-active', live);
      if (block) tlWatchBundleSelection(block);
      tlSyncStickyBundle();
      // First: it decides whether the offer header is on screen, which the two below read
      // through offsetParent.
      tlSyncComboOffers();
      tlSyncComboCount();
      tlSyncComboPrice();
    }
    check();
    var timer = window.setInterval(function () {
      check();
      if (++ticks > 48) window.clearInterval(timer);
    }, 250);
  }

  /* ── Selected-bundle name → sticky bar ────────────────────────────────────────
     Same principle as the price above: the bar does not decide what the bundle is, it
     REPORTS what the widget says. The shopper picks an offer ("Buy 2", "Duo") in
     Kaching's block, and once the bar has slid up over it the only thing left on screen
     is a price with no explanation of which offer it belongs to. The chip supplies that.

     Read from the DOM rather than from any app API: the deals are fetched client-side
     and Kaching exposes no stable JS hook, but its markup is stable — each offer is a
     `.kaching-bundles__bar`, the chosen one carries `--selected`, and the name lives in
     `.kaching-bundles__bar-title`. No name resolves → chip stays hidden and the product
     title keeps the line, which is also the state on every product with no deal. */
  function tlReadSelectedBundleName() {
    var bars = document.querySelectorAll('.kaching-bundles__bar--selected');
    for (var i = 0; i < bars.length; i++) {
      // The app embed ships placeholders on pages where no deal applies, and they keep a
      // preselected bar under `display: none`. offsetParent skips those.
      if (bars[i].offsetParent === null) continue;
      var titleEl = bars[i].querySelector('.kaching-bundles__bar-title');
      var name = titleEl ? (titleEl.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (name) return name;
    }
    return '';
  }

  function tlSyncStickyBundle() {
    var bar = document.querySelector('[data-toyland-sticky-atc]');
    if (!bar) return;
    var chip = bar.querySelector('[data-sticky-bundle]');
    var nameEl = bar.querySelector('[data-sticky-bundle-name]');
    if (!chip || !nameEl) return;

    /* A combo page has exactly one offer, preselected, so there is nothing to pick —
       and the chip exists to name the offer the shopper PICKED. Reporting a choice
       nobody made would be the bar inventing an interaction, so the product title
       keeps the line. (The deal's name is not lost: the widget's own header row shows
       it in place, see "COMBO PDP" §1 in toyland-redesign.css.) */
    var name = document.body.classList.contains('tl-pdp-combo') ? '' : tlReadSelectedBundleName();
    if (!name) {
      chip.hidden = true;
      bar.classList.remove('is-bundle');
      return;
    }
    if (nameEl.textContent !== name) {
      nameEl.textContent = name;
      chip.title = name; // the chip ellipsises; keep the full offer name reachable
      // Restart the pop rather than let a repeat class-add do nothing.
      chip.classList.remove('is-changed');
      void chip.offsetWidth;
      chip.classList.add('is-changed');
    }
    chip.hidden = false;
    bar.classList.add('is-bundle');
  }

  /* ── Combo PDP: how many items are in this combo ──────────────────────────────
     The card's header is the app's own offer row, restyled (see "COMBO PDP" §1 in
     toyland-redesign.css): Kaching writes the deal's name, label and price into it,
     and this supplies the one thing the app does not — the "· 4 items" appended
     after the name. A custom property rather than an injected node on purpose: that
     row belongs to the app, which re-renders it whenever the shopper swaps a
     product, and anything of ours inside it would be thrown away each time.

     Every row counts, the first one included: it is the container product this page is
     about, and the list shows it alongside the rest of the combo. No list at all clears
     the property so the heading stands alone rather than announcing "0 items". */
  function tlSyncComboCount() {
    if (!document.body.classList.contains('tl-pdp-combo')) return;
    var root = document.documentElement;
    /* The SELECTED offer's list, not the first one on the page. A deal with more than
       one offer renders a list per bar, and counting the wrong one puts a count in the
       heading that does not describe what the shopper is looking at. Falls back to the
       first list for the frames before any bar is marked selected. */
    var bar = tlSelectedComboBar();
    var list =
      (bar && bar.querySelector('.kaching-bundles__mix-and-match-products')) ||
      document.querySelector('.kaching-bundles__mix-and-match-products');
    var n = list ? list.querySelectorAll('.kaching-bundles__mix-and-match-product').length : 0;
    if (n < 1) {
      root.style.removeProperty('--tl-combo-count');
      return;
    }
    root.style.setProperty('--tl-combo-count', '" · ' + n + (n === 1 ? ' item"' : ' items"'));
  }

  /* ── Combo PDP: what the deal actually costs ──────────────────────────────────
     The container product Shopify holds a combo on is priced for the catalogue (₹349 on
     the Navratri look), and the Mix & Match deal the shopper is offered is priced by the
     app (₹999). Only one of those is a real answer to "what does this cost", and Liquid
     can see only the other one — the deal is fetched client-side, so nothing about it is
     in the page HTML.

     The metafield used to restate it (combo_price_details.price / scratch_price /
     discount), which meant the deal price lived in two places and drifted the moment the
     merchant changed the deal and not the JSON. This reads the offer instead: the widget
     is the thing that knows, so it is the thing we ask.

     Same DOM contract the sticky bar's chip already relies on (tlReadSelectedBundleName)
     — `.kaching-bundles__bar--selected` is the chosen offer, `--bar-price` is what it
     charges and `--bar-full-price` the struck-through total. On a combo the CSS hides the
     row those live in (`bar-main`), which costs us nothing: textContent reads out of a
     display:none node exactly the same. */
  function tlSelectedComboBar() {
    var bars = document.querySelectorAll('.kaching-bundles__bar--selected');
    var i;
    for (i = 0; i < bars.length; i++) {
      // The app embed ships preselected placeholder bars on pages with no deal, kept
      // under display:none. offsetParent skips those; on a combo the bar itself is
      // visible even though its header row is not.
      if (bars[i].offsetParent !== null) return bars[i];
    }
    /* Nothing marked yet. The app preselects an offer but renders the bars before it
       marks one, so for a frame or two there is a price on screen with no `--selected`
       anywhere. On a ONE-offer deal the first visible bar is that offer, so take it
       rather than show a skeleton through it. On a multi-offer deal it is a guess, and
       a wrong guess would quote one offer's price against another's items — so wait for
       the app to say, and let the skeleton hold for those few frames. */
    var all = tlVisibleComboBars();
    return all.length === 1 ? all[0] : null;
  }

  /** Every offer bar the shopper can actually see. The app embed ships preselected
      placeholder bars on pages with no deal and keeps them under display:none, so
      offsetParent is what separates a real offer from that scaffolding. */
  function tlVisibleComboBars() {
    var out = [];
    var all = document.querySelectorAll('.kaching-bundles__bar');
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) out.push(all[i]);
    }
    return out;
  }

  /* ── How many offers this deal has ────────────────────────────────────────────
     A combo product is not promised to have exactly one offer — that is the merchant's
     choice in Kaching, and it can change without the theme hearing about it. The combo
     layout hides the offer header (radio, name, price) to reclaim the space, which is
     right for one offer and wrong for two: it takes away the only control that picks
     between them, leaving what looks like a single combo.

     So the count decides. `tl-pdp-combo--multi` on <body> at two or more bars lifts
     that hiding and the shell flattening with it (see "COMBO PDP" §1–2 in
     assets/toyland-redesign.css), handing the page back to the app's own chrome. One
     bar — the common case — is left exactly as it was.

     Re-counted rather than read once: the app injects its block client-side, well after
     DOMContentLoaded, and re-renders it on every swap. This runs on the same 250ms poll
     and the same MutationObserver as the price for that reason. */
  function tlSyncComboOffers() {
    if (!document.body.classList.contains('tl-pdp-combo')) return;
    document.body.classList.toggle('tl-pdp-combo--multi', tlVisibleComboBars().length > 1);
  }

  /* "Rs. 2,208.30" → 220830. Paise, because that is what formatMoney and every other
     price in this file speak. The last separator is a decimal point only when 1–2 digits
     follow it ("1,256" is twelve hundred rupees, "1.25" is one rupee twenty-five) —
     every other comma and dot is a thousands separator and comes out. */
  function tlMoneyToPaise(text) {
    if (!text) return 0;
    var m = String(text).replace(/\s/g, '').match(/\d[\d.,]*/);
    if (!m) return 0;
    var raw = m[0];
    var paise = 0;
    var dec = raw.match(/[.,](\d{1,2})$/);
    if (dec) {
      paise = dec[1].length === 1 ? parseInt(dec[1], 10) * 10 : parseInt(dec[1], 10);
      raw = raw.slice(0, dec.index);
    }
    var rupees = parseInt(raw.replace(/[.,]/g, ''), 10) || 0;
    return rupees * 100 + paise;
  }

  function tlBarMoney(bar, selector) {
    var el = bar ? bar.querySelector(selector) : null;
    return el ? tlMoneyToPaise(el.textContent) : 0;
  }

  /* Fill the page's price slots from the selected offer, and drop the skeleton.
     Every slot ships empty from Liquid on a combo (sections/toyland-product.liquid), so
     until this finds a usable number the shopper sees a placeholder — never a price they
     are not being charged. It runs on the same poll and the same MutationObserver as the
     item count, so swapping an item or picking another offer re-prices the page. */
  function tlSyncComboPrice() {
    if (!document.body.classList.contains('tl-pdp-combo')) return;
    var box = document.querySelector('[data-pdp-price-box]');
    if (!box) return;

    var bar = tlSelectedComboBar();
    var price = tlBarMoney(bar, '.kaching-bundles__bar-price');
    // Nothing priceable yet — hold the skeleton rather than flash a zero.
    if (!price) return;
    var compare = tlBarMoney(bar, '.kaching-bundles__bar-full-price');
    var hasSale = compare > price;
    var discount = hasSale ? Math.round(((compare - price) * 100) / compare) : 0;

    tlSetText(document.querySelector('[data-pdp-price]'), formatMoney(price));

    var compareEl = document.querySelector('[data-pdp-compare]');
    if (compareEl) {
      if (hasSale) tlSetText(document.querySelector('[data-pdp-compare-val]'), formatMoney(compare));
      compareEl.hidden = !hasSale;
    }

    var saveEl = document.querySelector('[data-pdp-save]');
    if (saveEl) {
      if (hasSale) tlSetText(saveEl, discount + '% OFF');
      saveEl.hidden = !hasSale;
    }

    var saveLineEl = document.querySelector('[data-pdp-save-line]');
    if (saveLineEl) {
      if (hasSale) tlSetText(document.querySelector('[data-pdp-save-amount]'), formatMoney(compare - price));
      saveLineEl.hidden = !hasSale;
    }

    // The gallery's "% OFF" flag, which quotes the same deal.
    var imgSaveEl = document.querySelector('[data-pdp-img-save]');
    if (imgSaveEl) {
      if (hasSale) tlSetText(imgSaveEl, discount + '% OFF');
      imgSaveEl.hidden = !hasSale;
    }

    /* The Add to Cart total is the app's to write — it knows the quantities the deal
       implies — so only fill the gap before it does. Writing over a value it already put
       there would start the two of us trading writes through tlWatchAtcPrice. */
    var atcPrice = document.querySelector('[data-pdp-atc-price]');
    if (atcPrice && !(atcPrice.textContent || '').trim()) atcPrice.textContent = formatMoney(price);
    tlSyncStickyPrice();

    box.classList.remove('tl-pdp__price-box--pending');
  }

  /** Write only on change — these run on a 250ms poll, and a no-op write still churns. */
  function tlSetText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  /* Selecting a different offer re-renders the bars, so watch the widget for changes.
     Attributes are included because the swap of `--selected` between two existing bars is
     a class change and nothing else — a childList-only observer misses it entirely.

     Bound to the app's `<kaching-bundle>` host, not to `.kaching-bundles__block` inside
     it: the app re-renders by replacing that block, which would leave an observer on the
     block watching a detached node (the same trap tlWatchAtcPrice documents). The host
     is the element the app inserts once and then fills. */
  function tlWatchBundleSelection(block) {
    if (typeof MutationObserver === 'undefined') return;
    var root = block.closest('kaching-bundle, kaching-bundle-deals') || block;
    if (root.getAttribute('data-tl-bundle-mirror') === 'true') return;
    root.setAttribute('data-tl-bundle-mirror', 'true');
    new MutationObserver(function () {
      tlSyncStickyBundle();
      // Swapping an item re-renders the list; the count in the heading follows it, and
      // so does the price — a swap can change what the deal charges. The offer count
      // leads, for the same reason it does on the poll: the app can add or drop a bar
      // in the same re-render, and the other two read the layout it decides.
      tlSyncComboOffers();
      tlSyncComboCount();
      tlSyncComboPrice();
    }).observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function tlInitPdpPriceWatchers() {
    tlWatchAtcPrice();
    tlWatchBundleBlock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tlInitPdpPriceWatchers);
  } else {
    tlInitPdpPriceWatchers();
  }

  // ─── PDP form submit (main ATC + sticky bar via requestSubmit) ─────────────
  /** Pending "✓ Added" → "Add to Cart" restore, so a follow-up add can cancel it. */
  var tlAtcRestoreTimer = null;

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-toyland-pdp-form]');
    if (!form) return;

    var sub = e.submitter;
    if (sub && sub.getAttribute('name') === 'checkout') {
      return;
    }

    e.preventDefault();

    /* The selected variant is sold out (or the combination does not exist). Every
       button that reaches this form — main, sticky, Enter in the qty box — stops here,
       before any label is stashed, so "Sold Out" never becomes a label to restore. */
    if (tlPdpUnavailable(form)) {
      tlShowToast('This option is sold out — please pick another.', 'error');
      tlSyncStickyAvailability(form);
      return;
    }

    /* One add per click. Without this, a double-click (or Enter plus a click, or the
       sticky bar's requestSubmit landing on top of a click) fired two adds — and
       worse, the second one captured "Adding…" as the label to restore, so the button
       was left reading "Adding…" for good. A shopper looking at a button stuck on
       "Adding…" reads it as dead and stops clicking, which is one of the ways
       "add to cart does nothing and says nothing" happens. */
    if (form.dataset.tlAdding === '1') return;
    form.dataset.tlAdding = '1';

    /* The previous add's "✓ Added" is still on screen on a timer. Cancel it, or it
       fires mid-flight and resets this add's button to "Add to Cart" while it is
       still working — which reads as the click having been dropped. */
    if (tlAtcRestoreTimer) {
      clearTimeout(tlAtcRestoreTimer);
      tlAtcRestoreTimer = null;
    }

    var fd = new FormData(form);
    var mainBtn = form.querySelector('[data-toyland-pdp-submit], [data-toyland-main-atc]');
    // BOTH sticky bars (desktop + mobile) carry this attribute — querySelector saw only
    // the desktop one, so on a phone the button the shopper actually pressed never
    // showed a busy or a result state.
    var stickyBtns = Array.prototype.slice.call(
      document.querySelectorAll('[data-toyland-sticky-atc-add]')
    );

    /* Original labels are stashed on the element so no later restore can write back a
       transient "Adding…"/"✓ Added" as if it were the original.
       Refreshed from the live label on every submit EXCEPT when that label is one of
       our own transients — a stash taken once and never updated went stale the moment
       the variant picker rewrote the button ("Sold Out" → "Add to Cart"), and the next
       restore put the old text back on a product that was in stock. */
    var TL_TRANSIENT_LABELS = ['Adding…', '✓ Added'];
    function tlStashLabel(el, live) {
      if (TL_TRANSIENT_LABELS.indexOf(live) === -1 && !/^Max \d+ per order$/.test(live)) {
        el.dataset.tlOrigLabel = live;
      } else if (el.dataset.tlOrigLabel == null) {
        el.dataset.tlOrigLabel = 'Add to Cart';
      }
      return el.dataset.tlOrigLabel;
    }

    var origMain = mainBtn ? tlStashLabel(mainBtn, tlAtcLabel(mainBtn)) : '';
    stickyBtns.forEach(function (b) {
      tlStashLabel(b, b.textContent.trim());
    });

    function setStickyLabel(text) {
      stickyBtns.forEach(function (b) {
        b.textContent = text == null ? b.dataset.tlOrigLabel : text;
      });
    }

    var settled = false;
    function releaseAtcButtons() {
      if (settled) return;
      settled = true;
      form.dataset.tlAdding = '0';
      if (mainBtn) mainBtn.dataset.busy = 'false';
      stickyBtns.forEach(function (b) { b.disabled = false; });
      // The shopper may have picked a sold-out variant while this add was in flight.
      tlSyncStickyAvailability(form);
    }
    function restoreAtcLabels() {
      if (mainBtn) tlAtcLabel(mainBtn, origMain);
      setStickyLabel(null);
      tlSyncStickyAvailability(form);
      // Same race for the main label: origMain was stashed against the old variant.
      if (tlPdpUnavailable(form)) syncPdpPrice(form);
    }

    if (mainBtn) {
      mainBtn.dataset.busy = 'true';
      tlAtcLabel(mainBtn, 'Adding…');
    }
    stickyBtns.forEach(function (b) { b.disabled = true; });
    setStickyLabel('Adding…');

    // Max-3-per-item is enforced centrally by the /cart/add interceptor below.
    fetch('/cart/add.js', { method: 'POST', body: fd })
      .then(tlReadAddResponse)
      .then(function () {
        return refreshCartUI();
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: cart }));
        if (mainBtn) tlAtcLabel(mainBtn, '✓ Added');
        setStickyLabel('✓ Added');

        /* EVERY Add to Cart on the PDP — main button, sticky bar, bundle or not — ends
           here: the line goes into the cart, the drawer opens, the header count is
           already refreshed by refreshCartUI() above.
           There is deliberately NO bundle → checkout shortcut. Kaching's own
           "skipCart" is false in its deal settings (verified in the app-embed config on
           the PDP), i.e. checkout routing is managed here via cart → checkout, not by
           the app. Anything that jumped straight to checkout on a bundle would fight
           that setting and strand the shopper past the cart. */
        openDawnCartDrawer();
        /* Clickable again immediately; the "✓ Added" label lingers a beat as feedback.
           Releasing on a timer alone meant a shopper who clicked during that beat was
           silently ignored. */
        releaseAtcButtons();
        tlAtcRestoreTimer = setTimeout(restoreAtcLabels, 1100);
      })
      .catch(function (err) {
        /* Say something. Always. The old version only messaged when the text happened
           to match /maximum of/ — every other failure (Shopify 422, sold out, network
           drop, an app rejecting the add) silently put "Add to Cart" back, so from the
           shopper's side the button had simply stopped working. */
        var isLimit = (err && err.tlLimit) || /maximum of/i.test((err && err.message) || '');
        tlReportAddFailure(err);
        releaseAtcButtons();
        if (isLimit) {
          var capLabel = 'Max ' + TL_MAX_PER_ORDER + ' per order';
          if (mainBtn) tlAtcLabel(mainBtn, capLabel);
          setStickyLabel(capLabel);
          tlAtcRestoreTimer = setTimeout(restoreAtcLabels, 1600);
        } else {
          restoreAtcLabels();
        }
      });
  });

  // ─── PDP “Buy Now” (main CTA + sticky bar) → Razorpay Magic Checkout ────────
  // Razorpay Magic Checkout (the "magicx" app embed) is a client-side script
  // that INTERCEPTS a real click/submit on a standard Shopify checkout button
  // (name="checkout") and opens its panel in-page. A plain
  // `window.location = '/checkout'` fires no such event, so magicx can't catch
  // it and the shopper lands on Shopify's native checkout. So we: (1) add the
  // current variant/qty via AJAX — which also passes through the max-3 cart
  // guard above — then (2) dispatch a genuine Shopify checkout submit for
  // magicx to intercept. If the app isn't active (e.g. local `theme dev`, where
  // Razorpay's script doesn't load), the submit falls through to /checkout.
  function tlTriggerMagicCheckout() {
    var f = document.createElement('form');
    f.method = 'post';
    f.action = (window.routes && window.routes.cart_url) || '/cart';
    f.style.display = 'none';
    var b = document.createElement('button');
    b.type = 'submit';
    b.name = 'checkout';
    f.appendChild(b);
    document.body.appendChild(f);
    // A real click bubbles to magicx's delegated listener (which preventDefaults
    // and opens the panel); otherwise it natively submits → Shopify checkout.
    b.click();
  }

  document.addEventListener('click', function (e) {
    var buyNow = e.target.closest('[data-toyland-buy-now], [data-toyland-sticky-buy-now]');
    if (!buyNow || buyNow.disabled) return;
    e.preventDefault();

    var form = document.querySelector('[data-toyland-pdp-form]');
    if (!form) return;
    if (tlPdpUnavailable(form)) {
      tlShowToast('This option is sold out — please pick another.', 'error');
      tlSyncStickyAvailability(form);
      return;
    }

    var orig = buyNow.textContent;
    buyNow.disabled = true;
    buyNow.dataset.busy = 'true';
    buyNow.textContent = 'Processing…';

    var restored = false;
    function restoreBuyNow() {
      if (restored) return;
      restored = true;
      // Back to the variant's verdict, not blindly enabled — it may have changed meanwhile.
      buyNow.disabled = tlPdpUnavailable(form);
      buyNow.dataset.busy = 'false';
      buyNow.textContent = orig;
      tlSyncStickyAvailability(form);
      window.removeEventListener('pageshow', restoreBuyNow);
    }
    // When magicx intercepts, its panel opens in-page and no navigation happens,
    // so nothing else would ever clear the busy state — the button would read
    // “Processing…” forever once the shopper closes the panel. Restore it while
    // the panel still covers the page (harmless), and again on bfcache restore
    // if the shopper did navigate to checkout and pressed Back.
    window.addEventListener('pageshow', restoreBuyNow);

    fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
      .then(tlReadAddResponse)
      .then(function () {
        tlTriggerMagicCheckout();
        setTimeout(restoreBuyNow, 1500);
      })
      .catch(function (err) {
        /* The per-order cap toasts its own reason; everything else used to fail in
           total silence here — the button just went back to "Buy Now" and the shopper
           was left pressing a control that never did anything. */
        tlReportAddFailure(err);
        restoreBuyNow();
      });
  });

  // ─── Cart page checkout → Shopify's native checkout (never Razorpay Magic) ──
  // The same magicx mechanism as above, used in reverse. Magic Checkout only
  // intercepts a REAL click/submit on a name="checkout" control; assigning
  // window.location fires neither event, so the navigation reaches Shopify
  // untouched. Capture phase + stopImmediatePropagation also stops the click ever
  // reaching magicx's delegated (bubble-phase) listener.
  //
  // Deliberately scoped to the cart page: the PDP "Buy Now" above still routes to
  // Magic Checkout, and it works by building its own name="checkout" button — an
  // unscoped handler here would swallow that click and silently kill Magic on the
  // PDP. The template-cart test is inside the handler, not around it, so it can't
  // depend on whether document.body exists when this file runs.
  document.addEventListener(
    'click',
    function (e) {
      if (!document.body || !document.body.classList.contains('template-cart')) return;
      var el = e.target;
      if (!el || typeof el.closest !== 'function') return;
      var btn = el.closest('[name="checkout"]');
      if (!btn || btn.disabled) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      // Skipping the cart form submit costs nothing: line quantities go through
      // /cart/change.js and Dawn's <cart-note> persists the note via
      // /cart/update.js on change, so the cart is already saved server-side.
      window.location.href = '/checkout';
    },
    true
  );

  // ─── PDP tabs (Details | Specs | Reviews | Shipping) ───────────────────────
  function toylandActivatePdpTab(key) {
    var root = document.querySelector('[data-toyland-tabs-root]');
    if (!root) return;
    var tab = root.querySelector('[data-toyland-tab="' + key + '"]');
    var panel = root.querySelector('[data-toyland-tab-panel="' + key + '"]');
    if (!tab || !panel) return;
    root.querySelectorAll('[data-toyland-tab]').forEach(function (b) {
      var on = b === tab;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-toyland-tab-panel]').forEach(function (p) {
      p.classList.toggle('is-active', p.dataset.toylandTabPanel === key);
    });
  }

  function toylandOpenPdpReviewsTab() {
    // Reviews is now its own centered section (#tl-reviews) — no tab to activate,
    // so just scroll to it. (Activating a non-existent 'reviews' tab would blank
    // out the Details/Specs/Shipping tabs.)
    requestAnimationFrame(function () {
      var el = document.getElementById('tl-reviews');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          el.focus({ preventScroll: true });
        } catch (err) {
          el.focus();
        }
      }
      window.dispatchEvent(new Event('resize'));
    });
  }

  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-toyland-tab]');
    if (tab) {
      e.preventDefault();
      var key = tab.dataset.toylandTab;
      toylandActivatePdpTab(key);
      return;
    }
    var reviewsLink = e.target.closest('[data-toyland-open-reviews], a[href="#tl-reviews"]');
    if (reviewsLink) {
      e.preventDefault();
      toylandOpenPdpReviewsTab();
      if (history.replaceState) {
        history.replaceState(null, '', '#tl-reviews');
      } else {
        location.hash = 'tl-reviews';
      }
    }
  });

  function toylandPdpReviewsFromHash() {
    if (location.hash !== '#tl-reviews') return;
    toylandOpenPdpReviewsTab();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', toylandPdpReviewsFromHash);
  } else {
    toylandPdpReviewsFromHash();
  }
  window.addEventListener('hashchange', toylandPdpReviewsFromHash);

  /** Hide PLP card review pills when count is 0 or Judge.me shows "No reviews". */
  function toylandHideEmptyCardReviewBadges(scope) {
    var cards;
    if (scope && scope.nodeType === 1) {
      cards =
        scope.matches && scope.matches('.tl-card-product')
          ? [scope]
          : scope.querySelectorAll('.tl-card-product');
    } else {
      cards = document.querySelectorAll('.tl-card-product');
    }
    Array.prototype.forEach.call(cards, function (card) {
      var cap = card.querySelector('.tl-card-product__rating-cap');
      if (!cap || cap.hidden) return;
      if (cap.querySelector('.tl-card-product__rating-cap-link')) return;

      var badge = cap.querySelector('.jdgm-preview-badge');
      var n = badge ? parseInt(badge.getAttribute('data-number-of-reviews'), 10) : NaN;
      var text = (cap.textContent || '').replace(/\s+/g, ' ').trim();
      if ((!n || n < 1) && /no reviews/i.test(text)) {
        cap.hidden = true;
        return;
      }
      if (badge) {
        var avg = parseFloat(badge.getAttribute('data-average-rating'));
        if ((!n || n < 1) && (!avg || avg <= 0)) cap.hidden = true;
      }
    });
  }

  // ─── Homepage — Judge.me store reviews carousel (toyland-reviews) ─────────
  function toylandRefreshJudgeMeWidgets() {
    try {
      if (window.jdgmCacheServer && typeof window.jdgmCacheServer.reloadAll === 'function') {
        window.jdgmCacheServer.reloadAll();
      } else if (window.jdgm_preloader && typeof window.jdgm_preloader === 'function') {
        window.jdgm_preloader();
      }
    } catch (e0) {
      /* Judge.me loader API */
    }
    try {
      if (window.jdgm && typeof window.jdgm.documentReady === 'function') {
        window.jdgm.documentReady();
      }
    } catch (e) {
      /* Judge.me not loaded */
    }
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e2) {
      /* ignore */
    }
    toylandHideEmptyCardReviewBadges();
  }

  function toylandScheduleHideEmptyCardReviewBadges() {
    toylandHideEmptyCardReviewBadges();
    setTimeout(toylandHideEmptyCardReviewBadges, 400);
    setTimeout(toylandHideEmptyCardReviewBadges, 1200);
    setTimeout(toylandHideEmptyCardReviewBadges, 2800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', toylandScheduleHideEmptyCardReviewBadges);
  } else {
    toylandScheduleHideEmptyCardReviewBadges();
  }

  function toylandJdgmHomeMountLooksHydrated(jdgmRoot) {
    if (!jdgmRoot) return false;
    var nodes = jdgmRoot.querySelectorAll('[class*="jdgm-"]');
    var i;
    var el;
    var r;
    for (i = 0; i < nodes.length; i++) {
      el = nodes[i];
      r = el.getBoundingClientRect();
      if (r.height > 12 && r.width > 24 && (el.textContent || '').trim().length > 8) {
        return true;
      }
    }
    if (jdgmRoot.querySelector('iframe[src*="judge"], iframe[src*="judgeme"]')) {
      return true;
    }
    return false;
  }

  function toylandInitHomeJudgeMeReviews() {
    if (!document.querySelector('[data-tl-judgeme-home-carousel]')) return;
    toylandHomeReviewsObserveJdgm();
    setTimeout(toylandRefreshJudgeMeWidgets, 0);
    setTimeout(toylandRefreshJudgeMeWidgets, 800);
    setTimeout(toylandRefreshJudgeMeWidgets, 1500);
    setTimeout(toylandRefreshJudgeMeWidgets, 3500);
    setTimeout(toylandHomeReviewsApplySlideLimitDebounced, 900);
    setTimeout(toylandHomeReviewsApplySlideLimitDebounced, 2200);
    setTimeout(toylandHomeReviewsApplySlideLimitDebounced, 4500);
    setTimeout(toylandHomeReviewsJdgmFallback, 12000);
    setTimeout(toylandHomeReviewsJdgmFallback, 24000);
  }

  /** Legacy: manual theme cards removed; no-op unless data-tl-allow-manual-fallback="true" and fallback node exists. */
  function toylandHomeReviewsJdgmFallback() {
    var root = document.querySelector('[data-toyland-reviews-root]');
    if (!root || root.getAttribute('data-tl-allow-manual-fallback') !== 'true') return;
    var jdgmRoot = root.querySelector('[data-tl-judgeme-home-carousel]');
    var manual = root.querySelector('[data-tl-reviews-manual-fallback]');
    if (!jdgmRoot || !manual) return;

    if (toylandJdgmHomeMountLooksHydrated(jdgmRoot)) {
      root.classList.remove('tl-reviews--jdgm-failed');
      manual.setAttribute('hidden', '');
      return;
    }

    var mount = jdgmRoot.querySelector(
      '.jdgm-carousel-wrapper, .jdgm-widget.jdgm-carousel-wrapper, .jdgm-carousel, .jdgm-cards-carousel, .jdgm-testimonials-carousel, .jdgm-videos-carousel, [class*="jdgm-carousel"]'
    );
    var hasContent = false;
    if (mount) {
      var slides = mount.querySelectorAll(
        '.jdgm-carousel-item, .jdgm-carousel__item, [class*="carousel-item"], .jdgm-rev, .jdgm-rev__pic-img, .jdgm-media-card'
      );
      if (slides.length) hasContent = true;
      if (!hasContent && (mount.textContent || '').trim().length > 40) hasContent = true;
    }
    if (!hasContent && toylandJdgmHomeMountLooksHydrated(jdgmRoot)) hasContent = true;

    if (hasContent) {
      root.classList.remove('tl-reviews--jdgm-failed');
      manual.setAttribute('hidden', '');
    } else {
      root.classList.add('tl-reviews--jdgm-failed');
      manual.removeAttribute('hidden');
    }
  }

  function toylandHomeReviewsJdgmRecover() {
    var root = document.querySelector('[data-toyland-reviews-root]');
    if (!root) return;
    var jdgmRoot = root.querySelector('[data-tl-judgeme-home-carousel]');
    if (!jdgmRoot) return;
    var manual = root.querySelector('[data-tl-reviews-manual-fallback]');
    if (toylandJdgmHomeMountLooksHydrated(jdgmRoot)) {
      root.classList.remove('tl-reviews--jdgm-failed');
      if (manual) manual.setAttribute('hidden', '');
      return;
    }
    if (!manual) return;
    var mount = jdgmRoot.querySelector(
      '.jdgm-carousel-wrapper, .jdgm-widget.jdgm-carousel-wrapper, .jdgm-carousel, .jdgm-cards-carousel, .jdgm-testimonials-carousel, .jdgm-videos-carousel, [class*="jdgm-carousel"]'
    );
    if (!mount) return;
    var slides = mount.querySelectorAll(
      '.jdgm-carousel-item, .jdgm-carousel__item, [class*="carousel-item"], .jdgm-rev, .jdgm-media-card'
    );
    var hasContent =
      slides.length > 0 || (mount.textContent || '').trim().length > 40 || toylandJdgmHomeMountLooksHydrated(jdgmRoot);
    if (!hasContent) return;
    root.classList.remove('tl-reviews--jdgm-failed');
    manual.setAttribute('hidden', '');
  }

  function toylandHomeReviewsObserveJdgm() {
    var root = document.querySelector('[data-toyland-reviews-root]');
    if (!root || typeof MutationObserver === 'undefined') return;
    var jdgmRoot = root.querySelector('[data-tl-judgeme-home-carousel]');
    if (!jdgmRoot || jdgmRoot.getAttribute('data-tl-jdgm-observed') === '1') return;
    jdgmRoot.setAttribute('data-tl-jdgm-observed', '1');
    var obs = new MutationObserver(function () {
      toylandHomeReviewsJdgmRecover();
      toylandHomeReviewsApplySlideLimitDebounced();
    });
    obs.observe(jdgmRoot, { childList: true, subtree: true });
  }

  var toylandHomeReviewsLimitTimer = null;

  function toylandCountJdgmSlideStars(slide) {
    if (!slide || !slide.querySelector) return 0;
    var row = slide.querySelector('.jdgm-carousel-item__review-rating');
    var scope = row || slide;
    var on = scope.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-star.jdgm-star--on');
    if (on.length) return on.length;
    var all = scope.querySelectorAll('.jdgm-star');
    if (!all.length) return 0;
    var c = 0;
    var i;
    for (i = 0; i < all.length; i++) {
      if (all[i].classList.contains('jdgm--on') || all[i].classList.contains('jdgm-star--on')) c++;
    }
    return c || all.length;
  }

  function toylandSlideTimestampMs(slide) {
    if (!slide || !slide.querySelector) return 0;
    var ts = slide.querySelector('.jdgm-carousel-item__timestamp, .jdgm-rev__timestamp');
    if (!ts) return 0;
    var txt = (ts.textContent || '').trim();
    var m = txt.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) return new Date(+m[3], +m[2] - 1, +m[1]).getTime();
    var d = Date.parse(txt);
    return isNaN(d) ? 0 : d;
  }

  /** Keep first N slides, or highest-star first when data-tl-reviews-sort-rating is true. */
  function toylandApplyHomeReviewsCarouselLimit(root) {
    if (!root || !root.getAttribute) return;
    var lim = parseInt(root.getAttribute('data-tl-reviews-limit') || '5', 10);
    if (!lim || lim < 1) lim = 5;
    var sortRating = root.getAttribute('data-tl-reviews-sort-rating') === 'true';

    var jdgmRoot = root.querySelector('[data-tl-judgeme-home-carousel]');
    if (!jdgmRoot) return;

    var carousel = jdgmRoot.querySelector('.jdgm-carousel');
    if (!carousel) return;

    var wrapper = carousel.querySelector('.jdgm-carousel__item-wrapper');
    var slides = [];
    var el;
    if (wrapper) {
      for (el = wrapper.firstElementChild; el; el = el.nextElementSibling) {
        if (!el.matches) continue;
        if (el.matches('a.jdgm-carousel-item') || el.matches('.jdgm-carousel-item')) slides.push(el);
      }
    }
    if (!slides.length) {
      slides = Array.prototype.slice.call(carousel.querySelectorAll('a.jdgm-carousel-item, .jdgm-carousel-item'));
    }
    if (!slides.length) return;

    var i;
    if (!sortRating) {
      if (wrapper) wrapper.removeAttribute('data-tl-jdgm-flex');
      for (i = 0; i < slides.length; i++) {
        slides[i].style.removeProperty('order');
        if (i < lim) slides[i].classList.remove('tl-jdgm-carousel-item--suppressed');
        else slides[i].classList.add('tl-jdgm-carousel-item--suppressed');
      }
      return;
    }

    var ranked = [];
    for (i = 0; i < slides.length; i++) {
      ranked.push({
        el: slides[i],
        rating: toylandCountJdgmSlideStars(slides[i]),
        ts: toylandSlideTimestampMs(slides[i])
      });
    }
    ranked.sort(function (a, b) {
      if (b.rating !== a.rating) return b.rating - a.rating;
      return b.ts - a.ts;
    });

    var keep = [];
    for (i = 0; i < ranked.length && keep.length < lim; i++) {
      keep.push(ranked[i].el);
    }

    var orderMap = {};
    for (i = 0; i < keep.length; i++) orderMap[keep[i]] = i;

    for (i = 0; i < slides.length; i++) {
      if (keep.indexOf(slides[i]) >= 0) slides[i].classList.remove('tl-jdgm-carousel-item--suppressed');
      else slides[i].classList.add('tl-jdgm-carousel-item--suppressed');
    }

    if (wrapper) {
      wrapper.setAttribute('data-tl-jdgm-flex', '1');
      for (i = 0; i < ranked.length; i++) {
        var o = orderMap[ranked[i].el];
        ranked[i].el.style.order = o !== undefined ? String(o) : '999';
      }
    }
  }

  function toylandHomeReviewsApplySlideLimitDebounced() {
    if (toylandHomeReviewsLimitTimer) clearTimeout(toylandHomeReviewsLimitTimer);
    toylandHomeReviewsLimitTimer = setTimeout(function () {
      toylandHomeReviewsLimitTimer = null;
      var root = document.querySelector('[data-toyland-reviews-root]');
      if (!root) return;
      var jdgmRoot = root.querySelector('[data-tl-judgeme-home-carousel]');
      if (!jdgmRoot) return;
      var wrapper = jdgmRoot.querySelector('.jdgm-carousel__item-wrapper');
      var n = 0;
      if (wrapper) {
        var c;
        for (c = wrapper.firstElementChild; c; c = c.nextElementSibling) {
          if (c.matches && (c.matches('a.jdgm-carousel-item') || c.matches('.jdgm-carousel-item'))) n++;
        }
      }
      if (n === 0) return;
      toylandApplyHomeReviewsCarouselLimit(root);
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', toylandInitHomeJudgeMeReviews);
  } else {
    toylandInitHomeJudgeMeReviews();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    var t = ev && ev.target;
    if (t && t.querySelector && t.querySelector('[data-tl-judgeme-home-carousel]')) {
      var r = t.querySelector('[data-toyland-reviews-root]');
      if (r) {
        r.classList.remove('tl-reviews--jdgm-failed');
        var m = r.querySelector('[data-tl-reviews-manual-fallback]');
        if (m) m.setAttribute('hidden', '');
        var j = r.querySelector('[data-tl-judgeme-home-carousel]');
        if (j) j.removeAttribute('data-tl-jdgm-observed');
      }
      toylandInitHomeJudgeMeReviews();
    }
  });

  // ─── PDP qty +/- ───────────────────────────────────────────────────────────
  function syncPdpPrice(form) {
    if (!form) return;
    var qtyInput = form.querySelector('[data-qty-input]');
    var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    var unitPrice = parseInt(form.dataset.initialPrice || '0', 10) || 0;
    var unitCompare = parseInt(form.dataset.initialCompare || '0', 10) || 0;
    var totalPrice = unitPrice * qty;
    var hasSale = unitCompare > unitPrice && unitCompare > 0;

    /* The price box states what the PRODUCT costs, so it is UNIT priced and does not move
       with the stepper. It used to render unit × qty, which made "₹349" jump to "₹1,047"
       at qty 3 — read as a price change rather than a line total, and flatly wrong beside
       a bundle widget quoting its own per-deal prices. The running total still exists,
       on the Add to Cart button and the sticky bar, which is where an order total belongs.
       This also puts the JS back in agreement with the server-rendered markup, which was
       always unit priced. */
    var priceEl = document.querySelector('[data-pdp-price]');
    var compareEl = document.querySelector('[data-pdp-compare]');
    var compareValEl = document.querySelector('[data-pdp-compare-val]');
    var saveEl = document.querySelector('[data-pdp-save]');

    /* A COMBO page states the deal's price, not the container variant's, and only the
       Kaching widget knows it — tlSyncComboPrice owns every slot below. The variant here
       is the ₹0 this page ships with (see data-initial-price in the section), so letting
       this run would blank the deal price out on the shopper's next tap of the stepper.
       The ATC label logic further down still runs: "Sold Out" is about the variant. */
    var comboPage = document.body.classList.contains('tl-pdp-combo');

    if (priceEl && !comboPage) priceEl.textContent = formatMoney(unitPrice);
    // The sticky bar is synced at the END of this function, once the ATC total it
    // mirrors has been written below.

    if (compareEl && !comboPage) {
      if (hasSale) {
        // keep the "MRP " prefix intact — update only the amount span when present
        if (compareValEl) compareValEl.textContent = formatMoney(unitCompare);
        else compareEl.textContent = formatMoney(unitCompare);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (saveEl && !comboPage) {
      if (hasSale) {
        var discount = Math.round(((unitCompare - unitPrice) * 100) / unitCompare);
        saveEl.textContent = discount + '% OFF';
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }

    // "You save ₹X" — unit saving, matching the price box above it and the first paint.
    var saveLineEl = document.querySelector('[data-pdp-save-line]');
    var saveAmountEl = document.querySelector('[data-pdp-save-amount]');
    if (saveLineEl && !comboPage) {
      if (hasSale) {
        if (saveAmountEl) saveAmountEl.textContent = formatMoney(unitCompare - unitPrice);
        saveLineEl.hidden = false;
      } else {
        saveLineEl.hidden = true;
      }
    }

    // Add-to-Cart button: label and running line total live in separate spans, so the
    // price can be hidden on mobile by CSS alone ("Add to Cart · ₹899" → "Add to Cart").
    var atcBtn = document.querySelector('[data-toyland-main-atc]');
    var atcLabel = document.querySelector('[data-pdp-atc-label]');
    var atcPrice = document.querySelector('[data-pdp-atc-price]');
    /* "Sold Out" is a claim about the VARIANT, so it is read from the variant picker's
       own verdict (data-tl-unavailable, set alongside the disabled attribute in
       sections/toyland-product.liquid) and not from `disabled` alone.
       `disabled` is not that signal: our own add disables buttons while it is in
       flight, and the bundle apps toggle the ATC too. Reading it directly meant any
       transient disable got stamped into the label as "Sold Out" — and because
       nothing re-runs the picker afterwards, it stayed there on a product that was
       perfectly in stock. Falls back to `disabled` on the first paint, before the
       picker has run (and on products with no picker at all, where Liquid's disabled
       attribute genuinely is the availability flag). */
    var atcSoldOut = !!(
      atcBtn &&
      (atcBtn.hasAttribute('data-tl-unavailable')
        ? atcBtn.getAttribute('data-tl-unavailable') === 'true'
        : atcBtn.disabled)
    );
    // An add in flight owns the label ("Adding…" / "✓ Added"); don't overwrite it.
    var atcBusy = !!(atcBtn && atcBtn.dataset.busy === 'true');
    /* With a live bundle deal the app owns the order total — it knows tier pricing we
       have no way to compute — so leave the number it put on the button alone. Writing
       unit × qty over it is what produced "Duo" selected while the button quoted ₹1,047
       (₹349 × 3): our value landing last and winning. */
    var bundleActive = document.body.classList.contains('tl-pdp-bundle-active');
    if (atcLabel && !atcBusy) atcLabel.textContent = atcSoldOut ? 'Sold Out' : 'Add to Cart';
    if (atcPrice) {
      if (!bundleActive && !comboPage) atcPrice.textContent = formatMoney(totalPrice);
      atcPrice.hidden = atcSoldOut;
    }

    /* No fallback on a combo: the fallback exists so the bar keeps a number when the
       button has none, and on a combo the only number we could offer is the container's.
       Better an empty bar for a moment than the wrong price in it. */
    tlSyncStickyPrice(comboPage ? '' : formatMoney(totalPrice));
    // Runs on first paint and on every variant change (the picker fires pdp-price-sync).
    tlSyncStickyAvailability(form);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-toyland-qty]');
    if (!btn) return;
    e.preventDefault();
    var dir = btn.dataset.toylandQty;
    var input = btn.parentElement.querySelector('input[type="number"]');
    if (!input) return;
    var v = parseInt(input.value, 10) || 1;
    // KRN Toyz: cap at max (3 per order) so inc can't exceed the limit
    var maxQ = parseInt(input.getAttribute('max'), 10) || Infinity;
    if (dir === 'inc') v = Math.min(maxQ, v + 1);
    else if (dir === 'dec') v = Math.max(1, v - 1);
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncPdpPrice(btn.closest('[data-toyland-pdp-form]'));
  });

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-qty-input]')) return;
    // KRN Toyz: clamp typed quantity to the input's own [min, max] — the max
    // attribute is written by Liquid from qty_max (see TL_MAX_PER_ORDER at the top).
    var minQ = parseInt(e.target.getAttribute('min'), 10) || 1;
    var maxQ = parseInt(e.target.getAttribute('max'), 10) || Infinity;
    var qv = parseInt(e.target.value, 10);
    if (isNaN(qv) || qv < minQ) qv = minQ;
    if (qv > maxQ) qv = maxQ;
    e.target.value = qv;
    syncPdpPrice(e.target.closest('[data-toyland-pdp-form]'));
  });

  document.addEventListener('toyland:pdp-price-sync', function (e) {
    var form =
      e.target && e.target.closest
        ? e.target.closest('[data-toyland-pdp-form]')
        : e.target &&
            e.target.matches &&
            e.target.matches('[data-toyland-pdp-form]')
          ? e.target
          : null;
    syncPdpPrice(form);

    toylandMaybeSyncPdpShareFromEvent(e.target);
  });

  // ─── PDP social proof (dynamic buyers today + rotating recent-order cities) ───
  // The buyer count is generated client-side but seeded by product + date so it stays
  // stable for the whole day (no jumping on reload). Cities rotate for a "live" feel.
  (function () {
    function initPdpSocial() {
      var el = document.querySelector('[data-pdp-social]');
      if (!el || el.dataset.tlSocialInit) return;
      el.dataset.tlSocialInit = '1';

      var buyersEl = el.querySelector('[data-pdp-buyers]');
      var citiesEl = el.querySelector('[data-pdp-cities]');
      var pid = parseInt(el.getAttribute('data-product-id'), 10) || 0;
      var cities = (el.getAttribute('data-cities') || 'Mumbai, Delhi, Bengaluru')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);

      // Stable-per-day linear-congruential seed from product id + today's date.
      var now = new Date();
      var daySeed = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
      var seed = ((pid % 99991) + daySeed) % 233280;
      function rand(n) {
        seed = (seed * 9301 + 49297) % 233280;
        return Math.floor((seed / 233280) * n);
      }

      var buyers = 37 + rand(96); // 37–132 bought "today"
      if (buyersEl) buyersEl.textContent = buyers;

      function pick3() {
        if (cities.length <= 3) return cities.join(', ');
        var pool = cities.slice();
        var out = [];
        for (var i = 0; i < 3 && pool.length; i++) {
          out.push(pool.splice(rand(pool.length), 1)[0]);
        }
        return out.join(', ');
      }
      if (citiesEl) citiesEl.textContent = pick3();

      el.hidden = false;

      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (citiesEl && cities.length > 3 && !reduceMotion) {
        setInterval(function () {
          citiesEl.textContent = pick3();
        }, 5000);
      }
    }

    if (document.readyState !== 'loading') initPdpSocial();
    else document.addEventListener('DOMContentLoaded', initPdpSocial);
  })();

  // ─── PDP gallery pin: fixed-distance track (generic across departments) ──────
  // The gallery stays fully visible and fixed while the info column scrolls, then the
  // whole page scrolls on. To make this identical on every product — short single-variant
  // toy pages AND tall apparel pages with big size-chart/spec accordions — the gallery
  // is wrapped in a fixed-height "track" so its sticky pin always lasts the SAME distance
  // (half the gallery's own height), independent of how tall the info column grows.
  // Without this, a tall info column keeps the gallery pinned far too long ("stuck").
  // Desktop only; on mobile the track has no height and the gallery is static.
  (function () {
    var gallery = document.querySelector('.tl-pdp__gallery');
    if (!gallery) return;
    var track = gallery.parentNode && gallery.parentNode.classList
      && gallery.parentNode.classList.contains('tl-pdp__gallery-track')
      ? gallery.parentNode
      : null;
    if (!track) {
      track = document.createElement('div');
      track.className = 'tl-pdp__gallery-track';
      gallery.parentNode.insertBefore(track, gallery);
      track.appendChild(gallery);
    }
    function update() {
      if (window.matchMedia('(max-width: 900px)').matches) {
        track.style.removeProperty('height');
        return;
      }
      var gh = gallery.getBoundingClientRect().height;
      if (!gh) return;
      // pin distance = half the gallery → track height = gallery + half = 1.5× gallery
      track.style.height = Math.round(gh * 1.5) + 'px';
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    var imgEl = gallery.querySelector('.tl-pdp__main-img-el');
    if (imgEl && !imgEl.complete) imgEl.addEventListener('load', update);
  })();

  // ─── PDP accordions: smooth open/close (native <details> snaps instantly) ───
  // Intercepts the summary toggle and animates the body height so panels glide open
  // and closed. Falls back to the instant native toggle when motion is reduced.
  (function () {
    var DUR = 320;
    var reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function slide(item, body, opening) {
      item.dataset.tlAnimating = '1';
      var start = opening ? 0 : body.scrollHeight;
      var end = opening ? body.scrollHeight : 0;
      body.style.overflow = 'hidden';
      body.style.height = start + 'px';
      body.style.transition = 'none';
      // force reflow so the start height is committed before we transition
      void body.offsetHeight;
      body.style.transition = 'height ' + DUR + 'ms cubic-bezier(.25,.8,.25,1)';
      body.style.height = end + 'px';

      var done = false;
      function finish() {
        if (done) return;
        done = true;
        body.style.transition = '';
        body.style.height = '';
        body.style.overflow = '';
        if (!opening) item.open = false;
        delete item.dataset.tlAnimating;
      }
      body.addEventListener('transitionend', function te(e) {
        if (e.propertyName !== 'height') return;
        body.removeEventListener('transitionend', te);
        finish();
      });
      setTimeout(finish, DUR + 80); // safety net if transitionend is missed
    }

    // Close every other open panel in the same accordion group (single-open behaviour).
    function closeSiblings(group, keep) {
      if (!group) return;
      Array.prototype.forEach.call(group.querySelectorAll('.tl-pdp__acc-item[open]'), function (other) {
        if (other === keep || other.dataset.tlAnimating) return;
        var ob = other.querySelector('.tl-pdp__acc-body');
        if (reduce || !ob) { other.open = false; }
        else slide(other, ob, false);
      });
    }

    document.addEventListener('click', function (e) {
      var head = e.target.closest('.tl-pdp__acc-head');
      if (!head) return;
      var item = head.parentElement; // <details.tl-pdp__acc-item>
      if (!item || !item.classList.contains('tl-pdp__acc-item')) return;
      var body = item.querySelector('.tl-pdp__acc-body');
      if (!body) return;
      if (item.dataset.tlAnimating) { e.preventDefault(); return; }
      // Take over the toggle in all cases so single-open holds even without animation.
      e.preventDefault();
      var group = item.closest('.tl-pdp__acc');
      if (!item.open) {
        closeSiblings(group, item); // only one open at a time
        item.open = true; // reveal, then animate from 0 → content height
        if (!reduce) slide(item, body, true);
      } else {
        if (reduce) item.open = false;
        else slide(item, body, false); // animate to 0, then drop [open] in finish()
      }
    });
  })();

  /** PDP overlay share icon + modal: refresh links from selected variant where possible */
  function toylandResolvePdpShareUrl(formEl, root) {
    if (!root) return '';
    var origin = root.getAttribute('data-share-origin') || '';
    origin = origin.replace(/\/*$/g, '') || '';

    function fallbackHref() {
      return root.getAttribute('data-share-url') || '';
    }

    var form =
      formEl && formEl.matches && formEl.matches('[data-toyland-pdp-form]')
        ? formEl
        : formEl || document.querySelector('[data-toyland-pdp-form]');

    var fallbackFull = fallbackHref();
    if (!form) return fallbackFull || window.location.href;

    var vidInput = form.querySelector('input[name="id"]');
    var vid = vidInput ? String(vidInput.value) : '';

    var jsonEl = form.querySelector('[data-toyland-variants-json]');

    function byVariantFallback() {
      if (!fallbackFull || !vid) return fallbackFull || window.location.href;
      try {
        var u = String(fallbackFull);
        var re = /([?&#])variant=/;
        if (re.test(u)) {
          return u.replace(/([?&#])variant=\d+/g, '$1variant=' + encodeURIComponent(vid));
        }
        var join = u.indexOf('?') >= 0 ? '&' : '?';
        return u + join + 'variant=' + encodeURIComponent(vid);
      } catch (_e) {
        return fallbackFull || window.location.href;
      }
    }

    if (!jsonEl || !vid) return byVariantFallback();

    try {
      var variants = JSON.parse(jsonEl.textContent || '[]');
      var v =
        variants && variants.find
          ? variants.find(function (x) {
              return String(x.id) === vid;
            })
          : null;

      if (v && v.url) {
        var rel = String(v.url);
        if (/^https?:\/\//i.test(rel)) return rel;
        if (rel.indexOf('/') !== 0) rel = '/' + rel;
        if (origin) return origin + rel;
        return fallbackFull || window.location.href;
      }
    } catch (_e2) {}

    return byVariantFallback();
  }

  /** Modal may be moved under `document.body` while open — resolve it for queries. */
  function toylandGetPdpShareModalEl(root) {
    if (!root) return null;
    var nested = root.querySelector('[data-pdp-share-modal]');
    if (nested) return nested;
    var mid = root.getAttribute('data-pdp-share-modal-id');
    return mid ? document.getElementById(mid) : null;
  }

  function toylandQueryInPdpShareScope(root, selector) {
    var el = root.querySelector(selector);
    if (el) return el;
    var modal = toylandGetPdpShareModalEl(root);
    return modal ? modal.querySelector(selector) : null;
  }

  function toylandQueryAllInPdpShareScope(root, selector) {
    var fromRoot = root.querySelectorAll(selector);
    if (fromRoot.length)
      return Array.prototype.slice.call(fromRoot);
    var modal = toylandGetPdpShareModalEl(root);
    return modal
      ? Array.prototype.slice.call(modal.querySelectorAll(selector))
      : [];
  }

  function toylandPdpShareRootFromModal(modal) {
    if (!modal) return null;
    var rid = modal.getAttribute('data-pdp-share-root-id');
    if (rid) {
      var byId = document.getElementById(rid);
      if (byId) return byId;
    }
    return modal.closest('[data-pdp-gallery-share]');
  }

  function toylandApplyPdpShareLinks(opts) {
    var root =
      opts && opts.root
        ? opts.root
        : document.querySelector('[data-pdp-gallery-share]');
    if (!root || !root.getAttribute('data-share-title')) return;

    var form =
      opts && opts.form
        ? opts.form
        : document.querySelector('[data-toyland-pdp-form]');
    var title = root.getAttribute('data-share-title') || document.title || '';
    var url = toylandResolvePdpShareUrl(form, root);

    root.setAttribute('data-share-url', url);

    var encU = encodeURIComponent(url);

    var fb = toylandQueryInPdpShareScope(root, '[data-pdp-share-link="facebook"]');
    if (fb) {
      fb.setAttribute(
        'href',
        'https://www.facebook.com/sharer/sharer.php?u=' + encU
      );
    }

    var tw = toylandQueryInPdpShareScope(root, '[data-pdp-share-link="twitter"]');
    if (tw) {
      tw.setAttribute(
        'href',
        'https://twitter.com/intent/tweet?url=' + encU + '&text=' + encodeURIComponent(title)
      );
    }

    var wa = toylandQueryInPdpShareScope(root, '[data-pdp-share-link="whatsapp"]');
    if (wa) {
      wa.setAttribute(
        'href',
        'https://wa.me/?text=' + encodeURIComponent(title + '\n' + url)
      );
    }
  }

  function toylandMaybeSyncPdpShareFromEvent(dispatchTarget) {
    var roots = document.querySelectorAll('[data-pdp-gallery-share]');
    if (!roots.length) return;

    var form =
      dispatchTarget &&
      dispatchTarget.matches &&
      dispatchTarget.matches('[data-toyland-pdp-form]')
        ? dispatchTarget
        : dispatchTarget &&
            dispatchTarget.closest &&
            dispatchTarget.closest('[data-toyland-pdp-form]')
          ? dispatchTarget.closest('[data-toyland-pdp-form]')
          : document.querySelector('[data-toyland-pdp-form]');

    roots.forEach(function (root) {
      toylandApplyPdpShareLinks({ root: root, form: form });
    });
  }

  function toylandCopyToClipboardFallBack(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.readOnly = true;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      ta.setAttribute('aria-hidden', 'true');
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (_e) {
      return false;
    }
  }

  function toylandAnnouncePdpShare(root, msg) {
    var live = toylandQueryInPdpShareScope(root, '[data-pdp-share-live]');
    if (!live || !msg) return;
    live.textContent = msg;
    setTimeout(function () {
      if (live) live.textContent = '';
    }, 2800);
  }

  /** Share icon on PDP image opens modal — delegated handlers (theme-editor safe). */
  (function toylandInitPdpShareModal() {
    var tlPdpSharePrevFocus = null;
    /** iOS/mobile: avoid `<html>{overflow:hidden}` — locks scroll via body.fixed + restores Y. */
    var tlPdpShareScrollLockY = null;

    function tlPdpShareApplyScrollLock() {
      if (tlPdpShareScrollLockY !== null) return;
      tlPdpShareScrollLockY =
        typeof window.scrollY === 'number'
          ? window.scrollY
          : window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0;
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + tlPdpShareScrollLockY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    function tlPdpShareClearScrollLock() {
      if (tlPdpShareScrollLockY === null) return;
      var savedY = tlPdpShareScrollLockY;
      tlPdpShareScrollLockY = null;
      document.documentElement.style.removeProperty('scroll-behavior');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.removeProperty('scroll-behavior');
      window.requestAnimationFrame(function () {
        window.scrollTo(0, savedY);
      });
    }

    function refreshAll() {
      var roots = document.querySelectorAll('[data-pdp-gallery-share]');
      if (!roots.length) return;
      var form = document.querySelector('[data-toyland-pdp-form]');

      roots.forEach(function (root) {
        toylandApplyPdpShareLinks({ root: root, form: form });
        toylandQueryAllInPdpShareScope(root, '[data-pdp-share-native]').forEach(function (nativeBtn) {
          if (typeof navigator.share === 'function') {
            nativeBtn.removeAttribute('hidden');
          } else {
            nativeBtn.setAttribute('hidden', '');
          }
        });
      });
    }

    function tlPdpShareOpen(trigger) {
      var root =
        trigger && trigger.closest('[data-pdp-gallery-share]');
      var modal = root && toylandGetPdpShareModalEl(root);
      if (!trigger || !root || !modal) return;

      toylandApplyPdpShareLinks({
        root: root,
        form: document.querySelector('[data-toyland-pdp-form]'),
      });

      toylandQueryAllInPdpShareScope(root, '[data-pdp-share-native]').forEach(function (nativeBtn) {
        if (typeof navigator.share === 'function') {
          nativeBtn.removeAttribute('hidden');
        } else {
          nativeBtn.setAttribute('hidden', '');
        }
      });

      tlPdpSharePrevFocus = document.activeElement;
      tlPdpShareApplyScrollLock();
      /* Re-parent to <body> so `position:fixed` is viewport-based (avoids transformed/stacking PDP ancestors). */
      if (modal.parentNode !== document.body) {
        document.body.appendChild(modal);
      }
      modal.setAttribute('data-pdp-share-portaled', '1');
      modal.removeAttribute('hidden');
      trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('tl-pdp-share-modal-open');

      window.setTimeout(function () {
        var closeBtn = modal.querySelector('.tl-pdp__share-modal-dismiss');
        if (closeBtn && typeof closeBtn.focus === 'function') {
          closeBtn.focus({ preventScroll: true });
        }
      }, 10);
    }

    function tlPdpShareClose(modal) {
      if (!modal || modal.hasAttribute('hidden')) return;
      var root = toylandPdpShareRootFromModal(modal);
      var trigger =
        root && root.querySelector('[data-pdp-share-open]');

      modal.setAttribute('hidden', '');
      if (modal.getAttribute('data-pdp-share-portaled') && root) {
        root.appendChild(modal);
        modal.removeAttribute('data-pdp-share-portaled');
      }
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      document.documentElement.classList.remove('tl-pdp-share-modal-open');
      tlPdpShareClearScrollLock();

      if (
        tlPdpSharePrevFocus &&
        typeof tlPdpSharePrevFocus.focus === 'function' &&
        document.body.contains(tlPdpSharePrevFocus)
      ) {
        tlPdpSharePrevFocus.focus({ preventScroll: true });
      }
      tlPdpSharePrevFocus = null;
    }

    refreshAll();

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-pdp-share-open]');
      if (opener) {
        e.preventDefault();
        tlPdpShareOpen(opener);
        return;
      }

      var closer = e.target.closest('[data-pdp-share-close]');
      var modalClosing =
        closer &&
        closer.closest('[data-pdp-share-modal]:not([hidden])');
      if (
        closer !== null &&
        modalClosing !== null &&
        !(e.altKey || e.ctrlKey || e.metaKey || e.shiftKey)
      ) {
        e.preventDefault();
        tlPdpShareClose(modalClosing);
        return;
      }

      var nb = e.target.closest('[data-pdp-share-native]');
      var modalOpen =
        nb && nb.closest('[data-pdp-share-modal]:not([hidden])');
      var rootNb =
        (nb && nb.closest('[data-pdp-gallery-share]')) ||
        (modalOpen && toylandPdpShareRootFromModal(modalOpen));
      if (
        modalOpen &&
        rootNb &&
        typeof navigator.share === 'function' &&
        !(e.altKey || e.ctrlKey || e.metaKey || e.shiftKey)
      ) {
        e.preventDefault();
        var u =
          rootNb.getAttribute('data-share-url') || window.location.href;
        var t = rootNb.getAttribute('data-share-title') || '';
        navigator
          .share({ title: t, url: u })
          .then(function () {
            tlPdpShareClose(modalOpen);
          })
          .catch(function () {});
        return;
      }

      var copyBtn = e.target.closest('[data-pdp-share-copy]');
      var modalCopy =
        copyBtn && copyBtn.closest('[data-pdp-share-modal]:not([hidden])');
      var rootCopy =
        (copyBtn && copyBtn.closest('[data-pdp-gallery-share]')) ||
        (modalCopy && toylandPdpShareRootFromModal(modalCopy));
      if (
        copyBtn !== null &&
        rootCopy !== null &&
        modalCopy !== null &&
        !(e.altKey || e.ctrlKey || e.metaKey || e.shiftKey)
      ) {
        e.preventDefault();
        var url = rootCopy.getAttribute('data-share-url') || '';

        function doneCopy() {
          var msg =
            window.accessibilityStrings &&
            typeof window.accessibilityStrings.shareSuccess === 'string'
              ? window.accessibilityStrings.shareSuccess
              : 'Link copied to clipboard';

          toylandAnnouncePdpShare(rootCopy, msg);
          copyBtn.classList.add('is-copied');

          window.setTimeout(function () {
            copyBtn.classList.remove('is-copied');
          }, 1800);

          window.setTimeout(function () {
            copyBtn.focus({ preventScroll: true });
          }, 10);
        }

        function failCopy() {
          toylandAnnouncePdpShare(
            rootCopy,
            'Unable to copy link — select and copy the address manually.'
          );
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(doneCopy).catch(function () {
            if (toylandCopyToClipboardFallBack(url)) doneCopy();
            else failCopy();
          });
        } else {
          if (toylandCopyToClipboardFallBack(url)) doneCopy();
          else failCopy();
        }
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var modalOpen = document.querySelector(
        '[data-pdp-share-modal]:not([hidden])'
      );
      if (!modalOpen) return;
      e.preventDefault();
      tlPdpShareClose(modalOpen);
    });

    document.addEventListener('shopify:section:load', function (ev) {
      var t = ev.target;
      if (!t || !t.querySelector || !t.querySelector('[data-pdp-gallery-share]')) return;
      refreshAll();
    });
  })();

  // ─── PDP variant pickers — update form variant id ──────────────────────────
  document.addEventListener('change', function (e) {
    var picker = e.target.closest('[data-toyland-variant-radio]');
    if (!picker) return;
    var form = picker.closest('[data-toyland-pdp-form]');
    if (!form) return;
    var variantInput = form.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = picker.value;
  });

  // ─── Sticky ATC visibility (PDP) ───────────────────────────────────────────
  var stickyAtc = document.querySelector('[data-toyland-sticky-atc]');
  var pdpForm = document.querySelector('[data-toyland-pdp-form]');
  if (stickyAtc && pdpForm) {
    var pdpHero = document.querySelector('.tl-pdp__grid') || document.querySelector('.tl-pdp');
    var mainAtc = pdpForm.querySelector('[data-toyland-main-atc], [data-toyland-pdp-submit], button[type="submit"]');
    var mainAtcVisible = true;
    var heroVisible = true;
    var mqStickyMobile = window.matchMedia('(max-width: 899px)');
    var setStickyVisible = function (isVisible) {
      if (isVisible) {
        stickyAtc.removeAttribute('hidden');
        document.body.classList.add('tl-has-sticky-atc');
      } else {
        stickyAtc.setAttribute('hidden', '');
        document.body.classList.remove('tl-has-sticky-atc');
      }
    };
    var syncSticky = function () {
      if (mqStickyMobile.matches) {
        // Mobile: reveal as soon as the main Add to Cart button scrolls out of
        // view (the hero grid is tall on mobile, so gating on it shows too late).
        setStickyVisible(!mainAtcVisible);
      } else {
        // Desktop: reveal as soon as the main Add to Cart / Buy Now buttons
        // scroll up out of view (i.e. reach the top), not the whole hero grid.
        setStickyVisible(!mainAtcVisible);
      }
    };
    // Re-evaluate when crossing the mobile/desktop breakpoint (resize/rotate).
    if (mqStickyMobile.addEventListener) {
      mqStickyMobile.addEventListener('change', syncSticky);
    } else if (mqStickyMobile.addListener) {
      mqStickyMobile.addListener(syncSticky);
    }

    if (mainAtc && 'IntersectionObserver' in window) {
      var atcObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          mainAtcVisible = entry.isIntersecting;
          syncSticky();
        });
      }, { threshold: [0, 0.2, 0.6, 1] });
      atcObserver.observe(mainAtc);

      if (pdpHero) {
        var heroObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            heroVisible = entry.isIntersecting;
            syncSticky();
          });
        }, { threshold: [0, 0.08, 0.2] });
        heroObserver.observe(pdpHero);
      }
    } else if (mainAtc) {
      var onScrollCheck = function () {
        var atcRect = mainAtc.getBoundingClientRect();
        mainAtcVisible = atcRect.top < window.innerHeight && atcRect.bottom > 0;
        if (pdpHero) {
          var heroRect = pdpHero.getBoundingClientRect();
          heroVisible = heroRect.top < window.innerHeight && heroRect.bottom > 0;
        } else {
          heroVisible = false;
        }
        syncSticky();
      };
      window.addEventListener('scroll', onScrollCheck, { passive: true });
      onScrollCheck();
    } else {
      setStickyVisible(false);
    }
  }

  // ─── PDP gallery: thumbnails + prev/next + hover zoom ─────────────────────
  (function initToylandPdpGallery() {
    var gallery = document.querySelector('[data-pdp-gallery]');
    if (!gallery) return;

    var mainWrap = gallery.querySelector('[data-pdp-main-img]');
    var img = mainWrap ? mainWrap.querySelector('.tl-pdp__main-img-el') : null;
    var zoomInner = mainWrap ? mainWrap.querySelector('[data-pdp-zoom-inner]') : null;
    var sizesAttr =
      gallery.getAttribute('data-pdp-main-sizes') || '(max-width: 900px) 100vw, min(720px, 50vw)';

    function thumbsList() {
      // Phase 2: honour the per-colour gallery swap — exclude thumbs hidden by the
      // apparel variant picker so prev/next/swipe/keyboard cycle only the shown colour.
      return Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-thumb]'))
        .filter(function (t) { return t.style.display !== 'none' && !t.hidden; });
    }

    function activeThumbIndex() {
      var thumbs = thumbsList();
      if (!thumbs.length) return -1;
      var cur = gallery.querySelector('[data-pdp-thumb].is-active');
      var ix = thumbs.indexOf(cur);
      return ix >= 0 ? ix : 0;
    }

    var thumbStripScroller = gallery.querySelector('.tl-pdp__thumbs-scroller');
    var thumbStripRow = thumbStripScroller
      ? thumbStripScroller.querySelector('.tl-pdp__thumbs')
      : null;

    /* Is the strip a REAL scroller, or the transform-driven pager?

       Desktop keeps the thumb row in an `overflow: hidden` box and slides it with a
       transform — there is no scrollbar to drag, so the arrows are the only way
       through and the transform is what moves it.

       Under 640px toyland-mobile.css turns that same box into `overflow-x: auto` so
       the strip can be swiped, and the two mechanisms do NOT compose:

         - a transform shifts the row visually but `scrollLeft` still bottoms out at
           0, so whatever it pushed past the left edge could not be swiped back to;
         - a transform does not change layout size either, so `scrollWidth` still
           measures the row where it WOULD sit — swiping to the right end ran `offset`
           px past the last thumb into empty space.

       Both of those were the same bug seen from two ends (2026-08-26, reported).
       Read off computed style rather than a hardcoded breakpoint: the CSS switches
       modes at 640px for the scroller but 899px for the rest of the mobile gallery,
       and this stays correct if either moves. */
    function thumbStripIsScrollable() {
      if (!thumbStripScroller || typeof window.getComputedStyle !== 'function') return false;
      var ox = window.getComputedStyle(thumbStripScroller).overflowX;
      return ox === 'auto' || ox === 'scroll' || ox === 'overlay';
    }

    /* Bring the active preview into view: by scrolling where the strip scrolls, by
       sliding the row where it does not. `smooth` is for a thumb the shopper just
       picked; the initial sync and resizes jump, because animating a strip into place
       on load is motion nobody asked for. */
    function syncThumbStripView(smooth) {
      if (!thumbStripScroller || !thumbStripRow) return;
      var native = thumbStripIsScrollable();
      /* A transform left over from a desktop-width render is exactly what breaks the
         swipe, and a resize across 640px can hand us one. Clear it before measuring —
         the reads below force layout, so they see the cleared value. */
      if (native && thumbStripRow.style.transform) thumbStripRow.style.transform = '';
      /* And the mirror of it: an overflow:hidden box keeps whatever scrollLeft it was
         given, so a resize from phone to desktop width would otherwise stack a stale
         scroll offset underneath the transform. */
      if (!native && thumbStripScroller.scrollLeft) thumbStripScroller.scrollLeft = 0;

      var active = gallery.querySelector('[data-pdp-thumb].is-active');
      if (!active) {
        if (!native) thumbStripRow.style.transform = '';
        return;
      }
      var viewW = thumbStripScroller.clientWidth;
      var rowW = thumbStripRow.scrollWidth;
      if (!viewW || rowW <= viewW) {
        if (!native) thumbStripRow.style.transform = 'translateX(0)';
        return;
      }

      var pad = 8;
      var thumbW = active.offsetWidth;
      /* Where the thumb sits inside the SCROLLED content. offsetLeft answers that only
         when the row is the offsetParent, which it is not on the mobile strip — rects
         plus the current scrollLeft answer it wherever the offsetParent turns out to
         be. */
      var current = native ? thumbStripScroller.scrollLeft : 0;
      var thumbLeft = native
        ? current + (active.getBoundingClientRect().left - thumbStripScroller.getBoundingClientRect().left)
        : active.offsetLeft;

      var maxOffset = (native ? thumbStripScroller.scrollWidth : rowW) - viewW;
      var centered = thumbLeft - (viewW - thumbW) / 2;
      var offset = Math.max(0, Math.min(centered, maxOffset));
      if (thumbLeft < offset + pad) {
        offset = Math.max(0, thumbLeft - pad);
      } else if (thumbLeft + thumbW > offset + viewW - pad) {
        offset = Math.min(maxOffset, thumbLeft + thumbW - viewW + pad);
      }

      if (!native) {
        thumbStripRow.style.transform = 'translateX(' + -offset + 'px)';
        return;
      }
      // Sub-pixel churn on every resize tick is a scroll event storm for nothing.
      if (Math.abs(offset - current) < 1) return;
      var reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (smooth && !reduced && typeof thumbStripScroller.scrollTo === 'function') {
        thumbStripScroller.scrollTo({ left: offset, behavior: 'smooth' });
      } else {
        thumbStripScroller.scrollLeft = offset;
      }
    }

    function activateThumb(thumb) {
      if (!thumb || !img) return;
      var src = thumb.dataset.src;
      var srcset = thumb.dataset.srcset;
      var alt = thumb.dataset.alt;
      thumbsList().forEach(function (x) {
        x.classList.remove('is-active');
      });
      thumb.classList.add('is-active');
      if (src) img.src = src;
      if (srcset) img.srcset = srcset;
      else img.removeAttribute('srcset');
      img.setAttribute('sizes', sizesAttr);
      if (alt) img.alt = alt;
      img.style.transform = '';
      img.style.transformOrigin = '';
      if (mainWrap) mainWrap.classList.remove('is-zooming');
      syncThumbStripView(true);
      syncMiniStrip();
    }

    function activateThumbByDelta(delta) {
      var thumbs = thumbsList();
      if (!thumbs.length || !img) return;
      var len = thumbs.length;
      var i = activeThumbIndex();
      if (i < 0) i = 0;
      var next = (i + delta + len * 10) % len;
      activateThumb(thumbs[next]);
    }

    if (thumbStripScroller && thumbStripRow) {
      window.addEventListener(
        'resize',
        function () {
          syncThumbStripView();
        },
        { passive: true }
      );
      if (typeof requestAnimationFrame === 'function') {
        // Wrapped, not passed by reference: rAF hands its callback a timestamp, which
        // would arrive as a truthy `smooth` and animate the strip on first paint.
        requestAnimationFrame(function () {
          syncThumbStripView();
        });
      } else {
        syncThumbStripView();
      }
    }

    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-pdp-thumb]');
      if (thumb && gallery.contains(thumb)) {
        activateThumb(thumb);
        return;
      }
      if (!gallery.contains(e.target)) return;
      if (e.target.closest('[data-pdp-thumbs-prev]')) {
        e.preventDefault();
        activateThumbByDelta(-1);
        return;
      }
      if (e.target.closest('[data-pdp-thumbs-next]')) {
        e.preventDefault();
        activateThumbByDelta(1);
        return;
      }
      if (e.target.closest('[data-pdp-gallery-prev]')) {
        e.preventDefault();
        activateThumbByDelta(-1);
        return;
      }
      if (e.target.closest('[data-pdp-gallery-next]')) {
        e.preventDefault();
        activateThumbByDelta(1);
      }
    });

    gallery.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (!thumbsList().length || !img) return;
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      var ae = document.activeElement;
      if (!ae || (!gallery.contains(ae) && ae !== gallery)) return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') activateThumbByDelta(-1);
      else activateThumbByDelta(1);
    });

    // Hero swipe — mobile / tablet (horizontal gesture changes image)
    if (mainWrap && img && thumbsList().length > 1 && mainWrap.getAttribute('data-pdp-swipe-bound') !== 'true') {
      mainWrap.setAttribute('data-pdp-swipe-bound', 'true');
      var swipeStartX = null;
      var swipeStartY = null;
      var swipeThresholdPx = 44;
      mainWrap.addEventListener(
        'touchstart',
        function (e) {
          if (e.touches.length !== 1) return;
          swipeStartX = e.touches[0].clientX;
          swipeStartY = e.touches[0].clientY;
        },
        { passive: true }
      );
      mainWrap.addEventListener(
        'touchcancel',
        function () {
          swipeStartX = null;
          swipeStartY = null;
        },
        { passive: true }
      );
      mainWrap.addEventListener(
        'touchend',
        function (e) {
          if (swipeStartX === null || swipeStartY === null) return;
          var t = e.changedTouches[0];
          if (!t) return;
          var dx = t.clientX - swipeStartX;
          var dy = t.clientY - swipeStartY;
          swipeStartX = null;
          swipeStartY = null;
          if (Math.abs(dx) < swipeThresholdPx) return;
          if (Math.abs(dx) < Math.abs(dy) * 1.12) return;
          if (dx < 0) activateThumbByDelta(1);
          else activateThumbByDelta(-1);
        },
        { passive: true }
      );
    }

    // ─── Mobile sticky mini-gallery ──────────────────────────────────────────
    /* Mirrors the desktop sticky gallery column on phones: once the real image has
       scrolled above the header, a short fixed strip slides down so the product stays
       on screen while the info column is read, and it retracts again at the end of the
       PDP grid — the same point the desktop sticky column releases. */
    var miniStrip = document.querySelector('[data-pdp-ministrip]');
    var miniImg = miniStrip && miniStrip.querySelector('.tl-pdp__ministrip-img');
    var miniDots = miniStrip
      ? Array.prototype.slice.call(miniStrip.querySelectorAll('.tl-pdp__ministrip-dot'))
      : [];

    function syncMiniStrip() {
      if (!miniStrip || !miniImg) return;
      var allThumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-thumb]'));
      var active = gallery.querySelector('[data-pdp-thumb].is-active');
      if (active) {
        if (active.dataset.src) miniImg.src = active.dataset.src;
        if (active.dataset.srcset) miniImg.srcset = active.dataset.srcset;
        else miniImg.removeAttribute('srcset');
        if (active.dataset.alt) miniImg.alt = active.dataset.alt;
      }
      if (!miniDots.length) return;
      var activeIx = allThumbs.indexOf(active);
      miniDots.forEach(function (dot, i) {
        var thumb = allThumbs[i];
        // Honour the per-colour gallery swap: dots for filtered-out media stay hidden.
        dot.hidden = !!thumb && (thumb.style.display === 'none' || thumb.hidden);
        dot.classList.toggle('is-active', i === activeIx);
      });
    }

    if (miniStrip && miniImg && mainWrap) {
      var pdpSection = gallery.closest('.tl-pdp') || gallery.parentElement;
      var miniMq = window.matchMedia('(max-width: 900px)');
      var miniTicking = false;

      // JS owns visibility from here — without it the strip stays `hidden` (no dead bar).
      miniStrip.hidden = false;
      syncMiniStrip();

      var updateMiniStrip = function () {
        if (!miniMq.matches) {
          miniStrip.classList.remove('is-active');
          return;
        }
        var headerH = tlHeaderGroupHeight();
        var imgRect = mainWrap.getBoundingClientRect();
        var sectionRect = pdpSection.getBoundingClientRect();
        var scrolledPastImage = imgRect.bottom <= headerH + 4;
        var stillInProductGrid = sectionRect.bottom > headerH + miniStrip.offsetHeight;
        miniStrip.classList.toggle('is-active', scrolledPastImage && stillInProductGrid);
      };

      var onMiniScroll = function () {
        if (miniTicking) return;
        miniTicking = true;
        requestAnimationFrame(function () {
          miniTicking = false;
          updateMiniStrip();
        });
      };

      window.addEventListener('scroll', onMiniScroll, { passive: true });
      window.addEventListener('resize', onMiniScroll, { passive: true });
      updateMiniStrip();

      // Tap the strip to jump back to the full gallery.
      miniStrip.addEventListener('click', function (e) {
        if (!e.target.closest('[data-pdp-ministrip-top]')) return;
        var target =
          window.pageYOffset + mainWrap.getBoundingClientRect().top - tlHeaderGroupHeight() - 8;
        window.scrollTo({
          top: Math.max(0, target),
          behavior: toylandPrefersReducedMotion() ? 'auto' : 'smooth'
        });
      });

      // Swipe the strip to change image, same as the hero.
      if (thumbsList().length > 1) {
        var miniStartX = null;
        var miniStartY = null;
        miniStrip.addEventListener(
          'touchstart',
          function (e) {
            if (e.touches.length !== 1) return;
            miniStartX = e.touches[0].clientX;
            miniStartY = e.touches[0].clientY;
          },
          { passive: true }
        );
        miniStrip.addEventListener(
          'touchend',
          function (e) {
            if (miniStartX === null || miniStartY === null) return;
            var t = e.changedTouches[0];
            var dx = t ? t.clientX - miniStartX : 0;
            var dy = t ? t.clientY - miniStartY : 0;
            miniStartX = null;
            miniStartY = null;
            if (Math.abs(dx) < 44 || Math.abs(dx) < Math.abs(dy) * 1.12) return;
            activateThumbByDelta(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }
    }

    // ─── Mobile fullscreen viewer (lightbox) ─────────────────────────────────
    /* Phones have no hover, so the desktop hover-zoom below never applies to them —
       tapping the hero opens this instead: the photo full-bleed on black, with prev/next,
       pinch + double-tap zoom, drag-to-pan while zoomed, and swipe to change image.

       It does NOT keep its own idea of "current image". Navigation goes through
       activateThumb(), the same call the hero, arrows and thumbs use, so the main gallery
       and the sticky mini-strip are already on the right photo when the viewer closes.

       Built in JS rather than Liquid because it is one <img> reusing the thumb dataset
       (exactly how the hero swaps), so there is no per-media markup to render server-side. */
    if (mainWrap && img && thumbsList().length > 0 &&
        mainWrap.getAttribute('data-pdp-lightbox-bound') !== 'true') {
      mainWrap.setAttribute('data-pdp-lightbox-bound', 'true');

      var lbMq = window.matchMedia('(max-width: 899px)');
      var lbRoot = null, lbImg = null, lbCounter = null, lbStage = null;
      var lbScale = 1, lbTx = 0, lbTy = 0;
      var LB_MAX = 4, LB_DBL = 2.5;
      var lbScrollY = 0;

      function lbBuild() {
        if (lbRoot) return;
        lbRoot = document.createElement('div');
        lbRoot.className = 'tl-pdp-lb';
        lbRoot.setAttribute('data-pdp-lightbox', '');
        lbRoot.hidden = true;
        lbRoot.setAttribute('role', 'dialog');
        lbRoot.setAttribute('aria-modal', 'true');
        lbRoot.setAttribute('aria-label', 'Product images');
        lbRoot.innerHTML =
          '<div class="tl-pdp-lb__bar">' +
            '<button type="button" class="tl-pdp-lb__close" data-pdp-lb-close aria-label="Close image viewer">' +
              '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
            '</button>' +
            '<span class="tl-pdp-lb__counter" data-pdp-lb-counter aria-live="polite"></span>' +
          '</div>' +
          '<div class="tl-pdp-lb__stage" data-pdp-lb-stage>' +
            '<img class="tl-pdp-lb__img" data-pdp-lb-img alt="" sizes="100vw" draggable="false">' +
          '</div>' +
          '<button type="button" class="tl-pdp-lb__nav tl-pdp-lb__nav--prev" data-pdp-lb-prev aria-label="Previous image">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>' +
          '</button>' +
          '<button type="button" class="tl-pdp-lb__nav tl-pdp-lb__nav--next" data-pdp-lb-next aria-label="Next image">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>' +
          '</button>';
        document.body.appendChild(lbRoot);
        lbImg = lbRoot.querySelector('[data-pdp-lb-img]');
        lbCounter = lbRoot.querySelector('[data-pdp-lb-counter]');
        lbStage = lbRoot.querySelector('[data-pdp-lb-stage]');
        lbBind();
      }

      function lbApplyTransform(animate) {
        if (!lbImg) return;
        lbImg.style.transition = animate && !toylandPrefersReducedMotion()
          ? 'transform 0.22s cubic-bezier(.25,.8,.25,1)'
          : 'none';
        lbImg.style.transform =
          'translate(' + lbTx + 'px,' + lbTy + 'px) scale(' + lbScale + ')';
        lbRoot.classList.toggle('is-zoomed', lbScale > 1.01);
      }

      function lbResetTransform(animate) {
        lbScale = 1; lbTx = 0; lbTy = 0;
        lbApplyTransform(animate);
      }

      /* Keep the zoomed image from being dragged off-screen: the pan offset can never
         exceed the overhang the scale created on each axis. */
      function lbClampPan() {
        if (!lbImg) return;
        var r = lbImg.getBoundingClientRect();
        var overX = Math.max(0, (r.width - window.innerWidth) / 2);
        var overY = Math.max(0, (r.height - window.innerHeight) / 2);
        lbTx = Math.max(-overX, Math.min(overX, lbTx));
        lbTy = Math.max(-overY, Math.min(overY, lbTy));
      }

      function lbRender(index) {
        var thumbs = thumbsList();
        if (!thumbs.length) return;
        var i = ((index % thumbs.length) + thumbs.length) % thumbs.length;
        var t = thumbs[i];
        if (!t) return;
        // Single source of truth: this also moves the hero + mini-strip.
        activateThumb(t);
        if (t.dataset.src) lbImg.src = t.dataset.src;
        if (t.dataset.srcset) lbImg.srcset = t.dataset.srcset;
        else lbImg.removeAttribute('srcset');
        lbImg.alt = t.dataset.alt || '';
        lbCounter.textContent = i + 1 + ' / ' + thumbs.length;
        lbRoot.classList.toggle('tl-pdp-lb--single', thumbs.length < 2);
        lbResetTransform(false);
      }

      function lbStep(delta) {
        var i = activeThumbIndex();
        lbRender((i < 0 ? 0 : i) + delta);
      }

      function lbIsOpen() {
        return !!(lbRoot && lbRoot.classList.contains('is-open'));
      }

      function lbOpen() {
        lbBuild();
        lbRender(activeThumbIndex());
        lbScrollY = window.pageYOffset;
        document.body.style.overflow = 'hidden';
        // One class, applied synchronously — no attribute juggling, no transition to
        // get stranded in, so "open" is always literally visible.
        lbRoot.hidden = false;
        lbRoot.classList.add('is-open');
        var closeBtn = lbRoot.querySelector('[data-pdp-lb-close]');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }

      function lbClose() {
        if (!lbIsOpen()) return;
        lbRoot.classList.remove('is-open');
        lbRoot.hidden = true;
        document.body.style.overflow = '';
        window.scrollTo(0, lbScrollY);
      }

      function lbBind() {
        lbRoot.addEventListener('click', function (e) {
          if (e.target.closest('[data-pdp-lb-close]')) { lbClose(); return; }
          if (e.target.closest('[data-pdp-lb-prev]')) { lbStep(-1); return; }
          if (e.target.closest('[data-pdp-lb-next]')) { lbStep(1); return; }
          // Tap on the backdrop (not the photo) closes, as in every native viewer.
          if (e.target === lbStage || e.target === lbRoot) lbClose();
        });

        document.addEventListener('keydown', function (e) {
          if (!lbIsOpen()) return;
          if (e.key === 'Escape') { e.preventDefault(); lbClose(); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); lbStep(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); lbStep(1); }
        });

        /* ── Gestures ──
           1 finger, not zoomed  → horizontal swipe changes image, vertical drag closes
           1 finger, zoomed      → pan
           2 fingers             → pinch zoom about the midpoint
           double tap            → toggle between fit and LB_DBL                        */
        var pStartX = 0, pStartY = 0, pStartTx = 0, pStartTy = 0;
        var pinchDist = 0, pinchScale = 1;
        var moved = false, lastTap = 0, tapTimer = null;

        function dist(t1, t2) {
          var dx = t1.clientX - t2.clientX, dy = t1.clientY - t2.clientY;
          return Math.sqrt(dx * dx + dy * dy);
        }

        lbStage.addEventListener('touchstart', function (e) {
          if (e.touches.length === 2) {
            pinchDist = dist(e.touches[0], e.touches[1]);
            pinchScale = lbScale;
            moved = true;
            return;
          }
          if (e.touches.length !== 1) return;
          pStartX = e.touches[0].clientX;
          pStartY = e.touches[0].clientY;
          pStartTx = lbTx; pStartTy = lbTy;
          moved = false;
        }, { passive: true });

        lbStage.addEventListener('touchmove', function (e) {
          if (e.touches.length === 2 && pinchDist > 0) {
            e.preventDefault();
            var ratio = dist(e.touches[0], e.touches[1]) / pinchDist;
            lbScale = Math.max(1, Math.min(LB_MAX, pinchScale * ratio));
            if (lbScale <= 1.01) { lbTx = 0; lbTy = 0; }
            lbClampPan();
            lbApplyTransform(false);
            moved = true;
            return;
          }
          if (e.touches.length !== 1) return;
          var dx = e.touches[0].clientX - pStartX;
          var dy = e.touches[0].clientY - pStartY;
          if (Math.abs(dx) > 8 || Math.abs(dy) > 8) moved = true;
          if (lbScale > 1.01) {
            // Panning owns the gesture while zoomed — stop the page/viewer reacting.
            e.preventDefault();
            lbTx = pStartTx + dx;
            lbTy = pStartTy + dy;
            lbClampPan();
            lbApplyTransform(false);
          }
        }, { passive: false });

        lbStage.addEventListener('touchend', function (e) {
          if (pinchDist > 0 && e.touches.length === 0) {
            pinchDist = 0;
            if (lbScale <= 1.01) lbResetTransform(true);
            return;
          }
          var t = e.changedTouches[0];
          if (!t) return;
          var dx = t.clientX - pStartX;
          var dy = t.clientY - pStartY;

          if (!moved) {
            // Tap: second one within 300ms toggles zoom, a lone one does nothing (so a
            // stray tap never closes the viewer mid-browse).
            var now = Date.now();
            if (now - lastTap < 300) {
              window.clearTimeout(tapTimer);
              lastTap = 0;
              if (lbScale > 1.01) lbResetTransform(true);
              else {
                lbScale = LB_DBL;
                // Zoom toward the tapped point rather than the centre.
                lbTx = (window.innerWidth / 2 - t.clientX) * (LB_DBL - 1);
                lbTy = (window.innerHeight / 2 - t.clientY) * (LB_DBL - 1);
                lbClampPan();
                lbApplyTransform(true);
              }
            } else {
              lastTap = now;
            }
            return;
          }

          if (lbScale > 1.01) return; // pan finished — never navigate while zoomed
          if (Math.abs(dx) >= 44 && Math.abs(dx) > Math.abs(dy) * 1.12) {
            lbStep(dx < 0 ? 1 : -1);
          } else if (dy >= 90 && Math.abs(dy) > Math.abs(dx) * 1.4) {
            lbClose(); // swipe down to dismiss
          }
        }, { passive: true });

        // A desktop-width resize while open would leave a phone-only overlay stranded.
        lbMq.addEventListener
          ? lbMq.addEventListener('change', function (ev) { if (!ev.matches) lbClose(); })
          : lbMq.addListener(function (ev) { if (!ev.matches) lbClose(); });
      }

      /* Open on tap of the hero. Chrome layered over the image (arrows, share, badges)
         keeps its own behaviour — only the photo itself opens the viewer.

         A swipe on the hero already changes the image, and browsers still emit a click
         after it, which would pop the viewer open on every swipe. So track movement and
         treat anything past the threshold as "not a tap". */
      var heroTapX = null, heroTapY = null, heroWasSwipe = false;
      mainWrap.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { heroWasSwipe = true; return; }
        heroTapX = e.touches[0].clientX;
        heroTapY = e.touches[0].clientY;
        heroWasSwipe = false;
      }, { passive: true });
      mainWrap.addEventListener('touchmove', function (e) {
        if (heroTapX === null || !e.touches.length) return;
        if (Math.abs(e.touches[0].clientX - heroTapX) > 10 ||
            Math.abs(e.touches[0].clientY - heroTapY) > 10) heroWasSwipe = true;
      }, { passive: true });

      /* Delegated on `document` rather than bound to the image wrapper. The hero has
         several stacked children (zoom-inner, the badge row, the arrow "hit" strips that
         deliberately capture pointer events), and binding to one of them makes opening
         depend on exactly which layer the finger landed on. Asking "did this click happen
         inside the hero?" is the question we actually mean. */
      document.addEventListener('click', function (e) {
        if (!lbMq.matches || lbIsOpen()) return;
        var t = e.target;
        if (!t || !t.closest) return;
        if (!t.closest('[data-pdp-main-img]')) return;
        if (heroWasSwipe) { heroWasSwipe = false; return; }
        // Gallery chrome keeps its own behaviour — only the photo opens the viewer.
        if (t.closest('[data-pdp-gallery-prev], [data-pdp-gallery-next]')) return;
        if (t.closest('[data-pdp-gallery-share], .tl-pdp__share-root')) return;
        if (t.closest('a, button')) return;
        e.preventDefault();
        lbOpen();
      });
    }

    if (!mainWrap || !img || !zoomInner) return;
    if (mainWrap.getAttribute('data-pdp-zoom-bound') === 'true') return;
    mainWrap.setAttribute('data-pdp-zoom-bound', 'true');

    var hoverFine = window.matchMedia('(hover: hover) and (pointer: fine)');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!hoverFine.matches || reduceMotion.matches) return;

    var zoomLevel = 2.25;
    function onMove(e) {
      /* Ignore hover when pointer is over gallery chrome (siblings above zoom-inner). */
      if (!zoomInner.contains(e.target)) return;
      var r = zoomInner.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      img.style.transformOrigin = x + '% ' + y + '%';
      img.style.transform = 'scale(' + zoomLevel + ')';
      mainWrap.classList.add('is-zooming');
    }
    function onLeave() {
      img.style.transform = '';
      img.style.transformOrigin = '';
      mainWrap.classList.remove('is-zooming');
    }
    zoomInner.addEventListener('mousemove', onMove);
    zoomInner.addEventListener('mouseleave', onLeave);
  })();

  // Pincode checker: toyland-scripts.js (toyland-pincode snippet on PDP + pincode page)

  // ─── Collection page view-mode toggle (grid/list) ─────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-toyland-viewmode]');
    if (!btn) return;
    var mode = btn.dataset.toylandViewmode;
    var root = document.querySelector('[data-toyland-coll-grid]');
    if (!root) return;
    root.dataset.viewMode = mode;
    document.querySelectorAll('[data-toyland-viewmode]').forEach(function (b) {
      b.classList.toggle('is-active', b === btn);
    });
  });

  // ─── Custom sort dropdown (collection page) ───────────────────────────────
  function closeAllSortMenus(except) {
    document.querySelectorAll('[data-toyland-sort]').forEach(function (form) {
      if (form === except) return;
      var menu = form.querySelector('[data-toyland-sort-menu]');
      var trigger = form.querySelector('[data-toyland-sort-trigger]');
      if (menu) menu.setAttribute('hidden', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-toyland-sort-trigger]');
    var option  = e.target.closest('[data-toyland-sort] [data-value]');
    var insideMenu = e.target.closest('[data-toyland-sort]');

    // Toggle menu on trigger click
    if (trigger) {
      var form = trigger.closest('[data-toyland-sort]');
      var menu = form.querySelector('[data-toyland-sort-menu]');
      var isOpen = !menu.hasAttribute('hidden');
      closeAllSortMenus(isOpen ? null : form);
      if (isOpen) {
        menu.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        menu.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // Option click → set hidden input + navigate. We build the URL directly
    // from the form's hidden inputs (instead of relying on form.submit()) so
    // bracketed names like `options[prefix]` and search-page quirks always work.
    if (option) {
      var form2 = option.closest('[data-toyland-sort]');
      var input = form2.querySelector('[data-toyland-sort-input]');
      var valueEl = form2.querySelector('[data-toyland-sort-value]');
      var newSort = option.dataset.value;
      if (input) input.value = newSort;
      if (valueEl) valueEl.textContent = option.dataset.label;

      // Visually close the menu before nav for snappier feel
      var menuEl = form2.querySelector('[data-toyland-sort-menu]');
      var trigEl = form2.querySelector('[data-toyland-sort-trigger]');
      if (menuEl) menuEl.setAttribute('hidden', '');
      if (trigEl) trigEl.setAttribute('aria-expanded', 'false');

      try {
        var params = new URLSearchParams();
        form2.querySelectorAll('input[name]').forEach(function (el) {
          if (el.name && el.value !== undefined) params.set(el.name, el.value);
        });
        params.set('sort_by', newSort);
        params.delete('page'); // always reset to page 1 on sort change
        var base = form2.getAttribute('action') || window.location.pathname;
        var qs = params.toString();
        window.location.href = qs ? base + '?' + qs : base;
      } catch (err) {
        // Fallback: native submit if URLSearchParams is unavailable for any reason
        form2.submit();
      }
      return;
    }

    // Click outside → close
    if (!insideMenu) closeAllSortMenus();
  });

  // ─── Contact page topic dropdown ───────────────────────────────────────────
  function closeContactTopic(except) {
    document.querySelectorAll('[data-tl-contact-topic]').forEach(function (root) {
      if (root === except) return;
      var menu = root.querySelector('[data-tl-contact-topic-menu]');
      var trigger = root.querySelector('[data-tl-contact-topic-trigger]');
      if (menu) menu.setAttribute('hidden', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var topicTrigger = e.target.closest('[data-tl-contact-topic-trigger]');
    var topicOption = e.target.closest('[data-tl-contact-topic-option]');
    var topicRoot = e.target.closest('[data-tl-contact-topic]');

    if (topicTrigger) {
      var root = topicTrigger.closest('[data-tl-contact-topic]');
      var menu = root && root.querySelector('[data-tl-contact-topic-menu]');
      if (!menu) return;
      var isOpen = !menu.hasAttribute('hidden');
      closeContactTopic(isOpen ? null : root);
      if (isOpen) {
        menu.setAttribute('hidden', '');
        topicTrigger.setAttribute('aria-expanded', 'false');
      } else {
        menu.removeAttribute('hidden');
        topicTrigger.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (topicOption) {
      var root2 = topicOption.closest('[data-tl-contact-topic]');
      var input = root2.querySelector('[data-tl-contact-topic-input]');
      var valueEl = root2.querySelector('[data-tl-contact-topic-value]');
      var menu2 = root2.querySelector('[data-tl-contact-topic-menu]');
      var trigger2 = root2.querySelector('[data-tl-contact-topic-trigger]');
      var value = topicOption.dataset.value || topicOption.textContent.trim();

      if (input) input.value = value;
      if (valueEl) valueEl.textContent = value;

      root2.querySelectorAll('[data-tl-contact-topic-option]').forEach(function (optionEl) {
        var selected = optionEl === topicOption;
        optionEl.classList.toggle('is-active', selected);
        optionEl.setAttribute('aria-selected', selected ? 'true' : 'false');
        optionEl.querySelectorAll('svg').forEach(function (svg) { svg.remove(); });
      });

      topicOption.insertAdjacentHTML(
        'beforeend',
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>'
      );
      if (menu2) menu2.setAttribute('hidden', '');
      if (trigger2) trigger2.setAttribute('aria-expanded', 'false');
      return;
    }

    if (!topicRoot) closeContactTopic();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    closeAllSortMenus();
  });

  // ─── Money formatter ───────────────────────────────────────────────────────
  function formatMoney(cents) {
    // Whole-rupee display \u2014 never render trailing decimals (.00). Force the money
    // format's amount placeholder to its no-decimals variant before formatting.
    var fmt = (window.theme && theme.moneyFormat) || '&#8377;{{amount_no_decimals}}';
    fmt = fmt
      .replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, '{{amount_no_decimals_with_comma_separator}}');
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, fmt);
    }
    return '\u20B9' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  // ─── Frequently bought together (PDP) — live total + dynamic CTA label ────
  function initToylandFBT() {
    var container = document.getElementById('tl-fbt');
    if (!container) return;

    var totalEl = document.getElementById('tl-fbt-total');
    var addAllBtn = document.getElementById('tl-fbt-add-all');
    var optionalChecks = container.querySelectorAll('[data-fbt-check]');
    var currentRow = container.querySelector('.tl-fbt__item--current');

    function currentLineCents() {
      if (!currentRow) return 0;
      return parseInt(currentRow.getAttribute('data-price'), 10) || 0;
    }

    function updateFBTFromPdp() {
      var form = document.querySelector('[data-toyland-pdp-form]');
      if (!form || !currentRow) {
        updateFBTUI();
        return;
      }
      var p = parseInt(form.getAttribute('data-initial-price'), 10) || 0;
      var idInput = form.querySelector('input[name="id"]');
      if (p) {
        currentRow.setAttribute('data-price', String(p));
        var lbl = currentRow.querySelector('.tl-fbt__item-price');
        if (lbl) lbl.textContent = formatMoney(p);
      }
      if (idInput && idInput.value) {
        currentRow.setAttribute('data-variant-id', idInput.value);
      }
      updateFBTUI();
    }

    function selectedLineItems() {
      var out = [];
      var form = document.querySelector('[data-toyland-pdp-form]');
      if (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value) {
          out.push({ id: parseInt(idInput.value, 10), quantity: 1 });
        }
      } else if (currentRow && currentRow.getAttribute('data-variant-id')) {
        out.push({ id: parseInt(currentRow.getAttribute('data-variant-id'), 10), quantity: 1 });
      }
      optionalChecks.forEach(function (cb) {
        if (!cb.checked) return;
        var item = cb.closest('.tl-fbt__item');
        if (!item || !item.getAttribute('data-variant-id')) return;
        out.push({ id: parseInt(item.getAttribute('data-variant-id'), 10), quantity: 1 });
      });
      return out;
    }

    function updateFBTUI() {
      var totalCents = currentLineCents();
      optionalChecks.forEach(function (cb) {
        if (!cb.checked) return;
        var item = cb.closest('.tl-fbt__item');
        if (!item) return;
        totalCents += parseInt(item.getAttribute('data-price'), 10) || 0;
      });
      if (totalEl) totalEl.textContent = formatMoney(totalCents);

      var count = 1;
      optionalChecks.forEach(function (cb) {
        if (cb.checked) count += 1;
      });
      if (addAllBtn) {
        var label =
          count === 1
            ? 'Add 1 item to cart'
            : 'Add ' + count + ' items to cart';
        if (addAllBtn.dataset.busy !== 'true') {
          addAllBtn.textContent = label;
        }
        addAllBtn.setAttribute('aria-label', label + '. Total ' + formatMoney(totalCents));
      }
    }

    optionalChecks.forEach(function (cb) {
      cb.addEventListener('change', updateFBTUI);
    });
    document.addEventListener('toyland:pdp-price-sync', updateFBTFromPdp);

    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        var items = selectedLineItems();
        if (!items.length) return;

        var prevLabel = addAllBtn.textContent;
        addAllBtn.dataset.busy = 'true';
        addAllBtn.disabled = true;
        addAllBtn.textContent = 'Adding…';

        var chain = Promise.resolve();
        items.forEach(function (item) {
          chain = chain.then(function () {
            return ajaxAdd(item.id, item.quantity || 1);
          });
        });

        chain
          .then(function () {
            return refreshCartUI();
          })
          .then(function (cart) {
            addAllBtn.textContent = '\u2713 Added!';
            document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: cart }));
            setTimeout(function () {
              addAllBtn.dataset.busy = 'false';
              addAllBtn.disabled = false;
              updateFBTUI();
              openDawnCartDrawer();
            }, 900);
          })
          .catch(function (err) {
            // Same rule as every other add path: never fail silently.
            tlReportAddFailure(err);
            /* The lines are added one at a time, so a failure partway through leaves
               some of them in the cart. Repaint from the server rather than leaving the
               drawer and the count describing a cart that no longer exists. */
            refreshCartUI().catch(function () {});
            addAllBtn.dataset.busy = 'false';
            addAllBtn.disabled = false;
            addAllBtn.textContent = prevLabel;
          });
      });
    }

    updateFBTFromPdp();
  }

  // ─── TL Cart AJAX helpers ──────────────────────────────────────────────────
  function ajaxUpdate(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok || (data && data.status)) {
          var msg = (data && (data.description || data.message)) || 'Cart update failed';
          throw new Error(msg);
        }
        return data;
      });
    });
  }

  /* Cart page line remove: handled by Dawn cart.js (no capture listener here).
   * Previous code used stopImmediatePropagation + replaceWith(#main-cart-footer),
   * which could drop the order summary. Drawer sync: PUB_SUB_EVENTS.cartUpdate → tlRenderCart. */

  function escHtml(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Repaint of snippets/toyland-shipping-progress after a cart write. The threshold and
     both strings are read off the server-rendered element's data-attributes, never
     restated here — Theme settings → Rynoka — Delivery is the single source of truth,
     and a copy of the number in this asset is exactly how the two drifted apart before.
     Falls back to 49900 paise only if the markup predates those attributes. */
  var TL_SHIP_TICK =
    '<svg class="tl-ship-prog__tick" width="15" height="15" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

  function tlUpdateShipProgress(drawerEl, cart) {
    var progWrap = drawerEl.querySelector('[data-tl-progress-wrap]');
    if (!progWrap) return;

    var root = progWrap.querySelector('[data-tl-ship-prog], .tl-ship-prog');
    var fill = progWrap.querySelector('.tl-ship-prog__fill, [data-tl-prog-fill]');
    var msg = progWrap.querySelector('.tl-ship-prog__msg, [data-tl-prog-msg]');

    var target = parseInt(root && root.getAttribute('data-tl-threshold'), 10);
    if (!(target > 0)) target = 49900;

    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var remaining = target - total;
    var unlocked = remaining <= 0;
    var pct = unlocked ? 100 : Math.max(0, Math.min(100, Math.round((total / target) * 100)));

    if (fill) fill.style.width = pct + '%';

    var track = progWrap.querySelector('.tl-ship-prog__track');
    if (track) track.setAttribute('aria-valuenow', String(pct));
    if (root) root.classList.toggle('is-unlocked', unlocked);

    if (msg) {
      var lockedCopy = (root && root.getAttribute('data-tl-copy-locked')) || 'Add [remaining] more for free delivery';
      var unlockedCopy = (root && root.getAttribute('data-tl-copy-unlocked')) || 'Free delivery unlocked';

      /* getAttribute already returns the decoded string, so escape it again before it
         goes back in as innerHTML — otherwise an apostrophe-free but tag-carrying admin
         string would execute on the second repaint even though it rendered inert once. */
      /* formatMoney() output is NOT escaped on purpose: shop.money_format may carry the
         rupee sign as the entity &#8377;, and escaping it would print "&#8377;299"
         verbatim. It is theme-controlled, so it is safe as innerHTML either way. */
      if (unlocked) {
        msg.innerHTML = TL_SHIP_TICK + escHtml(unlockedCopy);
      } else {
        msg.innerHTML = escHtml(lockedCopy).replace(
          '[remaining]',
          '<strong>' + formatMoney(remaining) + '</strong>'
        );
      }
      msg.classList.toggle('tl-ship-prog__msg--success', unlocked);
    }

    var ratio = progWrap.querySelector('[data-tl-prog-ratio]');
    var showRatio = !root || root.getAttribute('data-tl-show-ratio') !== '0';
    if (ratio) {
      ratio.hidden = unlocked || !showRatio;
      if (!ratio.hidden) ratio.innerHTML = formatMoney(total) + ' / ' + formatMoney(target);
    } else if (!unlocked && showRatio) {
      /* First paint was an unlocked cart, so Liquid never emitted the ratio node.
         Create it once so a cart that drops back under the threshold still shows one. */
      var top = progWrap.querySelector('.tl-ship-prog__top');
      if (top) {
        ratio = document.createElement('span');
        ratio.className = 'tl-ship-prog__ratio';
        ratio.setAttribute('data-tl-prog-ratio', '');
        ratio.innerHTML = formatMoney(total) + ' / ' + formatMoney(target);
        top.appendChild(ratio);
      }
    }
  }

  /* Discount-tag icon for cart-level discount rows — same path as the one in
     snippets/cart-drawer.liquid, kept as a constant so the string build stays readable. */
  var TL_DISCOUNT_TAG =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>' +
    '<line x1="7" y1="7" x2="7.01" y2="7"/></svg>';

  /* Cart writes are not instant, and tlEnsureCartFooter() rebuilds this footer's
     innerHTML wholesale — so between the stepper click and the reply the summary was
     showing figures it already knew were stale, then hard-swapping them. Marking the
     footer busy holds the OLD numbers on screen, dimmed, until the new ones are ready:
     the shopper reads "updating", not a flicker.

     Cleared in tlEnsureCartFooter() at the moment the new markup lands, so the two can
     never disagree. The class sits on the footer element, which survives the innerHTML
     swap, rather than on anything inside it. */
  function tlSetFooterBusy(busy) {
    var foot = document.querySelector('cart-drawer [data-tl-cart-footer]');
    if (foot) foot.classList.toggle('is-busy', !!busy);
  }

  /* One authoritative re-read when a cart write comes back with a discount the summary
     cannot name — see the defensive block in tlEnsureCartFooter(). /cart/change.js has
     been seen replying mid-recalculation, with the discount priced into total_price but
     its application entry not yet carrying a title or an amount. Rendering that is
     honest but generic ("Discount −₹50"), and it would then sit there disagreeing with
     what the same cart shows on a page load ("WELCOME50 −₹50"), so /cart.js is read once
     to settle it.

     The flag is cleared only AFTER the re-render, so the nested tlEnsureCartFooter()
     call cannot start another round: at most one extra request per cart write, and only
     when the numbers did not explain themselves. */
  var tlFooterReconciling = false;

  function tlReconcileCartFooter(drawerEl) {
    if (tlFooterReconciling) return;
    tlFooterReconciling = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (fresh) {
        if (fresh) tlEnsureCartFooter(drawerEl, fresh);
      })
      .catch(function () {})
      .then(function () {
        tlFooterReconciling = false;
      });
  }

  /* Payment trust row — string twin of snippets/toyland-payment-badges.liquid, which is
     what the cart page and the server-rendered drawer use. This copy is DRAWER-ONLY, so
     it hardcodes what the drawer's render call passes: no note paragraph (the
     .tl-cart-ssl line carries the security assurance) and the short method list
     (UPI / Cards / COD) that fits this ~340px column on one row. If the drawer's
     `methods:` argument in snippets/cart-drawer.liquid changes, change this too. */
  var TL_PAY_BADGES =
    '<div class="tl-cart-pay">' +
    '<ul class="tl-cart-pay__row" aria-label="Accepted payment methods">' +
    '<li class="tl-cart-pay__chip tl-cart-pay__chip--secure">' +
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="3" y="11" width="18" height="11" rx="2"/>' +
    '<path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
    ' Secure checkout</li>' +
    '<li class="tl-cart-pay__chip">UPI</li>' +
    '<li class="tl-cart-pay__chip">Cards</li>' +
    '<li class="tl-cart-pay__chip tl-cart-pay__chip--cod">COD</li>' +
    '</ul></div>';

  function tlEnsureCartFooter(drawerEl, cart) {
    var root = drawerEl.querySelector('#CartDrawer');
    if (!root) return;
    var foot = drawerEl.querySelector('[data-tl-cart-footer]');
    if (!foot) {
      foot = document.createElement('div');
      foot.className = 'tl-drawer__footer';
      foot.id = 'tl-cart-footer';
      foot.setAttribute('data-tl-cart-footer', '');
      root.appendChild(foot);
    }
    /* Mirrors the summary in snippets/cart-drawer.liquid — see the comment there.
       Total is cart.total_price, not items_subtotal_price: the latter is taken before
       cart-level discounts, so any bundle saving was invisible and the drawer quoted
       more than checkout would charge. Savings row only appears when there is one. */
    var sub = cart.original_total_price || cart.total_price || 0;
    var total = cart.total_price || 0;
    var n = cart.item_count;

    /* Discount split — twin of the same split in snippets/cart-drawer.liquid.
       cart.total_discount is EVERY discount added together, so an order discount and a
       bundle's line-level saving arrive as one number and have to be separated: the
       order-level ones get their own titled rows, the remainder is everything else.

       Everything below is written defensively because /cart/change.js has been seen
       returning a cart_level_discount_applications entry with NO title and a zero
       total_allocated_amount right after a quantity write. Rendered literally that
       produced two wrong rows at once: an empty "🏷 −₹0" row, and the order discount
       misfiled under "Bundle savings" because the zero contributed nothing to subtract.
       Both are prevented here rather than assumed away. */
    var cartItems = Array.isArray(cart.items) ? cart.items : [];

    /* Only entries that can actually be rendered as a row. An entry missing either half
       is not a discount anyone can read, and Shopify has already priced it into
       total_price regardless, so it is dropped from the list and left to the remainder
       below — where it still shows up in the arithmetic and the summary still adds up. */
    var cartDiscounts = (Array.isArray(cart.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : []
    )
      .map(function (d) {
        return {
          title: String((d && d.title) || '').trim(),
          amount: Math.max(0, (d && Number(d.total_allocated_amount)) || 0)
        };
      })
      .filter(function (d) {
        return d.title !== '' && d.amount > 0;
      });

    var cartLevelSaved = 0;
    cartDiscounts.forEach(function (d) {
      cartLevelSaved += d.amount;
    });

    /* Clamped at 0: if a discount app ever reports an allocation larger than
       total_discount, a negative here would print "−−₹40". */
    var saved = Math.max(0, (Number(cart.total_discount) || 0) - cartLevelSaved);

    /* "Bundle savings" is a CLAIM, so it needs positive evidence — a line actually
        flagged as a bundle. Without one the remainder is some discount we cannot name
        (a dropped title, an app discount, a code mid-recalculation), and calling that a
        bundle saving on a cart with no bundle in it is simply false. It is still shown,
        under a neutral label, so the rows continue to reconcile to the total. */
    var savedLabel = cartItems.some(function (it) {
      return tlCartLineIsBundle(it);
    })
      ? 'Bundle savings'
      : 'Discount';

    /* Delivery: free at or above the free-delivery threshold, otherwise deferred to
       checkout — the same rule as snippets/cart-drawer.liquid and the cart page
       (sections/main-cart-footer.liquid). The row used to be a hardcoded FREE, which
       promised free delivery on a ₹329 cart and then let checkout bill for it.
       The threshold comes from whichever node Liquid stamped it on — the summary this
       function is about to replace, or the drawer's progress bar — so Theme settings →
       Rynoka — Delivery stays the single source. */
    var thresholdEl =
      foot.querySelector('[data-tl-cart-summary][data-tl-threshold]') ||
      drawerEl.querySelector('[data-tl-ship-prog][data-tl-threshold]');
    var shipMin = parseInt(thresholdEl && thresholdEl.getAttribute('data-tl-threshold'), 10);
    if (!(shipMin > 0)) shipMin = 49900;
    var shipsFree = total >= shipMin;
    // COD trust strip temporarily hidden here and in snippets/cart-drawer.liquid.
    foot.innerHTML =
      '<div class="tl-cart-summary" data-tl-cart-summary data-tl-threshold="' + shipMin + '">' +
      '<div class="tl-cart-summary__row"><span>Subtotal (' +
      n +
      ' ' +
      (n === 1 ? 'item' : 'items') +
      ')</span><span>' +
      formatMoney(sub) +
      '</span></div>' +
      (saved > 0
        ? '<div class="tl-cart-summary__row tl-cart-summary__row--save">' +
          '<span>' + savedLabel + '</span><span>−' + formatMoney(saved) + '</span></div>'
        : '') +
      /* Titles are merchant-entered in Admin, so they go through escHtml. */
      cartDiscounts
        .map(function (d) {
          return (
            '<div class="tl-cart-summary__row tl-cart-summary__row--save tl-cart-summary__row--discount">' +
            '<span class="tl-cart-summary__disc">' +
            TL_DISCOUNT_TAG +
            escHtml(d.title) +
            '</span><span>−' +
            formatMoney(d.amount) +
            '</span></div>'
          );
        })
        .join('') +
      '<div class="tl-cart-summary__row"><span>Delivery</span>' +
      (shipsFree
        ? '<span class="tl-cart-summary__free">FREE</span>'
        : '<span class="tl-cart-summary__muted">Calculated at checkout</span>') +
      '</div>' +
      '<div class="tl-cart-summary__row tl-cart-summary__row--total"><span>Total</span><span>' +
      formatMoney(total) +
      '</span></div>' +
      '</div>' +
      TL_PAY_BADGES +
      '<a href="/checkout" class="tl-btn tl-btn-primary tl-btn-block tl-cart-checkout">' +
      'Proceed to Checkout ' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>' +
      '</a>' +
      '<div class="tl-cart-ssl">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>' +
      ' 256-bit SSL encrypted checkout' +
      '</div>';

    /* New figures are on screen — drop the dimmed hold. */
    foot.classList.remove('is-busy');

    /* A discount that could not be attributed to a named application or a bundle line.
       The figures on screen already reconcile, so this is a titling fix, not a
       correctness one — hence no busy state around it. */
    if (saved > 0 && savedLabel === 'Discount') {
      tlReconcileCartFooter(drawerEl);
    }
  }

  /* Is this /cart.js line part of a bundle deal? JS twin of
     snippets/toyland-cart-bundle-flag.liquid — keep the two in step; the reasoning and
     the evidence behind these tests are written up there.

     Short version: the marker is the `__kaching_bundles` line property that Kaching
     stamps via its cart interceptor, NOT a discount. Kaching prices deals with a
     Shopify Function that does not touch the online-store cart, so a bundled line
     comes back with total_discount 0 — keying off the discount matched nothing. */
  var TL_BUNDLE_RE = /bundle|kaching/i;

  function tlCartLineIsBundle(item) {
    if (!item) return false;

    /* Shopify's own flag: this line was built out of other lines (a cart-transform
       merge or expand), which is a bundle whatever app did it. The property test below
       still carries the deals that stay as separate lines. */
    if (item.has_components === true) return true;

    var props = item.properties;
    if (props) {
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && TL_BUNDLE_RE.test(k)) return true;
      }
    }

    // Fallback for the day the discount function does start pricing the cart, and for
    // any other bundle app that discounts the line rather than tagging it.
    var allocs = item.line_level_discount_allocations || item.discounts || [];
    for (var i = 0; i < allocs.length; i++) {
      var a = allocs[i] || {};
      var title = (a.discount_application && a.discount_application.title) || a.title || '';
      if (TL_BUNDLE_RE.test(title)) return true;
    }

    return false;
  }

  /* ── The products inside a bundled line ───────────────────────────────────────
     Kaching's Mix & Match deals reach the cart as ONE line: the app adds the chosen
     variants tagged with `__kaching_bundles`, and its Cart Transform merges them into
     a line whose merchandise is an unlisted container product ("Mix & Match — Any 2
     Panels", ₹0, requiresComponents). The row therefore names the deal and shows the
     container's image, and says nothing about what the shopper actually picked.

     Liquid can see through it — `line_item.item_components` — and both the cart page
     and the drawer's server render use it via snippets/toyland-cart-bundle-items.
     /cart.js cannot: it reports `has_components: true` and stops there, and Shopify
     has never added the components to the Ajax cart. Since this drawer repaints
     itself from /cart.js after every write, the contents would vanish at exactly the
     moment they matter most — the repaint right after the bundle is added.

     So the components are fetched as JSON from sections/toyland-cart-bundle-data
     through the Section Rendering API, which renders it against the current cart.
     Requested only when the cart holds a bundle-marked line — never on an ordinary
     cart — and then at most once per line key, whatever comes back. Line keys are
     safe to cache against: a key names one immutable selection, so a different
     selection is a different line rather than a stale entry.

     Keep this markup in step with snippets/toyland-cart-bundle-items.liquid: the same
     row must not change shape when the drawer repaints. */
  var tlBundleKids = Object.create(null); // cart line key → array of components
  var tlBundleMrp = Object.create(null); // cart line key → MRP "was" ÷ original_line_price
  var tlBundleKidsAsked = Object.create(null); // line keys a fetch has already covered
  var tlBundleKidsFetch = null; // in-flight request, shared rather than duplicated

  /* Cart line key → the landing page that line's product asked to be clicked through to
     (`custom.combo_redirect_map`, resolved for that line's own SKU). /cart.js carries
     no metafields, so a repaint has no way to resolve one for itself; instead Liquid
     stamps [data-tl-redirect] on the rows it renders and tlRenderCart harvests them
     off the DOM it is about to replace.
     Keyed on the line key for the same reason the components are: a key names one
     immutable selection, so an entry can never describe a different line later. */
  var tlLineRedirects = Object.create(null);

  function tlHarvestLineRedirects(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-tl-item][data-tl-redirect]').forEach(function (row) {
      var key = row.getAttribute('data-key');
      var url = row.getAttribute('data-tl-redirect');
      if (key && url) tlLineRedirects[key] = url;
    });
  }

  /* Point an already-drawn row at a destination that only arrived after it was drawn.
     tlRenderCart builds from /cart.js, which carries no metafields, so on a FIRST add
     the only href it has is item.url — Kaching's ₹0 container on a merged bundle. The
     section fetch that follows brings the real one; this writes it onto the row in
     place rather than forcing a second full repaint. Idempotent: no-ops once the row
     already carries the url, which is what makes it safe to call on every paint. */
  function tlApplyLineRedirect(row, key) {
    var url = key && tlLineRedirects[key];
    if (!row || !url || row.getAttribute('data-tl-redirect') === url) return;
    row.setAttribute('data-tl-redirect', url);
    ['.tl-cart-item__img-wrap', '.tl-cart-item__name'].forEach(function (sel) {
      var a = row.querySelector(sel);
      if (a && a.tagName === 'A') a.setAttribute('href', url);
    });
  }

  function tlFetchBundleKids() {
    if (tlBundleKidsFetch) return tlBundleKidsFetch;
    tlBundleKidsFetch = fetch('/?section_id=toyland-cart-bundle-data', {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        /* The Section Rendering API wraps the section in <div id="shopify-section-…">,
           so the JSON is the wrapper's text, not the response body. */
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var data = JSON.parse((doc.body.textContent || '').trim() || '{}');
        Object.keys(data).forEach(function (key) {
          var entry = data[key];
          /* Older shape was a bare array of components. Tolerated so a cached copy of
             the previous section cannot blank every bundle's contents mid-deploy. */
          if (Array.isArray(entry)) {
            tlBundleKids[key] = entry;
            return;
          }
          tlBundleKids[key] = entry.kids || [];
          if (entry.mrp > 1) tlBundleMrp[key] = entry.mrp;
          /* The line's own destination, which the DOM could not supply on a first add.
             Folded into the same map the harvest fills, so everything downstream —
             including the [data-tl-redirect] re-stamp in tlRenderCart — keeps working
             off one source. */
          if (entry.href) tlLineRedirects[key] = entry.href;
        });
      })
      .catch(function (err) {
        /* A bundle row without its contents is the drawer as it was before this
           existed — worth a warning, never worth breaking the repaint over. */
        console.warn('TL bundle contents unavailable', err);
      })
      .then(function () {
        tlBundleKidsFetch = null;
      });
    return tlBundleKidsFetch;
  }

  function tlBundleKidsHtml(kids, savedMoney) {
    if (!kids || !kids.length) return '';
    var rows = '';
    kids.forEach(function (kid) {
      if (!kid) return;
      /* Link to the child product: the line's own url points at the unlisted ₹0
         container product, which is a dead end for a shopper. `kid.url` arrives already
         resolved — a child that is itself a combo carries its landing page instead of
         its own product page — because that decision needs metafields and /cart.js has
         none. Decided in sections/toyland-cart-bundle-data.liquid; do not second-guess
         it here or the row will link somewhere else after a repaint. */
      var open = kid.url
        ? '<a class="tl-bundle-kids__link" href="' + escHtml(kid.url) + '">'
        : '<span class="tl-bundle-kids__link">';
      var close = kid.url ? '</a>' : '</span>';
      rows +=
        '<li class="tl-bundle-kids__row">' +
        open +
        '<span class="tl-bundle-kids__thumb">' +
        (kid.img ? '<img src="' + escHtml(kid.img) + '" alt="" width="32" height="32" loading="lazy">' : '') +
        '</span>' +
        '<span class="tl-bundle-kids__text">' +
        '<span class="tl-bundle-kids__name">' + escHtml(kid.title) + '</span>' +
        (kid.variant ? '<span class="tl-bundle-kids__variant">' + escHtml(kid.variant) + '</span>' : '') +
        '</span>' +
        (kid.qty > 1 ? '<span class="tl-bundle-kids__qty">×' + kid.qty + '</span>' : '') +
        close +
        '</li>';
    });
    /* The savings sentence, mirroring snippets/toyland-cart-bundle-items.liquid. The
       amount is Shopify's own (original − final line price), computed where the cart
       line was in hand and carried here on the slot — this function only ever sees the
       components. Omitted when a bundle saves nothing. */
    var saveHtml = savedMoney
      ? '<p class="tl-bundle-save">' +
        '<svg class="tl-bundle-save__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M16.5 9.4 7.5 4.21"/>' +
        '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>' +
        '<path d="m3.3 7 8.7 5 8.7-5"/>' +
        '<path d="M12 22V12"/>' +
        '</svg>' +
        ' You save ' + escHtml(savedMoney) + ' on this bundle' +
        '</p>'
      : '';
    return (
      '<div class="tl-bundle-kids tl-bundle-kids--drawer" data-tl-bundle-kids>' +
      '<p class="tl-bundle-kids__label">Bundle includes</p>' +
      '<ul class="tl-bundle-kids__list" role="list">' +
      rows +
      '</ul>' +
      '</div>' +
      saveHtml
    );
  }

  /**
   * Fill every contents slot we already have data for.
   * Returns true if some slot holds a key no fetch has covered yet — NOT merely a key
   * with no components. A line can legitimately have none (a plain quantity-break deal
   * carries the bundle marker but is never merged), and treating those as unresolved
   * would re-request the section on every single repaint, forever.
   */
  function tlPaintKnownBundleKids(scope) {
    var pending = false;
    scope.querySelectorAll('[data-tl-kids-slot]').forEach(function (slot) {
      var key = slot.getAttribute('data-tl-kids-slot');
      var kids = tlBundleKids[key];
      var row = slot.closest('[data-tl-item]');
      /* Before the kids check, not inside it: a line can resolve a redirect while having
         no components to paint, and its links would otherwise stay on the container. */
      tlApplyLineRedirect(row, key);
      if (kids && kids.length) {
        /* Strike the MRP total, not the selling-price total, like the Liquid render
           (snippets/toyland-bundle-mrp.liquid). The ratio comes from the bundle-data
           section; the line's own prices ride on the slot from tlRenderCart. */
        var saved = slot.getAttribute('data-tl-kids-saved');
        var orig = parseInt(slot.getAttribute('data-tl-kids-orig'), 10) || 0;
        var fin = parseInt(slot.getAttribute('data-tl-kids-final'), 10) || 0;
        if (tlBundleMrp[key] && orig > 0) {
          var was = Math.round(orig * tlBundleMrp[key]);
          if (was > fin) {
            saved = formatMoney(was - fin);
            var priceEl = row && row.querySelector('[data-tl-line-price]');
            var wasEl = priceEl && priceEl.querySelector('.tl-cart-item__price-was');
            if (priceEl && !wasEl) {
              wasEl = document.createElement('s');
              wasEl.className = 'tl-cart-item__price-was';
              priceEl.insertBefore(wasEl, priceEl.firstChild);
            }
            if (wasEl) wasEl.textContent = formatMoney(was);
          }
        }
        slot.innerHTML = tlBundleKidsHtml(kids, saved);
        /* The row turned out to be a bundle container after all, so drop the meta line
           the renderer left on it — that text is the app's product type ("Kaching
           Bundles Mix & Match"). Only ever fires when /cart.js did not set
           has_components; when it does, the meta was never written in the first place. */
        var meta = row && row.querySelector('.tl-cart-item__meta');
        if (meta) meta.remove();
      } else if (!tlBundleKidsAsked[key]) pending = true;
    });
    return pending;
  }

  function tlPaintBundleKids(scope) {
    if (!tlPaintKnownBundleKids(scope)) return;
    var keys = [];
    scope.querySelectorAll('[data-tl-kids-slot]').forEach(function (slot) {
      keys.push(slot.getAttribute('data-tl-kids-slot'));
    });
    // One fetch, then one repaint — never a loop: the keys in flight are marked as
    // asked whatever comes back, so a line the section had nothing for is left alone
    // instead of driving a request per repaint.
    tlFetchBundleKids().then(function () {
      keys.forEach(function (key) {
        tlBundleKidsAsked[key] = true;
      });
      if (scope.isConnected) tlPaintKnownBundleKids(scope);
    });
  }

  /**
   * Rebuild Rynoka cart drawer from /cart.js JSON.
   * When the page was rendered with an empty cart, Liquid omits [data-tl-cart-items] and the footer —
   * we must create those nodes so PDP / quick-add updates show without a full page reload.
   */
  function tlRenderCart(cart) {
    var drawerEl = document.querySelector('cart-drawer');
    if (!drawerEl) return;

    /* Last line of defence, and the important one: this function owns the empty
       state, so anything it cannot read as a whole cart it must leave alone rather
       than guess at. It used to fill in the gaps — `items` defaulted to [] and
       `count` fell back to items.length — which turned every partial payload into a
       confident "Your cart is empty" over a cart that was not. See tlIsWholeCart. */
    if (!tlIsWholeCart(cart)) return;

    var items = cart.items;
    var count = cart.item_count;

    if (count > 0) {
      drawerEl.classList.remove('is-empty');
    } else {
      drawerEl.classList.add('is-empty');
    }

    updateHeaderCartBubbles(cart);

    var subtitle = drawerEl.querySelector('[data-toyland-cart-subtitle]');
    if (subtitle) {
      subtitle.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
    }

    var body = drawerEl.querySelector('[data-tl-cart-body]');
    if (!body) return;

    var live = body.querySelector('#CartDrawer-LiveRegionText');

    if (count === 0 || items.length === 0) {
      /* .tl-upsell-strip is deliberately NOT removed: its picks are pinned to a
         collection, not derived from the cart, so it stays valid at zero items — and
         nothing re-renders this drawer from the server, so anything removed here is
         gone until a full page load. See snippets/cart-drawer.liquid. */
      body.querySelectorAll('[data-tl-cart-items], .tl-cod-banner').forEach(function (n) {
        n.remove();
      });
      var oldEmpty = body.querySelector('.tl-drawer__empty');
      if (oldEmpty) oldEmpty.remove();
      var emptyHtml =
        '<div class="tl-drawer__empty">' +
        '<div class="tl-drawer__empty-icon">' +
        '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C4BDB5" stroke-width="1.5">' +
        '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>' +
        '<path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg></div>' +
        '<p class="tl-drawer__empty-title">Your cart is empty</p>' +
        /* Mirrors snippets/cart-drawer.liquid verbatim — all three departments, not
           toys alone. The entity is spelled out because this string is built in JS,
           where a bare apostrophe would end the literal. */
        '<p class="tl-drawer__empty-sub">Add handcrafted pieces, women&rsquo;s wear or kids&rsquo; toys to get started!</p>' +
        '<button class="tl-btn tl-btn-primary" type="button" onclick="this.closest(\'cart-drawer\').close()">Continue Shopping</button>' +
        '</div>';
      /* Same anchor rule as the items list below: the upsell strip now survives the
         empty state, and it sits above the live region — anchoring on the live region
         alone would print "Your cart is empty" UNDER "You might also like". */
      var emptyAnchor = body.querySelector('.tl-upsell-strip') || live;
      if (emptyAnchor) emptyAnchor.insertAdjacentHTML('beforebegin', emptyHtml);
      else body.insertAdjacentHTML('afterbegin', emptyHtml);
      var footRm = drawerEl.querySelector('[data-tl-cart-footer]');
      if (footRm) footRm.remove();
      tlUpdateShipProgress(drawerEl, cart);
      if (live) live.textContent = 'Your cart is empty';
      return;
    }

    var emptyEl = body.querySelector('.tl-drawer__empty');
    if (emptyEl) emptyEl.remove();

    var itemsWrap = body.querySelector('[data-tl-cart-items]');
    if (!itemsWrap) {
      itemsWrap = document.createElement('div');
      itemsWrap.className = 'tl-cart-items';
      itemsWrap.id = 'tl-cart-items';
      itemsWrap.setAttribute('data-tl-cart-items', '');
      /* Above the upsell strip, which now outlives the empty state — anchoring on the
         live region alone would drop the items underneath "You might also like". */
      var itemsAnchor = body.querySelector('.tl-upsell-strip') || live;
      if (itemsAnchor) body.insertBefore(itemsWrap, itemsAnchor);
      else body.appendChild(itemsWrap);
    }

    // Read the rows' redirects back out before they are destroyed — see tlLineRedirects.
    tlHarvestLineRedirects(itemsWrap);

    itemsWrap.innerHTML = '';
    items.forEach(function (item) {
      var isBundleLine = tlCartLineIsBundle(item);
      var el = document.createElement('div');
      el.className = 'tl-cart-item' + (isBundleLine ? ' tl-cart-item--bundle' : '');
      el.setAttribute('data-tl-item', '');
      el.setAttribute('data-variant-id', item.variant_id);
      el.setAttribute('data-key', item.key);
      if (isBundleLine) el.setAttribute('data-tl-bundle', '');
      /* Where this row's links go. Falls back to item.url for every line Liquid never
         stamped — which is every ordinary product, and every line added since the page
         loaded. Re-stamped so the next repaint can harvest it in turn. */
      var lineHref = tlLineRedirects[item.key] || item.url;
      if (tlLineRedirects[item.key]) el.setAttribute('data-tl-redirect', tlLineRedirects[item.key]);

      /* A line Shopify built out of other lines — a merged Mix & Match bundle. Its
         product is the app's unlisted ₹0 container and its quantity counts bundles,
         not things, which is what strips the Qty control off the row below and moves
         the count into the title tag. Mirrors tl_item_has_kids in
         snippets/cart-drawer.liquid. */
      var hasKids = item.has_components === true;

      /* Bundle lines never get the stepper — editing the quantity drops the deal.
         A merged bundle shows no quantity here at all (it reads "1 Bundle" beside the
         title instead); an unmerged deal, e.g. a quantity break, keeps a static chip,
         because there the number is a real count of a real product.
         Mirrors snippets/cart-drawer.liquid; [data-tl-qty-val] lives on whichever of
         the two the row ends up with, so a row's quantity is always readable. */
      var qtyHtml = hasKids
        ? ''
        : isBundleLine
        ? '<div class="tl-cart-item__qty tl-cart-item__qty--locked" data-tl-qty-wrap>' +
          '<span class="tl-cart-item__qty-lock-label">Qty</span>' +
          '<span class="tl-cart-item__qty-val" data-tl-qty-val>' + item.quantity + '</span>' +
          '<span class="tl-cart-item__qty-lock-note">Bundle</span>' +
          '</div>'
        : '<div class="tl-cart-item__qty" data-tl-qty-wrap>' +
          '<button class="tl-cart-item__qty-btn" type="button" data-tl-qty="-1" aria-label="Decrease">−</button>' +
          '<span class="tl-cart-item__qty-val" data-tl-qty-val>' + item.quantity + '</span>' +
          '<button class="tl-cart-item__qty-btn" type="button" data-tl-qty="+1" aria-label="Increase">+</button>' +
          '</div>';
      var imgHtml = item.image
        ? '<img class="tl-cart-item__img" src="' + item.image + '" alt="" width="72" height="72" loading="lazy">'
        : '<div class="tl-cart-item__img-placeholder">\uD83E\uDDF8</div>';
      // Variant options (Size / Colour / …) as compact pills — mirrors snippets/cart-drawer.liquid.
      var optsHtml = '';
      if (!item.product_has_only_default_variant) {
        var pairs = Array.isArray(item.options_with_values) ? item.options_with_values : [];
        var pills = '';
        pairs.forEach(function (opt) {
          if (!opt || !opt.value || opt.value === 'Default Title') return;
          pills +=
            '<span class="tl-cart-item__opt">' +
            '<span class="tl-cart-item__opt-k">' + escHtml(opt.name) + ':</span> ' +
            escHtml(opt.value) +
            '</span>';
        });
        if (pills) optsHtml = '<div class="tl-cart-item__opts">' + pills + '</div>';
      }
      /* A bundled line's product type is the bundle app's own container ("Kaching
         Bundles Mix & Match"), which means nothing to a shopper — the contents list
         that takes its place says far more. Mirrors snippets/cart-drawer.liquid. */
      var metaHtml = optsHtml;
      if (!metaHtml && item.product_type && !hasKids) {
        metaHtml = '<p class="tl-cart-item__meta">' + escHtml(item.product_type) + '</p>';
      }
      /* Filled by tlPaintBundleKids once the components are known (see above).
         Asked for on any bundle-marked line, not just the ones /cart.js flags: the slot
         costs nothing when the line turns out to have no components, and the flag is
         the one piece of this that has never been verified against a live merged cart. */
      var lineSaved = (item.original_line_price || 0) - (item.final_line_price || 0);
      var kidsHtml =
        hasKids || isBundleLine
          ? '<div data-tl-kids-slot="' +
            escHtml(item.key) +
            '"' +
            // Carried on the slot because the savings line renders with the contents,
            // and by then the cart line itself is long out of scope.
            (lineSaved > 0 ? ' data-tl-kids-saved="' + escHtml(formatMoney(lineSaved)) + '"' : '') +
            ' data-tl-kids-orig="' + (item.original_line_price || 0) + '"' +
            ' data-tl-kids-final="' + (item.final_line_price || 0) + '"' +
            '></div>'
          : '';
      /* Thumbnail and title link on every row, merged bundles included — kept in step
         with snippets/cart-drawer.liquid, or a row would change shape the moment the
         drawer repaints. A merged line resolves no redirect of its own (its product is
         the app's unlisted container, which carries no metafields), so lineHref falls
         back to item.url and points at that container product. Deliberate. */
      var mediaHtml =
        '<a href="' + escHtml(lineHref) + '" class="tl-cart-item__img-wrap" tabindex="-1" aria-hidden="true">' +
        imgHtml +
        '</a>';
      var titleHtml = hasKids
        ? '<div class="tl-cart-item__title-row">' +
          '<a href="' + escHtml(lineHref) + '" class="tl-cart-item__name">' + escHtml(item.product_title) + '</a>' +
          // "1 Bundle" reads like a stock count beside a title, so the number is hidden
          // at one — but never removed: [data-tl-qty-val] is how a row's quantity is read.
          '<span class="tl-bundle-tag">' +
          '<span class="tl-bundle-tag__n' + (item.quantity > 1 ? '' : ' visually-hidden') + '" data-tl-qty-val>' +
          item.quantity +
          '</span>' +
          '<span class="tl-bundle-tag__word">' + (item.quantity > 1 ? 'Bundles' : 'Bundle') + '</span>' +
          '</span>' +
          '</div>'
        : '<a href="' + escHtml(lineHref) + '" class="tl-cart-item__name">' + escHtml(item.product_title) + '</a>';

      el.innerHTML =
        mediaHtml +
        '<div class="tl-cart-item__body">' +
        '<div class="tl-cart-item__top">' +
        '<div class="tl-cart-item__info">' +
        titleHtml +
        metaHtml +
        kidsHtml +
        '</div>' +
        '<button class="tl-cart-item__remove" type="button" data-tl-remove aria-label="Remove">' +
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>' +
        '</button>' +
        '</div>' +
        '<div class="tl-cart-item__bottom' +
        (hasKids ? ' tl-cart-item__bottom--price-only' : '') +
        '">' +
        qtyHtml +
        '<span class="tl-cart-item__price" data-tl-line-price>' +
        // Struck list total beside the real one whenever Shopify has discounted the
        // line — mirrors snippets/cart-drawer.liquid.
        (item.original_line_price > item.final_line_price
          ? '<s class="tl-cart-item__price-was">' + formatMoney(item.original_line_price) + '</s>'
          : '') +
        formatMoney(item.final_line_price) +
        '</span>' +
        '</div>' +
        '</div>';
      itemsWrap.appendChild(el);
    });

    tlPaintBundleKids(itemsWrap);
    tlEnsureCartFooter(drawerEl, cart);
    tlUpdateShipProgress(drawerEl, cart);
    if (live) {
      live.textContent =
        count + (count === 1 ? ' item in your cart. Total ' : ' items in your cart. Total ') + formatMoney(cart.items_subtotal_price || cart.total_price || 0);
    }
  }

  function tlRefreshCart() {
    var ticket = tlNextCartTicket();
    return ajaxCart()
      .then(function (cart) {
        tlApplyCart(cart, ticket);
        return cart;
      })
      .catch(function (e) {
        console.warn('TL cart refresh error', e);
      });
  }

  // Keep custom cart drawer in sync when the full cart page updates via Dawn cart.js
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      /* `cartData` is whatever the publisher had to hand. Dawn's product-form
         publishes the raw /cart/add.js reply — a line, not a cart — and apps publish
         summaries. tlApplyCart drops anything that is not a whole cart; the write
         interceptor's own post-write sync repaints from a real /cart.js read, so
         nothing is lost by ignoring the partial ones. */
      if (!event) return;
      try {
        tlApplyCart(event.cartData);
      } catch (err) {
        console.warn('Rynoka: cart repaint after cart-update skipped', err);
      }
    });
  }

  // ─── Qty / Remove handlers — event delegation on .tl-cart-items ────────────
  document.addEventListener('click', function (e) {
    // Qty buttons
    var qtyBtn = e.target.closest('[data-tl-qty]');
    if (qtyBtn) {
      var row = qtyBtn.closest('[data-tl-item]');
      if (!row) return;
      // Bundle rows render a static chip with no buttons; this is the belt-and-braces
      // half, for a row an app re-rendered with the stepper back in place.
      if (row.hasAttribute('data-tl-bundle')) return;
      var key = row.getAttribute('data-key');
      var valEl = row.querySelector('[data-tl-qty-val]');
      if (!key || !valEl) return;
      var delta = parseInt(qtyBtn.getAttribute('data-tl-qty'), 10);
      var current = parseInt(valEl.textContent, 10) || 0;
      var next = Math.max(0, current + delta);
      // KRN Toyz: per-order cap; block inc past the limit with a notice
      if (next > TL_MAX_PER_ORDER) {
        tlShowToast(
          'You’ve reached the maximum of ' + TL_MAX_PER_ORDER + ' per order for this item.',
          'limit'
        );
        return;
      }
      row.classList.add('is-loading');
      tlSetFooterBusy(true);
      var updates = {};
      updates[key] = next;
      /* Ticket taken when the write is ISSUED, and painted through tlApplyCart like
         every other cart read — a bare tlRenderCart here skipped the ordering rules
         entirely, so a stepper reply that overtook a newer one repainted the drawer
         from the older cart. */
      var qtyTicket = tlNextCartTicket();
      ajaxUpdate(updates).then(function (cart) {
        tlApplyCart(cart, qtyTicket);
      }).catch(function () {
        row.classList.remove('is-loading');
        /* The write failed, so the figures on screen are still the true ones — give
           them back at full strength rather than leaving the footer dimmed and, worse,
           its checkout button inert. */
        tlSetFooterBusy(false);
      });
      return;
    }

    // Remove button
    var removeBtn = e.target.closest('[data-tl-remove]');
    if (removeBtn) {
      var row2 = removeBtn.closest('[data-tl-item]');
      if (!row2) return;
      var key2 = row2.getAttribute('data-key');
      if (!key2) return;
      row2.classList.add('is-loading');
      tlSetFooterBusy(true);
      var updates2 = {};
      updates2[key2] = 0;
      var removeTicket = tlNextCartTicket();
      ajaxUpdate(updates2).then(function (cart) {
        tlApplyCart(cart, removeTicket);
      }).catch(function () {
        row2.classList.remove('is-loading');
        tlSetFooterBusy(false);
      });
    }
  });

  function toylandInitSitemapBrowser() {
    var wrap = document.querySelector('[data-tl-sitemap-browser]');
    if (!wrap || wrap.getAttribute('data-tl-sitemap-init') === '1') return;
    wrap.setAttribute('data-tl-sitemap-init', '1');
    var mount = wrap.querySelector('[data-tl-sitemap-browser-mount]');
    var errEl = wrap.querySelector('[data-tl-sitemap-browser-error]');
    var loadEl = wrap.querySelector('[data-tl-sitemap-browser-loading]');
    var url = (wrap.getAttribute('data-sitemap-url') || '').trim();
    if (!mount || !url) return;

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('status');
        return r.text();
      })
      .then(function (txt) {
        var doc = new DOMParser().parseFromString(txt, 'text/xml');
        if (doc.querySelector('parsererror')) throw new Error('parse');
        var SM_NS = 'http://www.sitemaps.org/schemas/sitemap/0.9';
        var locs = doc.getElementsByTagNameNS(SM_NS, 'loc');
        if (!locs || !locs.length) locs = doc.getElementsByTagName('loc');
        var ul = document.createElement('ul');
        ul.className = 'tl-sitemap-browser__list';
        var i;
        var n = 0;
        for (i = 0; i < locs.length; i++) {
          var href = (locs[i].textContent || '').trim();
          if (!href) continue;
          n++;
          var li = document.createElement('li');
          li.className = 'tl-sitemap-browser__item';
          var a = document.createElement('a');
          a.href = href;
          a.className = 'tl-sitemap-browser__link';
          a.textContent = href;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          li.appendChild(a);
          ul.appendChild(li);
        }
        if (!n) throw new Error('empty');
        mount.innerHTML = '';
        mount.appendChild(ul);
        mount.removeAttribute('hidden');
        if (loadEl) loadEl.setAttribute('hidden', '');
        if (errEl) errEl.setAttribute('hidden', '');
      })
      .catch(function () {
        mount.innerHTML = '';
        mount.setAttribute('hidden', '');
        if (loadEl) loadEl.setAttribute('hidden', '');
        if (errEl) errEl.removeAttribute('hidden');
      });
  }

  // ─── Refresh on cart:added / DOMContentLoaded ──────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    tlRefreshCart();
    initToylandFBT();
    toylandInitSitemapBrowser();

    // Contact: disable native HTML5 bubbles so the request can round-trip and
    // `form.errors` uses Shopify keys (author, email, body) — inline errors render in Liquid.
    // Without novalidate, the browser can block submit and no server errors appear.
    var contactForm = document.getElementById('ToylandContactForm');
    if (contactForm) {
      contactForm.setAttribute('novalidate', 'novalidate');

      if (contactForm.querySelector('[data-tl-contact-summary]')) {
        var firstInvalid = contactForm.querySelector(
          'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]'
        );
        if (firstInvalid) {
          try {
            firstInvalid.focus({ preventScroll: true });
          } catch (e) {
            firstInvalid.focus();
          }
          var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          firstInvalid.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }
  });
  document.addEventListener('cart:refresh', function (e) {
    // tlApplyCart dispatches this itself after painting — don't paint it twice.
    if (tlDispatchingOwnRefresh) return;
    /* A whole cart is painted straight from the event; anything less — an app saying
       "the cart changed" and handing over a count, a line, or nothing — is treated as
       a notification and answered with a real /cart.js read. Painting from the
       partial payload instead is what emptied a full drawer. */
    if (tlIsWholeCart(e.detail)) {
      tlApplyCart(e.detail);
    } else {
      tlRefreshCart();
    }
  });
  document.addEventListener('cart:added', tlRefreshCart);

  document.querySelectorAll('[data-toyland-pdp-form]').forEach(function (form) {
    syncPdpPrice(form);
  });

  // ─── Pincode Checker — dedicated page (/pages/pincode) ────────────────────
  // Zone detection uses prefix-based heuristics for the full Indian postal network.
  // When Shiprocket app is installed, replace tlPcpZoneFor() with a real API call.
  (function initPincodeCheckerPage() {
    var section = document.querySelector('[data-tl-pincode-page]');
    if (!section) return;

    var form    = section.querySelector('[data-tl-pincode-page-form]');
    var input   = section.querySelector('[data-tl-pincode-page-input]');
    var resultEl = section.querySelector('[data-tl-pincode-page-result]');
    var recentWrap = section.querySelector('[data-tl-pincode-recent]');
    var recentChips = section.querySelector('[data-tl-pincode-recent-chips]');
    var LS_KEY = 'tl_recent_pins';

    if (!form || !input || !resultEl) return;

    // ── Zone table (first 2–3 digits → zone info) ───────────────────────────
    // Returns { zone, days, codAvailable, freeShipping }
    function tlPcpZoneFor(pin) {
      var p = parseInt(pin, 10);
      var prefix3 = Math.floor(p / 1000);   // 110xxx → 110
      var prefix2 = Math.floor(p / 10000);  // 110xxx → 11

      // Northeast / Remote islands — 5-7 days
      var ne3 = [
        787,788,789,790,791,792,793,794,795,796,797,798,799, // Assam + NE
        744  // Andaman
      ];
      if (ne3.indexOf(prefix3) > -1) {
        return { zone: 'northeast', label: 'Northeast / Remote', days: '5–7', cod: true, codLimit: 5000 };
      }

      // Metro / Tier-1 — 1-2 days
      var metro3 = [
        110,111,        // Delhi NCR
        400,401,402,403,404,405,406,407,408,410,411,412,413,414, // Mumbai / Pune
        560,561,562,563,564,565,566,567,568,  // Bangalore
        600,601,602,603,604,605,606,607,608,609,610,611,612,613,614,615,616,617,618,619, // Chennai / TN
        700,701,702,703,704,705,706,707,708,  // Kolkata
        500,501,502,503,504,505,506,           // Hyderabad
        380,381,382,383,384,385,               // Ahmedabad
        395,394,393,392,391,390                // Surat / South Gujarat
      ];
      if (metro3.indexOf(prefix3) > -1) {
        return { zone: 'metro', label: 'Metro City', days: '1–2', cod: true, codLimit: 5000 };
      }

      // Tier-2 — 2-3 days
      var tier2_3 = [
        302,303,304,305,306,  // Jaipur
        226,227,228,229,      // Lucknow
        800,801,802,803,804,  // Patna / Bihar
        462,463,464,465,      // Bhopal
        452,453,454,455,      // Indore
        440,441,442,443,444,445,  // Nagpur
        641,642,643,644,645,  // Coimbatore
        390,391,392,          // Vadodara
        360,361,362,363,      // Rajkot
        682,683,684,685,686,  // Kochi
        160,161,162,163,      // Chandigarh
        248,249,250,          // Dehradun
        175,176,177,178,179,  // Himachal
        670,671,672,673,      // Calicut / Malabar
        571,572,573,          // Mysore region
        520,521,522,523,      // Vijayawada
        530,531,532,           // Vizag
        480,481,482,483,484,485 // Jabalpur / Chhattisgarh
      ];
      if (tier2_3.indexOf(prefix3) > -1) {
        return { zone: 'tier2', label: 'Tier 2 City', days: '2–3', cod: true, codLimit: 5000 };
      }

      // Anything in 1xx-8xx ranges that isn't NE → Tier 3
      if (prefix2 >= 10 && prefix2 <= 85) {
        return { zone: 'tier3', label: 'Tier 3 / Town', days: '3–5', cod: true, codLimit: 3000 };
      }

      // Fallback — serviceable but uncertain
      return { zone: 'tier3', label: 'Standard Delivery', days: '3–7', cod: false, codLimit: 0 };
    }

    function getCityFor(pin) {
      var map = {
        '110': 'New Delhi', '400': 'Mumbai', '560': 'Bangalore', '600': 'Chennai',
        '700': 'Kolkata',   '500': 'Hyderabad', '380': 'Ahmedabad', '395': 'Surat',
        '411': 'Pune',      '302': 'Jaipur', '226': 'Lucknow', '452': 'Indore',
        '440': 'Nagpur',    '641': 'Coimbatore', '682': 'Kochi', '160': 'Chandigarh',
        '360': 'Rajkot',    '390': 'Vadodara', '530': 'Visakhapatnam', '520': 'Vijayawada',
        '800': 'Patna',     '462': 'Bhopal', '248': 'Dehradun', '670': 'Kozhikode',
        '744': 'Port Blair (Andaman)', '787': 'Guwahati region'
      };
      return map[pin.slice(0, 3)] || null;
    }

    // ── Render result ─────────────────────────────────────────────────────────
    function renderResult(pin, data) {
      resultEl.removeAttribute('hidden');

      var city = getCityFor(pin);
      var cityLabel = city ? (' · <strong>' + city + '</strong>') : '';

      var zoneColorMap = {
        metro:     '#059669',
        tier2:     '#3B82F6',
        tier3:     '#F59E0B',
        northeast: '#7C3AED'
      };
      var zoneColor = zoneColorMap[data.zone] || '#6B7280';

      var codHTML = data.cod
        ? '<span class="tl-pcp__result-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> COD available up to ₹' + data.codLimit.toLocaleString('en-IN') + '</span>'
        : '<span class="tl-pcp__result-chip" style="opacity:.65"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="8" y1="8" x2="16" y2="16"/></svg> COD not available</span>';

      resultEl.className = 'tl-pcp__result tl-pcp__result--ok';
      resultEl.innerHTML =
        '<div class="tl-pcp__result-head">' +
          '<span class="tl-pcp__result-icon">✓</span>' +
          '<span>Delivery available for <strong>' + pin + '</strong>' + cityLabel + '</span>' +
        '</div>' +
        '<div class="tl-pcp__result-meta">' +
          '<span class="tl-pcp__result-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> ' + data.days + ' business days</span>' +
          '<span class="tl-pcp__result-chip" style="color:' + zoneColor + '"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/></svg> ' + data.label + '</span>' +
          codHTML +
          '<span class="tl-pcp__result-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg> Free delivery on all orders</span>' +
        '</div>';
    }

    function renderError(msg) {
      resultEl.removeAttribute('hidden');
      resultEl.className = 'tl-pcp__result tl-pcp__result--err';
      resultEl.innerHTML =
        '<div class="tl-pcp__result-head">' +
          '<span class="tl-pcp__result-icon">✕</span>' +
          '<span>' + msg + '</span>' +
        '</div>';
    }

    // ── Recent checks (localStorage) ─────────────────────────────────────────
    function getRecent() {
      try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch (e) { return []; }
    }

    function saveRecent(pin) {
      var pins = getRecent().filter(function (p) { return p !== pin; });
      pins.unshift(pin);
      if (pins.length > 5) pins = pins.slice(0, 5);
      try { localStorage.setItem(LS_KEY, JSON.stringify(pins)); } catch (e) {}
    }

    function renderRecent() {
      if (!recentWrap || !recentChips) return;
      var pins = getRecent();
      if (!pins.length) { recentWrap.setAttribute('hidden', ''); return; }
      recentWrap.removeAttribute('hidden');
      recentChips.innerHTML = pins.map(function (p) {
        return '<button type="button" class="tl-pcp__recent-pin" data-tl-pincode-recent-btn="' + p + '" aria-label="Recheck pincode ' + p + '">' + p + '</button>';
      }).join('');
    }

    // ── Spinner helpers ───────────────────────────────────────────────────────
    var btnText    = form.querySelector('.tl-pcp__submit-text');
    var btnArrow   = form.querySelector('.tl-pcp__submit-arrow');
    var btnSpinner = form.querySelector('.tl-pcp__submit-spinner');
    var submitBtn  = form.querySelector('[data-tl-pincode-page-btn]');

    function setLoading(state) {
      if (!submitBtn) return;
      submitBtn.disabled = state;
      if (btnText)    btnText.textContent = state ? 'Checking…' : 'Check Delivery';
      if (btnArrow)   btnArrow.hidden = state;
      if (btnSpinner) btnSpinner.hidden = !state;
    }

    // ── Check logic ───────────────────────────────────────────────────────────
    function doCheck(pin) {
      pin = (pin || '').replace(/\D/g, '').trim();

      if (pin.length !== 6) {
        renderError('Please enter a valid 6-digit pincode.');
        return;
      }

      // Leading-zero guard (no valid Indian PIN starts with 0)
      if (pin.charAt(0) === '0') {
        renderError('No Indian pincode starts with 0. Please double-check.');
        return;
      }

      setLoading(true);

      // Simulate async API call (replace setTimeout with fetch(Shiprocket) after app install)
      setTimeout(function () {
        setLoading(false);
        var data = tlPcpZoneFor(pin);
        renderResult(pin, data);
        saveRecent(pin);
        renderRecent();
        input.value = pin;
      }, 380);
    }

    // ── Form submit ───────────────────────────────────────────────────────────
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      doCheck(input.value);
    });

    // ── City chips quick-fill ─────────────────────────────────────────────────
    section.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-tl-pincode-city]');
      if (chip) {
        var pin = chip.dataset.tlPincodeCity;
        input.value = pin;
        doCheck(pin);
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }

      // Recent chip
      var recentBtn = e.target.closest('[data-tl-pincode-recent-btn]');
      if (recentBtn) {
        var rpin = recentBtn.dataset.tlPincodeRecentBtn;
        input.value = rpin;
        doCheck(rpin);
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });

    // ── Live digit-only filtering ─────────────────────────────────────────────
    input.addEventListener('input', function () {
      var cleaned = this.value.replace(/\D/g, '').slice(0, 6);
      if (this.value !== cleaned) this.value = cleaned;
    });

    // ── Init recent chips on load ─────────────────────────────────────────────
    renderRecent();
  }());

  // ─── Mobile footer accordions ──────────────────────────────────────────────
  (function initFooterAccordions() {
    var root = document.querySelector('.tl-footer');
    if (!root) return;

    var mq = window.matchMedia('(max-width: 768px)');
    var toggles = Array.prototype.slice.call(root.querySelectorAll('[data-tl-footer-toggle]'));
    if (!toggles.length) return;

    function setOpen(toggle, open) {
      var panelId = toggle.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) {
        if (mq.matches && !open) panel.setAttribute('hidden', '');
        else panel.removeAttribute('hidden');
      }
    }

    function syncForViewport() {
      toggles.forEach(function (toggle, index) {
        setOpen(toggle, mq.matches ? index === 0 : true);
      });
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        if (!mq.matches) return;
        setOpen(toggle, toggle.getAttribute('aria-expanded') !== 'true');
      });
    });

    syncForViewport();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', syncForViewport);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(syncForViewport);
    }
  }());

  // ─── PLP infinite scroll (shop page + collection) ─────────────────────────
  // 1) Section Rendering API (?sections=section.id).
  // 2) Fallback: full next-page HTML.
  // 3) Scroll geometry backs up IntersectionObserver.
  function initToylandPlpInfiniteScroll(root) {
    if (!root || root.getAttribute('data-toyland-plp-infinite-bound') === 'true') return;

    var grid = root.querySelector('[data-toyland-coll-grid]');
    var sentinel =
      root.querySelector('[data-toyland-plp-infinite-sentinel]') ||
      root.querySelector('[data-toyland-shop-infinite-sentinel]');
    var rangeEl = root.querySelector('[data-tl-range]');
    var totalEl = root.querySelector('[data-tl-total]');
    if (!grid || !sentinel) return;

    root.setAttribute('data-toyland-plp-infinite-bound', 'true');

    var sectionId = (
      root.getAttribute('data-plp-section-id') ||
      root.getAttribute('data-shop-section-id') ||
      ''
    ).trim();
    var loading = false;
    var done = false;
    var scrollTimer;
    var observer = null;
    var inFlightUrl = '';

    function htmlFromSectionsJson(text) {
      try {
        var json = JSON.parse(text.trim());
        var direct = sectionId ? json[sectionId] : null;
        if (direct && typeof direct === 'string') return direct;
        var k;
        for (k in json) {
          if (!Object.prototype.hasOwnProperty.call(json, k)) continue;
          var v = json[k];
          if (
            v &&
            typeof v === 'string' &&
            (v.indexOf('data-toyland-plp-infinite') !== -1 ||
              v.indexOf('data-toyland-shop-infinite') !== -1)
          ) {
            return v;
          }
        }
        var keys = Object.keys(json);
        if (keys.length === 1 && json[keys[0]] && typeof json[keys[0]] === 'string') return json[keys[0]];
      } catch (e1) {
        /* not JSON */
      }
      return '';
    }

    /** Prefer Section API; fall back to full document (AJAX-friendly HTML). */
    function fetchNextMarkup(nextUrl) {
      var baseStr = nextUrl;
      if (baseStr.indexOf('//') === 0) {
        baseStr = window.location.protocol + baseStr;
      }
      var base = new URL(baseStr, window.location.href);

      function fetchFullPage() {
        return fetch(base.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }).then(function (r) {
          if (!r.ok) throw new Error('full');
          return r.text();
        });
      }

      var uSections = new URL(base.toString());
      if (sectionId) {
        uSections.searchParams.set('sections', sectionId);
      } else {
        uSections.searchParams.set('sections', 'main');
      }

      return fetch(uSections.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json, text/html', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) {
          if (!r.ok) return fetchFullPage();
          return r.text();
        })
        .then(function (text) {
          var t = String(text || '').trim();
          if (!t) return fetchFullPage();

          var fromJson = htmlFromSectionsJson(t);
          if (fromJson && fromJson.indexOf('tl-card-product') !== -1) return fromJson;

          if (t.charAt(0) === '<' && t.indexOf('tl-card-product') !== -1) return t;

          if (sectionId && fromJson === '') {
            var uSingle = new URL(base.toString());
            uSingle.searchParams.set('section_id', sectionId);
            return fetch(uSingle.toString(), {
              credentials: 'same-origin',
              headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
            }).then(function (r2) {
              if (!r2.ok) return fetchFullPage();
              return r2.text();
            });
          }

          return fetchFullPage();
        })
        .then(function (html) {
          return String(html || '').trim();
        });
    }

    function nextUrlFromRoot(remoteRoot) {
      var next = (remoteRoot.getAttribute('data-next-url') || '').trim();
      if (next) return next;
      var nextLink =
        remoteRoot.querySelector('.tl-shop-pagination__link--next[href]') ||
        remoteRoot.querySelector('a.tl-coll-loadmore__btn[href]');
      return nextLink ? (nextLink.getAttribute('href') || '').trim() : '';
    }

    function extractInfiniteFromDoc(doc) {
      var pageWrap = doc.querySelector('[data-toyland-shop-page]');
      var remoteRoot =
        (pageWrap && pageWrap.querySelector('[data-toyland-plp-infinite]')) ||
        (pageWrap && pageWrap.querySelector('[data-toyland-shop-infinite]')) ||
        doc.querySelector('[data-toyland-plp-infinite]') ||
        doc.querySelector('[data-toyland-shop-infinite]') ||
        doc.querySelector('.toyland-shop-section [data-toyland-plp-infinite]') ||
        doc.querySelector('.toyland-collection-section [data-toyland-plp-infinite]');
      if (!remoteRoot) return null;
      var remoteGrid = remoteRoot.querySelector('[data-toyland-coll-grid]');
      if (!remoteGrid) return null;
      return { remoteGrid: remoteGrid, nextUrl: nextUrlFromRoot(remoteRoot) };
    }

    function pauseObserver() {
      if (observer && sentinel) observer.unobserve(sentinel);
    }

    function resumeObserver() {
      if (observer && sentinel && !done && root.getAttribute('data-next-url')) {
        observer.observe(sentinel);
      }
    }

    function updateRangeCount() {
      var total = totalEl ? parseInt(totalEl.textContent, 10) : 0;
      if (!rangeEl || !total) return;
      var n = grid.querySelectorAll('.tl-card-product').length;
      rangeEl.textContent = '1–' + Math.min(n, total);
    }

    function loadNext() {
      var nextUrl = (root.getAttribute('data-next-url') || '').trim();
      if (done || !nextUrl) return;
      if (loading || inFlightUrl === nextUrl) return;

      loading = true;
      inFlightUrl = nextUrl;
      pauseObserver();
      root.setAttribute('data-loading', 'true');
      sentinel.classList.add('is-loading');
      sentinel.classList.remove('is-error');

      fetchNextMarkup(nextUrl)
        .then(function (html) {
          if (!html) throw new Error('empty');
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var extracted = extractInfiniteFromDoc(doc);
          if (!extracted) throw new Error('parse');
          var cards = extracted.remoteGrid.querySelectorAll('.tl-card-product');
          if (!cards.length) throw new Error('nocards');

          Array.prototype.slice.call(cards).forEach(function (node) {
            grid.appendChild(node);
          });

          if (typeof window.toylandInitCardCarousels === 'function') {
            window.toylandInitCardCarousels(grid);
          }

          setTimeout(function toylandReinitJudgeMeCards() {
            try {
              if (window.jdgm && typeof window.jdgm.documentReady === 'function') {
                window.jdgm.documentReady();
              }
            } catch (e) {
              /* Judge.me not loaded or API changed */
            }
            toylandHideEmptyCardReviewBadges(grid);
            setTimeout(function () {
              toylandHideEmptyCardReviewBadges(grid);
            }, 500);
            try {
              window.dispatchEvent(new Event('resize'));
            } catch (e2) {
              /* ignore */
            }
          }, 0);

          if (extracted.nextUrl) {
            root.setAttribute('data-next-url', extracted.nextUrl);
          } else {
            root.removeAttribute('data-next-url');
            done = true;
            sentinel.setAttribute('hidden', '');
          }
          updateRangeCount();
        })
        .catch(function () {
          sentinel.classList.add('is-error');
        })
        .finally(function () {
          loading = false;
          if (inFlightUrl === nextUrl) inFlightUrl = '';
          root.removeAttribute('data-loading');
          sentinel.classList.remove('is-loading');
          resumeObserver();
        });
    }

    function sentinelNearViewport() {
      if (done || loading) return;
      var rect = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (rect.top < vh + 480) loadNext();
    }

    if (!root.getAttribute('data-next-url')) {
      done = true;
      sentinel.setAttribute('hidden', '');
      return;
    }

    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && !loading) loadNext();
          });
        },
        { root: null, rootMargin: '400px 0px 800px 0px', threshold: 0 }
      );
      observer.observe(sentinel);
    }

    window.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(sentinelNearViewport, 80);
      },
      { passive: true }
    );

    window.addEventListener('resize', sentinelNearViewport, { passive: true });

    setTimeout(sentinelNearViewport, 60);
    updateRangeCount();

    sentinel.addEventListener('click', function () {
      if (!sentinel.classList.contains('is-error')) return;
      sentinel.classList.remove('is-error');
      loadNext();
    });
  }

  (function bootToylandPlpInfiniteScroll() {
    var roots = document.querySelectorAll('[data-toyland-plp-infinite], [data-toyland-shop-infinite]');
    Array.prototype.forEach.call(roots, initToylandPlpInfiniteScroll);
  }());

  // ─── Collections index (/collections) infinite scroll ─────────────────────
  function initToylandLcollInfiniteScroll(root) {
    if (!root || root.getAttribute('data-toyland-lcoll-infinite-bound') === 'true') return;

    var list = root.querySelector('[data-tl-lcoll-list]');
    var sentinel = root.querySelector('[data-toyland-lcoll-infinite-sentinel]');
    var rangeEl = root.querySelector('[data-tl-lcoll-range]');
    var totalEl = root.querySelector('[data-tl-lcoll-scroll-total]');
    if (!list || !sentinel) return;

    root.setAttribute('data-toyland-lcoll-infinite-bound', 'true');

    var sectionId = (root.getAttribute('data-plp-section-id') || '').trim();
    var loading = false;
    var done = false;
    var scrollTimer;
    var observer = null;
    var inFlightUrl = '';

    function htmlFromSectionsJson(text) {
      try {
        var json = JSON.parse(text.trim());
        var direct = sectionId ? json[sectionId] : null;
        if (direct && typeof direct === 'string') return direct;
        var k;
        for (k in json) {
          if (!Object.prototype.hasOwnProperty.call(json, k)) continue;
          var v = json[k];
          if (v && typeof v === 'string' && v.indexOf('data-toyland-lcoll-infinite') !== -1) return v;
        }
        var keys = Object.keys(json);
        if (keys.length === 1 && json[keys[0]] && typeof json[keys[0]] === 'string') return json[keys[0]];
      } catch (e1) {
        /* not JSON */
      }
      return '';
    }

    function fetchNextMarkup(nextUrl) {
      var baseStr = nextUrl;
      if (baseStr.indexOf('//') === 0) {
        baseStr = window.location.protocol + baseStr;
      }
      var base = new URL(baseStr, window.location.href);

      function fetchFullPage() {
        return fetch(base.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (r) {
          if (!r.ok) throw new Error('full');
          return r.text();
        });
      }

      var uSections = new URL(base.toString());
      if (sectionId) {
        uSections.searchParams.set('sections', sectionId);
      } else {
        uSections.searchParams.set('sections', 'main');
      }

      return fetch(uSections.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json, text/html', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) {
          if (!r.ok) return fetchFullPage();
          return r.text();
        })
        .then(function (text) {
          var t = String(text || '').trim();
          if (!t) return fetchFullPage();
          var fromJson = htmlFromSectionsJson(t);
          if (fromJson && fromJson.indexOf('tl-lcoll-grid__cell') !== -1) return fromJson;
          if (t.charAt(0) === '<' && t.indexOf('tl-lcoll-grid__cell') !== -1) return t;
          return fetchFullPage();
        })
        .then(function (html) {
          return String(html || '').trim();
        });
    }

    function nextUrlFromRoot(remoteRoot) {
      var next = (remoteRoot.getAttribute('data-next-url') || '').trim();
      if (next) return next;
      var nextLink = remoteRoot.querySelector('.tl-shop-pagination__link--next[href]');
      return nextLink ? (nextLink.getAttribute('href') || '').trim() : '';
    }

    function extractFromDoc(doc) {
      var remoteRoot =
        doc.querySelector('[data-toyland-lcoll-infinite]') ||
        doc.querySelector('#toyland-list-collections [data-toyland-lcoll-infinite]');
      if (!remoteRoot) return null;
      var remoteList = remoteRoot.querySelector('[data-tl-lcoll-list]');
      if (!remoteList) return null;
      return { remoteList: remoteList, nextUrl: nextUrlFromRoot(remoteRoot) };
    }

    function pauseObserver() {
      if (observer && sentinel) observer.unobserve(sentinel);
    }

    function resumeObserver() {
      if (observer && sentinel && !done && root.getAttribute('data-next-url')) {
        observer.observe(sentinel);
      }
    }

    function updateRangeCount() {
      var total = totalEl ? parseInt(totalEl.textContent, 10) : 0;
      if (!rangeEl || !total) return;
      var n = list.querySelectorAll('.tl-lcoll-grid__cell').length;
      rangeEl.textContent = '1–' + Math.min(n, total);
    }

    function loadNext() {
      var nextUrl = (root.getAttribute('data-next-url') || '').trim();
      if (done || !nextUrl) return;
      if (loading || inFlightUrl === nextUrl) return;

      loading = true;
      inFlightUrl = nextUrl;
      pauseObserver();
      root.setAttribute('data-loading', 'true');
      sentinel.classList.add('is-loading');
      sentinel.classList.remove('is-error');

      fetchNextMarkup(nextUrl)
        .then(function (html) {
          if (!html) throw new Error('empty');
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var extracted = extractFromDoc(doc);
          if (!extracted) throw new Error('parse');
          var cells = extracted.remoteList.querySelectorAll('.tl-lcoll-grid__cell');
          if (!cells.length) throw new Error('nocells');

          Array.prototype.slice.call(cells).forEach(function (node) {
            list.appendChild(node);
          });

          if (extracted.nextUrl) {
            root.setAttribute('data-next-url', extracted.nextUrl);
          } else {
            root.removeAttribute('data-next-url');
            done = true;
            sentinel.setAttribute('hidden', '');
          }
          updateRangeCount();
        })
        .catch(function () {
          sentinel.classList.add('is-error');
        })
        .finally(function () {
          loading = false;
          if (inFlightUrl === nextUrl) inFlightUrl = '';
          root.removeAttribute('data-loading');
          sentinel.classList.remove('is-loading');
          resumeObserver();
        });
    }

    function sentinelNearViewport() {
      if (done || loading) return;
      var rect = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (rect.top < vh + 480) loadNext();
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadNext();
          });
        },
        { rootMargin: '480px 0px 480px 0px', threshold: 0.01 }
      );
      observer.observe(sentinel);
    }

    window.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(sentinelNearViewport, 80);
      },
      { passive: true }
    );

    window.addEventListener('resize', sentinelNearViewport, { passive: true });
    setTimeout(sentinelNearViewport, 60);
    updateRangeCount();

    sentinel.addEventListener('click', function () {
      if (!sentinel.classList.contains('is-error')) return;
      sentinel.classList.remove('is-error');
      loadNext();
    });
  }

  (function bootToylandLcollInfiniteScroll() {
    var roots = document.querySelectorAll('[data-toyland-lcoll-infinite]');
    Array.prototype.forEach.call(roots, initToylandLcollInfiniteScroll);
  }());

  // ─── PLP / Shop category chip strip — horizontal scroll (touch + desktop) ───
  function initToylandCollFiltersScroll(el) {
    if (!el || el.getAttribute('data-tl-scroll-bound') === 'true') return;
    el.setAttribute('data-tl-scroll-bound', 'true');

    function canScrollX() {
      return el.scrollWidth > el.clientWidth + 1;
    }

    el.addEventListener(
      'wheel',
      function (e) {
        if (!canScrollX()) return;
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      },
      { passive: false }
    );

    // Touch devices use native overflow scroll + tap on links; drag-to-scroll is desktop-only.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;
    var dragThreshold = 6;

    el.addEventListener('mousedown', function (e) {
      if (e.button !== 0 || !canScrollX()) return;
      dragging = true;
      moved = false;
      startX = e.pageX;
      startScroll = el.scrollLeft;
    });

    window.addEventListener('mousemove', function (e) {
      if (!dragging) return;
      var dx = e.pageX - startX;
      if (!moved && Math.abs(dx) > dragThreshold) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (moved) {
        el.scrollLeft = startScroll - dx;
      }
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
    }

    window.addEventListener('mouseup', endDrag);
    window.addEventListener('blur', endDrag);

    el.addEventListener(
      'click',
      function (e) {
        if (!moved) return;
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      },
      true
    );
  }

  function bootToylandCollFiltersScroll() {
    var strips = document.querySelectorAll('.tl-coll-filters__scroll');
    Array.prototype.forEach.call(strips, initToylandCollFiltersScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootToylandCollFiltersScroll);
  } else {
    bootToylandCollFiltersScroll();
  }
})();
